import {
 useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Bot,
  Building2,
  CalendarClock,
  ChevronRight,
  CircleDollarSign,
  ContactRound,
  Gauge,
  Headphones,
  Inbox,
  ListTodo,
  Mail,
  Menu,
  MessageSquareText,
  Moon,
  Phone,
  PhoneCall,
  Send,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  Sparkles,
  Sun,
  Users,
  Wallet,
  Workflow,
  X,
  Radio,
  Mic2,
  UserCog,
  PanelLeftClose,
  PanelLeftOpen,
  Paperclip,
  Voicemail,
  UserRoundCheck,
  ScanSearch,
  AlertTriangle,
  RefreshCw,
  FileText,
  Wrench,
  ClipboardCopy,
  Eye,
  ExternalLink,
  CheckCircle,
  CircleAlert,
  Globe2,
  CalendarPlus,
  Video,
  Link2,
  LogOut,
  UsersRound,
  CalendarDays,
  RefreshCcw,
   Smartphone,
   AtSign,
   AlarmClock,
   BadgeCheck,
   BookOpen,
   Clock,
  Cloud,
  MapPin,
  Plus,
  Edit3,
} from "lucide-react";
import { FounderControlTower } from "./FounderControlTower";
import {
  getCustomerLifecycles,
  type CustomerLifecycle,


  contactFromRaw,
  addEmailDraftFile,
  createContact,
  createOrganization,
  getProviderReadiness,
  getDispositions,
  getSchedules,
  getTasks,
  getCurrentUserId,
  getMicrosoftCommunications,
  getEmailDraft,
  getEmailDraftFile,
  getOrganizations,
  setSyntheticDataVisibility,
  manageSchedule,
  manageTask,
  recordCallDisposition,
  microsoftAction,
  saveEmailDraft,
  removeEmailDraftFile,
  updateContact,
  updateOrganization,
} from "./lib/api";
import type { MicrosoftCommunicationsSnapshot, ProviderReadiness } from "./lib/api";
import { config } from "./lib/config";
import { VoiceCallControls } from "./VoiceCallControls";
import { CompanyRestNotice } from "./CompanyRestNotice";
import { CompanyRestTimezone, useCompanyRestSelection } from "./CompanyRestTimezone";
import { CompanyImport } from "./CompanyImport";
import {
  CRM_PROVIDER_ADAPTERS,
  crmAdapterUi,
} from "./lib/providerAdapterRegistry.js";
import {
  dataSourceMessage,
  validateRuntimeConfiguration,
} from "./lib/runtimeState";
import type { DataSourceMode } from "./lib/runtimeState";
import {
  buildOrganizations,
  searchOrganizations,
} from "./lib/organizationModel";
import { prepareCommunication } from "./lib/lifecycleState";
import type {
  AccountLink,
  AcquisitionAttribution,
  Activity as ActivityType,
  Contact,
  IntegrationState,
  LifecycleEvent,
  Opportunity,
  Organization,
  CallDisposition,
  Task,
  EmailDraftAttachment,
  EmailDraftLink,
} from "./types";
import type { ResolutionState } from "./lib/callFlowData";
import {normalizeAgreementEvidence,normalizeResponseEvidence} from "./lib/callSessionEvidence";
import {CallsPanelBoundary} from "./CallsPanelBoundary";
import {
  buildReminderCommitmentLanguage,
  buildReminderPlan,
} from "./lib/reminderLogic";
import type {
  ReminderDeliveryMode,
  ReminderPlanItem,
} from "./lib/reminderLogic";
import {
  AdminOperationsPage,
  CustomerOperationsPage,
  RoleCommandCenter,
  workspaceRoleLabels,
} from "./RoleWorkspaces";
import type { WorkspaceRole } from "./RoleWorkspaces";
import { CustomerCallsPage } from "./CustomerCalls";
import {CompanyDialSummary} from "./CompanyDialSummary";
import {CompanyIntelligenceProfile} from "./CompanyIntelligenceProfile";
import {CompanyPhoneRoutes} from "./CompanyPhoneRoutes";
import {CompanyReviewSegments,CompanyReviewSegmentAdmin} from "./CompanyReviewSegments";
import type {CompanyReviewSegment} from "./lib/companyReviewSegments";
import {companyCallContext,type CompanyCallSelection} from "./lib/companyCallNavigation";
import {ContactBuyingRole} from "./ContactBuyingRole";
import { CallDetailsPage } from "./CallDetails";
import { useCommunications } from "./CommunicationsProvider";
import { CanonicalSmsConversation } from "./CanonicalSmsConversation";
import { useCrmVoice } from "./VoiceProvider";
import { formatCallDuration } from "./lib/callWorkspace";
import { AvertSignalLockup, AvertSignalMark } from "./Brand";
import { BlipEmailPreview } from "./BlipEmailPreview";
import { CommunicationsInbox } from "./CommunicationsInbox";
import { TaskWorkspace } from "./TaskWorkspace";
import { OpportunityWorkspace } from "./OpportunityWorkspace";
import { AdminFoundation } from "./AdminFoundation";
import {ROLE_PREVIEW_OPTIONS,effectiveCrmRole,hasCrmCapability,workspaceRoleForCrmRole,canUseRoleplayTraining,type CrmRole}from"./lib/crmRbac";
import { TeamChat } from "./TeamChat";
import { TeamAccess } from "./TeamAccess";
import { MyPhoneSetup, PhoneSystemAdmin } from "./PhoneSystemAdmin";
import { SchedulingPage } from "./SchedulingPage";
import { NotificationCenter } from "./NotificationCenter";
import { RecordNotes, type SupplementalHistoryEntry } from "./RecordNotes";
import { ProspectingPage } from "./ProspectingPage";
import { RoleplayTraining } from "./RoleplayTraining";
import { DemoCenter } from "./DemoCenter";
import { callsEvidence, resolveWorkingContact } from './lib/callsEvidence';
import { DemoLaunchAction } from "./DemoLaunchAction";
import { CustomerLifecyclePage } from "./CustomerLifecyclePage";
import { PaymentDesk } from "./PaymentDesk";
import { CommercialApprovals } from "./CommercialApprovals";
import { SalesKnowledge } from "./SalesKnowledge";
import { CommercialClosePanel } from "./CommercialClose";
import {commercialConfirmationAction,commercialGuidanceReady,commercialIssueReady,savedCommercialSelection,type CommercialQuoteObservation} from "./lib/live-conversation/commercial-observation";
import { RevenueOperations } from "./RevenueOperations";
import { SalesRepAttribution } from "./SalesRepAttribution";
import { CallsWorkspaceReactiveBridge } from "./CallsWorkspaceReactiveBridge";
import { useLiveConversation } from "./lib/live-conversation/useLiveConversation";
import { conversationEvidence, summarizeConversation, guidanceRevision, type Plan } from "./lib/live-conversation/reducer";
import { slotFromLocal, slotToLocal } from "./lib/live-conversation/time";
import { PHASE_LABELS as REACTIVE_PHASE_LABELS } from "./ReactiveCallGuidance";
import { canonicalDispositions, deriveNextAction, dispositionPolicy } from "./lib/dispositionPolicy";
import {useCrmIdentity}from'./lib/crmIdentity';
import {invalidateCrmData,purgeCrmResourceDrafts,registerCrmDraft,crmDraftGeneration,crmDraftWritable}from'./lib/crmSync';
import {readNewEmailState,saveNewEmailState,purgeNewEmailStates,newEmailFingerprint,newEmailAttemptLabel,type NewEmailState}from'./lib/newEmailAttempts';
import {parseReplyAttemptResult,type ReplyAttemptResult}from'./lib/replyAttempts';
import {useCrmResource}from'./lib/useCrmResource';
import {CrmResourceAccessProvider}from'./lib/crmResourceAccess';
import {signOutCrm}from"./lib/crmAuth";
import type{CrmCapability}from'./lib/crmRbac';
import { resolveDemoAudience } from './lib/salesDemo';

import { WorkNavigation, WorkQueueView } from "./WorkNavigation";
import {CompanyQueueWarmup} from './CompanyQueueStartup';
import {useCompanyQueueStartup} from './lib/useCompanyQueueStartup';
import { workViewForPath, safeWorkReturn, taskConversationPath } from "./lib/workNavigation";

const roleNavItems: Record<
  WorkspaceRole,
  readonly (readonly [string, string, any])[]
> = {
  bdr: [
    ["Work", "/work/focus", ListTodo],
    ["Inbox", "/inbox", Inbox],
    ["Calendar", "/calendar", CalendarDays],
    ["Organizations", "/organizations", Building2],
    ["Customers", "/customers", CircleDollarSign],
    ["Sales Execution", "/sales-execution", CircleDollarSign],
    ["Revenue Operations", "/revenue-operations", BarChart3],
    ["Prospecting", "/prospecting", ScanSearch],
  ],
  csr: [
    ["Work", "/work/focus", ListTodo],
    ["Inbox", "/inbox", Inbox],
    ["Inbound / Active", "/communications/live", Headphones],
    ["Customer Operations", "/customer-operations", Activity],
    ["Calendar", "/calendar", CalendarDays],
    ["Organizations", "/organizations", Building2],
    ["Customers", "/customers", CircleDollarSign],
  ],
  admin: [
    ["Work", "/work/focus", ListTodo],
    ["Inbox", "/inbox", Inbox],
    ["Inbound / Active", "/communications/live", Headphones],
    ["Calendar", "/calendar", CalendarDays],
    ["Organizations", "/organizations", Building2],
    ["Customers", "/customers", CircleDollarSign],
    ["Customer Operations", "/customer-operations", Activity],
    ["Sales Execution", "/sales-execution", CircleDollarSign],
    ["Revenue Operations", "/revenue-operations", BarChart3],
    ["Prospecting", "/prospecting", ScanSearch],
    ["Admin Operations", "/admin-operations", Shield],
    ["Demo Center", "/demo-center", ShieldCheck],
    ["Team & Access", "/team-access", UserCog],
    ["Settings", "/settings", Settings],
  ],
} as const;

function navItemsForCanonicalRole(role:CrmRole,can:(capability:CrmCapability)=>boolean){
 const base=[...roleNavItems[workspaceRoleForCrmRole(role)]].filter(([,path])=>path!=='/demo-center'&&(path!=='/settings'||role==='SUPER_ADMIN'))
 let items:readonly (readonly [string,string,any])[]=base
 if(role==='SUPER_ADMIN')items=base
 else if(role==='SALES_ADMIN')items=[...base,['Inbound / Active','/communications/live',Headphones]as const,['Team & Access','/team-access',UserCog]as const]
 else if(role==='AE')items=base.filter(([,path])=>path!=='/prospecting')
 else if(role==='BDR_SDR')items=base.filter(([,path])=>path!=='/organizations')
 else if(role==='OPERATIONS_ADMIN')items=[...base,['Team & Access','/team-access',UserCog]as const]
 else if(role==='OPS')items=base.filter(([,path])=>path!=='/communications/live')
 else if(role==='CSR')items=base.filter(([,path])=>path!=='/organizations')
 if(can('founder.control.read'))items=[['Control Tower','/founder-control',Gauge]as const,...items]
 if(can('sales.write'))items=[...items,['Payment Desk','/payment-desk',Wallet]as const]
 if(can('commercial.discount.approve.tier1')||can('commercial.discount.approve.tier2')||can('commercial.discount.approve.executive'))items=[...items,['Approvals','/approvals',BadgeCheck]as const]
 if(can('commercial.policy.read'))items=[...items,['Sales Knowledge','/sales-knowledge',BookOpen]as const]
 if(role==='SUPER_ADMIN'&&can('phone.system.manage'))items=[...items,['Phone System','/admin/phone-system',PhoneCall]as const]
 if(can('phone.self.read'))items=[...items,['My Phone Setup','/my-phone-setup',Smartphone]as const]
 if(can('attribution.manage')||can('attribution.read.own'))items=[...items,[can('attribution.manage')?'Sales Rep Attribution':'My Attribution Link','/sales-rep-attribution',Link2]as const]
 if(can('demo.center.read'))items=[...items,['Demo Center','/demo-center',ShieldCheck]as const];
 if(canUseRoleplayTraining(role))items=[...items,['Roleplay Training','/roleplay-training',BookOpen]as const];
 if(can('communications.read')&&!items.some(([,path])=>path==='/communications/live'))items=[...items.slice(0,2),['Inbound / Active','/communications/live',Headphones]as const,...items.slice(2)]
 return items
}

function App() {
  const identity=useCrmIdentity();
  const communications=useCommunications();
  const [theme, setTheme] = useState<"dark" | "light">(
    () => (localStorage.getItem("as-theme") as "dark" | "light") || "dark",
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("as-sidebar") === "collapsed",
  );
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [activities, setActivities] = useState<ActivityType[]>([]);
  const [dispositions, setWorkspaceDispositions] = useState<CallDisposition[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [accountLinks, setAccountLinks] = useState<AccountLink[]>([]);
  const [lifecycleEvents, setLifecycleEvents] = useState<LifecycleEvent[]>([]);
  const [acquisitionAttributions, setAcquisitionAttributions] = useState<AcquisitionAttribution[]>([]);
  const [schedules, setSchedules] = useState<import("./types").ScheduleEvent[]>([]);
  const [notifications, setNotifications] = useState<import("./types").CrmNotification[]>([]);
  const [unavailableNotice, setUnavailableNotice] = useState<{status:string;message:string}|null>(null);
  const [dataMode, setDataMode] = useState<DataSourceMode>("loading");
  const [dataErrors, setDataErrors] = useState<string[]>([]);
  const [selected, setSelected] = useState<Contact | null>(null);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [dialNumber, setDialNumber] = useState("");
  const [dialState,setDialState]=useState<'idle'|'dialing'|'ready'|'error'>('idle');
  const [dialMessage,setDialMessage]=useState('');
  const [quickAttempt,setQuickAttempt]=useState<{attemptId:string;contactId:string|null;phoneE164:string;resolutionState:string}|null>(null);
  const [quickAttachContact,setQuickAttachContact]=useState('');
  const [quickAttachOrganization,setQuickAttachOrganization]=useState('');
  const [searchQuery, setSearchQuery] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordName, setRecordName] = useState("");
  const [recordState, setRecordState] = useState<
    "idle" | "creating" | "success" | "error"
  >("idle");
  const [recordError, setRecordError] = useState("");
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const emptyContact = () => ({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organizationId: "",
    jobTitle: "",
    source: "",
    notes: "",
    emailAllowed: false,
    emailConsentSource: "",
    emailConsentReference: "",
    smsAllowed: false,
    smsConsentSource: "",
    doNotCall: false,
  });
  const [contactForm, setContactForm] = useState(emptyContact);
  const [contactState, setContactState] = useState<
    "idle" | "creating" | "success" | "error"
  >("idle");
  const [contactError, setContactError] = useState("");
  const [previewRole,setPreviewRole]=useState<CrmRole|null>(null);
  const [showSyntheticData,setShowSyntheticData]=useState(()=>{setSyntheticDataVisibility(false);return false});
  const [signingOut,setSigningOut]=useState(false);

  const navigate = useNavigate();
  const location=useLocation();
  const workView=workViewForPath(location.pathname);
  const [callsVisited,setCallsVisited]=useState(false);
  const [followUpsVisited,setFollowUpsVisited]=useState(false);
  const inboxRoute=location.pathname==='/inbox'||location.pathname==='/communications';
  const [inboxVisited,setInboxVisited]=useState(false);
  useEffect(()=>{if(workView==='calls')setCallsVisited(true);if(workView==='follow-ups')setFollowUpsVisited(true);if(inboxRoute)setInboxVisited(true)},[workView,inboxRoute]);
  const workParams=new URLSearchParams(location.search);
  const explicitContactId=workView==='calls'?workParams.get('contactId')||'':'';
  const explicitOrganizationId=workView==='calls'?workParams.get('organizationId')||'':'';
  const explicitPhoneRouteId=workView==='calls'?workParams.get('phoneRouteId')||'':'';
  const explicitTaskId=workView==='calls'?workParams.get('taskId')||'':'';
  const explicitReturn=workView==='calls'?safeWorkReturn(workParams.get('returnTo')):'/work/queue';
  const explicitKey=location.key||location.pathname+location.search;
  const [callNavigation,setCallNavigation]=useState({contactId:'',organizationId:'',phoneRouteId:'',taskId:'',returnTo:'/work/queue',key:''});
  const explicitCallContext=Boolean(explicitContactId||explicitOrganizationId);
  useEffect(()=>{if(explicitCallContext)setCallNavigation({contactId:explicitContactId,organizationId:explicitOrganizationId,phoneRouteId:explicitPhoneRouteId,taskId:explicitTaskId,returnTo:explicitReturn,key:explicitKey})},[explicitCallContext,explicitContactId,explicitOrganizationId,explicitPhoneRouteId,explicitTaskId,explicitReturn,explicitKey]);
  const requestedContactId=explicitCallContext?explicitContactId:callNavigation.contactId;
  const requestedOrganizationId=explicitCallContext?explicitOrganizationId:callNavigation.organizationId;
  const requestedPhoneRouteId=explicitCallContext?explicitPhoneRouteId:callNavigation.phoneRouteId;
  const requestedTask=tasks.find(task=>task.id===(explicitCallContext?explicitTaskId:callNavigation.taskId));
  const voice = useCrmVoice();
  const { configureWorkspace } = voice;
  const placeQuickDial=async()=>{
    if(dialState==='dialing'||['connecting','dialing','ringing','connected'].includes(voice.state))return;
    setDialState('dialing');setDialMessage('Validating and normalizing…');
    try{const attempt=await voice.quickDial(dialNumber);setQuickAttempt(attempt);setDialMessage(`${attempt.phoneE164} · ${attempt.resolutionState.replaceAll('_',' ').toLowerCase()}`);setDialState('ready')}catch(error){setDialMessage(error instanceof Error?error.message:'quick_dial_failed');setDialState('error')}
  };
  const attachQuickDial=async()=>{
    if(!quickAttempt||(!quickAttachContact&&!quickAttachOrganization))return;
    setDialMessage('Attaching original call record…');
    try{await voice.attachCall({attemptId:quickAttempt.attemptId,contactId:quickAttachContact||null,organizationId:quickAttachContact?null:quickAttachOrganization||null});setDialMessage('Original call record attached.');setQuickAttempt({...quickAttempt,resolutionState:'UNIQUE_MATCH'})}catch(error){setDialMessage(error instanceof Error?error.message:'voice_attachment_failed')}
  };
  const handleLogout=useCallback(async()=>{
    if(signingOut)return;
    setSigningOut(true);
    try{await signOutCrm()}finally{
      window.location.replace(`${window.location.origin}/`);
    }
  },[signingOut]);
  const openCompanyCreate = () => {
    setRecordOpen(true);
    setRecordState("idle");
    setRecordError("");
  };
  const openContactCreate = () => {
    setContactOpen(true);
    setQuickCreateOpen(false);
    setContactState("idle");
    setContactError("");
  };

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("as-theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(
      "as-sidebar",
      sidebarCollapsed ? "collapsed" : "expanded",
    );
  }, [sidebarCollapsed]);

  const queueStartup=useCompanyQueueStartup()
  const core=useCrmResource<import('./lib/runtimeState').CrmDataSnapshot>('core-snapshot',(previous,context)=>{
    setSyntheticDataVisibility(showSyntheticData)
    return queueStartup.readSnapshot(previous,context)
  },showSyntheticData)
  const history=useCrmResource('dispositions',()=>getDispositions(),showSyntheticData)
  useEffect(()=>{
    const snapshot=core.data
    if(!snapshot){setContacts([]);setActivities([]);setTasks([]);setOpportunities([]);setOrganizations([]);setAccountLinks([]);setLifecycleEvents([]);setAcquisitionAttributions([]);setSchedules([]);setNotifications([]);setSelected(null);setDataMode(core.error?'degraded':'loading');setDataErrors(core.error?['membership']:[]);return}
    setContacts(snapshot.contacts);setActivities(snapshot.activities);setTasks(snapshot.tasks);setOpportunities(snapshot.opportunities);setOrganizations(snapshot.organizations);setAccountLinks(snapshot.accountLinks);setLifecycleEvents(snapshot.lifecycleEvents);setAcquisitionAttributions(snapshot.acquisitionAttributions);setSchedules(snapshot.schedules);setNotifications(snapshot.notifications)
    setSelected(current=>snapshot.denied?.includes('contacts')?null:current?snapshot.contacts.find(item=>item.id===current.id)||current:snapshot.contacts[0]||null)
    setDataMode(core.error?'degraded':snapshot.mode);setDataErrors(core.error?['updates delayed']:snapshot.errors)
  },[core.data,core.error])
  useEffect(()=>{setWorkspaceDispositions(history.data||[])},[history.data])
  useEffect(()=>configureWorkspace({contacts,activities,dispositions},contactId=>navigate(`/organizations/contact/${contactId}`)),[activities,configureWorkspace,contacts,dispositions,navigate]);

  const deniedResources=core.data?.denied??[];
  useEffect(()=>{for(const resource of core.data?.denied??[])purgeCrmResourceDrafts(resource)},[core.data]);
  const routeResources=workView==='calls'||location.pathname.startsWith('/calls')||location.pathname.startsWith('/organizations')||(location.pathname.startsWith('/communications/live')||location.pathname.startsWith('/communications/texts'))?['contacts','organizations']:(workView==='queue'||workView==='follow-ups'||location.pathname.startsWith('/tasks'))?['tasks']:[];
  const routeDenied=Boolean(core.error&&!core.data)||routeResources.some(resource=>deniedResources.includes(resource as import('./lib/runtimeState').DataResource));
  const actualRole=identity.role;
  const canViewSyntheticData=['SUPER_ADMIN','SALES_ADMIN','OPERATIONS_ADMIN'].includes(actualRole);
  const effectiveRole=effectiveCrmRole(actualRole,previewRole);
  const can=(capability:CrmCapability)=>previewRole?hasCrmCapability(effectiveRole,capability):identity.capabilities.includes(capability);
  const workspaceRole=workspaceRoleForCrmRole(effectiveRole) as WorkspaceRole;
  const navItems=navItemsForCanonicalRole(effectiveRole,can);

  const runtimeConfiguration = validateRuntimeConfiguration({
    supabaseUrl: config.supabaseUrl,
    supabaseAnonKey: config.supabaseAnonKey,
    workspaceId: config.workspaceId,
    apiBase: config.apiBase,
  });
  const sourceMessage = dataSourceMessage(dataMode);

  const queueWarmup=workView==='queue'&&!routeDenied&&queueStartup.ready(core.permissionContext)?<CompanyQueueWarmup key="company-queue-warmup" isReady={()=>queueStartup.ready(core.permissionContext)}/>:null
  if(!core.data)return <>{queueWarmup}<div className="runtime-banner degraded" role="status">{core.error||'Loading authorized CRM resources…'}</div></>;
  return (
    <>{queueWarmup}
    <div className={`app-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`} onSubmitCapture={(event)=>{if(!previewRole)return;event.preventDefault();event.stopPropagation();setUnavailableNotice({status:'ROLE PREVIEW',message:`This is a non-mutating preview. ${effectiveRole.replaceAll('_',' ')} permissions are shown, but no record will be changed.`})}} onClickCapture={(event)=>{const element=event.target as HTMLElement;const target=element.closest<HTMLElement>('[data-unavailable]');if(target){event.preventDefault();event.stopPropagation();setUnavailableNotice({status:target.dataset.status||'DEFERRED',message:target.dataset.unavailable||'This action is not currently available.'});return}if(!previewRole)return;const button=element.closest<HTMLButtonElement>('button');if(!button||button.dataset.previewSafe==='true')return;const label=(button.textContent||'').trim();if(/save|create|send|approve|reject|suppress|re-scan|run |archive|pause|resume|complete|grant|disable|enable|call number|add /i.test(label)){event.preventDefault();event.stopPropagation();setUnavailableNotice({status:'ROLE PREVIEW',message:`${label||'This action'} is disabled in preview so your Super Admin authority cannot perform a change on behalf of ${effectiveRole.replaceAll('_',' ')}.`})}}}>
      <aside
        className={`sidebar ${mobileOpen ? "open" : ""} ${sidebarCollapsed ? "collapsed" : ""}`}
      >
        <div className="brand">
          <AvertSignalLockup
            subtitle="FROM FIRST SIGNAL TO VERIFIED RESOLUTION"
            markSize={42}
          />
          <button
            className="sidebar-collapse-btn desktop-only"
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!sidebarCollapsed}
            aria-controls="crm-sidebar-nav"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => setSidebarCollapsed((v) => !v)}
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen size={18} />
            ) : (
              <PanelLeftClose size={18} />
            )}
          </button>
          <button
            className="icon-btn mobile-only"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        <nav id="crm-sidebar-nav" aria-label="CRM navigation">
          {navItems.map(([label, to, Icon]) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                isActive || (to==='/work/focus'&&Boolean(workView)) || (to==='/inbox'&&location.pathname==='/communications') ? "nav-item active" : "nav-item"
              }
            >
              <Icon size={18} />
              <span className="nav-label">{label}</span>
              {to==='/communications/live'&&communications.actionableCount>0&&<span className="nav-actionable-badge" aria-label={`${communications.actionableCount} inbound communications`}>{communications.actionableCount>99?'99+':communications.actionableCount}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <NavLink
            to="/workforce-privacy"
            className="nav-item"
            onClick={() => setMobileOpen(false)}
          >
            <FileText size={16} />
            <span className="nav-label">Workforce privacy</span>
          </NavLink>
          <div className={`system-health ${dataMode}`}>
            <span
              className={`status-dot ${dataMode === "live" ? "connected" : ""}`}
            />
            <div>
              <strong>{sourceMessage.title}</strong>
              <span>
                {dataMode === "live"
                  ? "Endpoint evidence confirmed"
                  : "Not production truth"}
              </span>
            </div>
          </div>
          <div
            className="user-chip role-user-chip user-chip-link"
            onClick={() => navigate("/sales-knowledge")}
            onKeyDown={(event) => {
              if (event.key === "Enter") navigate("/sales-knowledge");
            }}
            title="Sales profile & commercial policy"
            role="link"
            tabIndex={0}
            aria-label="Sales profile and commercial policy"
          >
            <div className="avatar">{identity.initials}</div>
            <div>
              <strong>{identity.displayName}</strong>
              <span>{effectiveRole.replaceAll('_',' ')}</span>
            </div>
            <ChevronRight size={16} />
          </div>
          <button
            type="button"
            className="nav-item logout-action"
            data-preview-safe="true"
            aria-label="Log out of the CRM"
            disabled={signingOut}
            onClick={() => { void handleLogout(); }}
          >
            <LogOut size={16} />
            <span className="nav-label">{signingOut ? "Signing out…" : "Log out"}</span>
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button
            className="icon-btn mobile-only"
            aria-label="Open navigation"
            onClick={() => setMobileOpen(true)}
          >
            <Menu size={20} />
          </button>
          <div className="global-search">
            <Search size={18} />
            <input
              aria-label="Search CRM records"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                if (event.target.value.trim()) navigate("/organizations");
              }}
              placeholder="Search organizations, contacts, emails, phones..."
            />
          </div>
          <div className="top-actions">
        <div className="header-dialer">
          <button
            className="chat-launcher header-dialer-toggle"
            aria-label="Quick dial"
            aria-expanded={dialerOpen}
            title="Quick dial"
            onClick={() => setDialerOpen(!dialerOpen)}
          >
            <PhoneCall size={17} />
            <span>Quick dial</span>
            <ChevronRight size={15} />
          </button>
          {dialerOpen && (
            <div className="dialer-popout">
              <input
                value={dialNumber}
                onChange={(e) => {setDialNumber(e.target.value);setDialState('idle');setDialMessage('')}}
                onKeyDown={event=>{if(event.key==='Enter'){event.preventDefault();void placeQuickDial()}}}
                placeholder="Phone number"
                aria-label="Quick Dial phone number"
              />
              <div className="dialpad-grid">
                {[
                  "1",
                  "2",
                  "3",
                  "4",
                  "5",
                  "6",
                  "7",
                  "8",
                  "9",
                  "*",
                  "0",
                  "#",
                ].map((n) => (
                  <button type="button" key={n} onClick={() => setDialNumber((v) => v + n)}>
                    {n}
                  </button>
                ))}
              </div>
              <button type="button" className="secondary-btn compact" aria-label="Delete last Quick Dial character" onClick={()=>setDialNumber(value=>value.slice(0,-1))}>⌫ Delete</button>
              {dialMessage&&<small className={dialState==='error'?'voice-call-error':''} aria-live="polite">{dialMessage}</small>}
              <button type="button" className="call-button compact" disabled={dialState==='dialing'||['connecting','dialing','ringing','connected'].includes(voice.state)} onClick={()=>void placeQuickDial()}>
                <Phone size={16} /> Call number
              </button>
              {quickAttempt&&!quickAttempt.contactId&&<div className="quick-dial-attachment">
                <select aria-label="Attach Quick Dial call to contact" value={quickAttachContact} onChange={event=>{setQuickAttachContact(event.target.value);if(event.target.value)setQuickAttachOrganization('')}}><option value="">Contact (optional)</option>{contacts.map(contact=><option key={contact.id} value={contact.id}>{contact.name}</option>)}</select>
                <select aria-label="Attach Quick Dial call to organization" value={quickAttachOrganization} disabled={Boolean(quickAttachContact)} onChange={event=>setQuickAttachOrganization(event.target.value)}><option value="">Organization (optional)</option>{organizations.map(organization=><option key={organization.id} value={organization.id}>{organization.name}</option>)}</select>
                <button type="button" className="attach-org-btn" disabled={!quickAttachContact&&!quickAttachOrganization} onClick={()=>void attachQuickDial()}>Attach original call record</button>
              </div>}
            </div>
          )}
        </div>


            <span className="top-role-pill">
              {workspaceRoleLabels[workspaceRole]}
            </span>
            {actualRole==='SUPER_ADMIN'&&<label className="view-as-control">View as<select data-preview-safe="true" aria-label="View as role" value={previewRole||actualRole} onChange={event=>{const role=event.target.value as CrmRole;setPreviewRole(role==='SUPER_ADMIN'?null:role)}}>{ROLE_PREVIEW_OPTIONS.map(role=><option key={role} value={role}>{role.replaceAll('_',' ')}</option>)}</select></label>}
            {canViewSyntheticData&&<label className="synthetic-data-toggle" title="Include governed demo and acceptance records in CRM views"><input data-preview-safe="true" type="checkbox" checked={showSyntheticData} onChange={event=>{setSyntheticDataVisibility(event.target.checked);setShowSyntheticData(event.target.checked)}}/><span>Show synthetic / acceptance data</span></label>}
            <button
              className={`chat-launcher ${chatOpen ? "active" : ""}`}
              aria-label="Team Chat"
              onClick={() => setChatOpen((value) => !value)}
            >
              <MessageSquareText size={17} />
              <span>Team chat</span>
              {chatUnread && (
                <i className="unread-dot" aria-label="New team chat messages" />
              )}
            </button>
            <button
              className="chat-launcher"
              aria-label="Meetings"
              onClick={() => navigate("/calendar?meeting=new")}
            >
              <CalendarDays size={17} />
              <span>Meetings</span>
            </button>
            <NotificationCenter items={notifications} setItems={setNotifications} onNavigate={navigate}/>
            <button
              className="theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
              <span>{theme === "dark" ? "Light" : "Dark"}</span>
            </button>
            <div className="quick-create">
              <button
                className="primary-btn"
                onClick={() => setQuickCreateOpen((value) => !value)}
                aria-expanded={quickCreateOpen}
              >
                <Plus size={17} /> Add
              </button>
              {quickCreateOpen && (
                <div className="quick-create-menu">
                  <button
                    onClick={() => {
                      setQuickCreateOpen(false);
                      openCompanyCreate();
                    }}
                  >
                    <Building2 size={16} /> Company
                  </button>
                  <button onClick={openContactCreate}>
                    <ContactRound size={16} /> Contact
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {previewRole&&<div className="role-preview-banner" role="status"><Eye size={17}/><div><strong>Viewing as {effectiveRole.replaceAll('_',' ')}</strong><span>You are previewing this role. Your Super Admin access has not changed.</span></div><button data-preview-safe="true" onClick={()=>setPreviewRole(null)}>Exit preview</button></div>}

        {(dataMode !== "live" || !runtimeConfiguration.valid) && (
          <div className={`runtime-banner ${dataMode}`} role="status">
            <AlertTriangle size={16} />
            <div>
              <strong>{sourceMessage.title}</strong>
              <span>
                {sourceMessage.detail}
                {dataErrors.length
                  ? ` Unavailable: ${dataErrors.join(", ")}.`
                  : ""}
                {!runtimeConfiguration.valid
                  ? ` Configuration: ${runtimeConfiguration.issues.join(", ")}.`
                  : ""}
              </span>
            </div>
          </div>
        )}

        <CrmResourceAccessProvider value={deniedResources}>
        <section className="work-shell" hidden={!workView} aria-label="Work workspace">
          {workView&&<WorkNavigation view={workView}/>}
          {routeDenied&&workView?<div className="runtime-banner degraded" role="alert">Access to this resource is unavailable. Protected details and drafts have been cleared. Revalidating automatically.</div>:null}
          {!routeDenied&&workView==='focus'&&<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole} organizations={organizations} contacts={contacts} opportunities={opportunities} tasks={tasks}/>}
          {!routeDenied&&workView==='queue'&&<WorkQueueView tasks={tasks} contacts={contacts} userId={identity.userId}/>}
          {followUpsVisited&&!deniedResources.includes('tasks')&&<div hidden={workView!=='follow-ups'}><TaskWorkspace tasks={tasks} setTasks={setTasks} contacts={contacts} organizations={organizations} role={workspaceRole} live={dataMode==='live'} active={workView==='follow-ups'} onOpenConversation={task=>{const path=taskConversationPath(task,location.pathname+location.search);if(path)navigate(path)}}/></div>}
          {callsVisited&&!deniedResources.includes('contacts')&&!deniedResources.includes('organizations')&&<div hidden={workView!=='calls'}><CallsPage isVisible={workView==='calls'} activities={activities} dispositions={dispositions} historyUnavailable={dataMode!=='live'} contacts={contacts} organizations={organizations} opportunities={opportunities} accountLinks={accountLinks} acquisitionAttributions={acquisitionAttributions} onOpportunitiesChanged={invalidateCrmData} requestedContactId={requestedContactId} requestedOrganizationId={requestedOrganizationId} requestedPhoneRouteId={requestedPhoneRouteId} requestKey={explicitCallContext?explicitKey:callNavigation.key} taskContext={requestedTask} returnTo={explicitCallContext?explicitReturn:callNavigation.returnTo}/></div>}
        </section>
        {inboxVisited&&<div className="inbox-route-shell" hidden={!inboxRoute}><CommunicationsInbox contacts={contacts} includeSynthetic={showSyntheticData}/></div>}
        {routeDenied&&!workView?<div className="runtime-banner degraded" role="alert">Access to this resource is unavailable. Protected details and drafts have been cleared. Revalidating automatically.</div>:<Routes>
          <Route path="/" element={null}/><Route path="/work/*" element={null}/><Route path="/calls" element={null}/>
          <Route path="/communications/texts" element={<Navigate replace to="/communications/live?channel=texts"/>}/>
          <Route path="/communications/live" element={<CustomerCallsPage contacts={contacts} activities={activities} dispositions={dispositions} schedules={schedules} tasks={tasks} includeSynthetic={showSyntheticData} />} />
          <Route path="/communications/calls/:callId" element={<CallDetailsPage />} />
          <Route path="/customer-calls" element={<Navigate replace to="/communications/live" />} />
          <Route path="/calendar" element={<SchedulingPage events={schedules} setEvents={setSchedules} contacts={contacts} organizations={organizations}/>} />
          <Route
            path="/organizations"
            element={
              <OrganizationsPage
                contacts={contacts}
                organizations={organizations}
                accountLinks={accountLinks}
                lifecycleEvents={lifecycleEvents}
                acquisitionAttributions={acquisitionAttributions}
                canLaunchDemo={can('sales.write')}
                activities={activities}
                query={searchQuery}
                live={dataMode === "live"}
                includeSynthetic={showSyntheticData}
                onCreateCompany={openCompanyCreate}
                onOrganizationsChange={setOrganizations}
                onContactsChange={setContacts}
              />
            }
          />
          <Route
            path="/organizations/contact/:contactId"
            element={
              <OrganizationsPage
                contacts={contacts}
                organizations={organizations}
                accountLinks={accountLinks}
                lifecycleEvents={lifecycleEvents}
                acquisitionAttributions={acquisitionAttributions}
                canLaunchDemo={can('sales.write')}
                activities={activities}
                query=""
                live={dataMode === "live"}
                includeSynthetic={showSyntheticData}
                onCreateCompany={openCompanyCreate}
                onOrganizationsChange={setOrganizations}
                onContactsChange={setContacts}
              />
            }
          />
          <Route
            path="/customer-operations"
            element={<CustomerOperationsPage />}
          />
          <Route
            path="/customers"
            element={can("crm.read") ? <CustomerLifecyclePage key={showSyntheticData?'synthetic':'real'} includeSynthetic={showSyntheticData} /> : <RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole} />}
          />
          <Route
            path="/payment-desk"
            element={can("sales.write") ? <PaymentDesk /> : <RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole} />}
          />
          <Route path="/approvals" element={(can('commercial.discount.approve.tier1')||can('commercial.discount.approve.tier2')||can('commercial.discount.approve.executive'))?<CommercialApprovals/>:<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole}/>} />
          <Route path="/roleplay-training" element={canUseRoleplayTraining(actualRole)?<RoleplayTraining key={identity.userId+':'+identity.workspaceId}/>:<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole}/>} />
          <Route path="/sales-knowledge" element={can('commercial.policy.read')?<SalesKnowledge/>:<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole}/>} />
          <Route
            path="/sales-execution"
            element={<OpportunityWorkspace opportunities={opportunities} setOpportunities={setOpportunities} organizations={organizations} contacts={contacts} accountLinks={accountLinks} acquisitionAttributions={acquisitionAttributions} />}
          />
          <Route path="/revenue-operations" element={can('sales.read')?<RevenueOperations key={showSyntheticData?'synthetic':'real'} opportunities={opportunities} tasks={tasks} canManage={can('commercial.policy.manage')}/>:<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole}/>} />
          <Route path="/sales-rep-attribution" element={can('attribution.manage')||can('attribution.read.own')?<SalesRepAttribution canManage={can('attribution.manage')}/>:<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole}/>} />
          <Route path="/prospecting" element={can('sales.read')?<ProspectingPage role={effectiveRole} />:<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole}/>} />
          <Route
            path="/admin-operations"
            element={
              workspaceRole === "admin" ? (
                <AdminOperationsPage />
              ) : (
                <RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole} />
              )
            }
          /><Route path="/demo-center" element={can('demo.center.read')?<DemoCenter />:<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole}/>} />
          <Route path="/founder-control" element={can('founder.control.read')?<FounderControlTower key={showSyntheticData?'synthetic':'real'} includeSynthetic={showSyntheticData}/>:<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole}/>} />
          <Route
            path="/visibility-intelligence"
            element={<CustomerOperationsPage />}
          />
          <Route
            path="/contacts"
            element={
              <OrganizationsPage
                contacts={contacts}
                organizations={organizations}
                accountLinks={accountLinks}
                lifecycleEvents={lifecycleEvents}
                acquisitionAttributions={acquisitionAttributions}
                canLaunchDemo={can('sales.write')}
                activities={activities}
                query={searchQuery}
                live={dataMode === "live"}
                includeSynthetic={showSyntheticData}
                onCreateCompany={openCompanyCreate}
                onOrganizationsChange={setOrganizations}
                onContactsChange={setContacts}
              />
            }
          />
          <Route
            path="/pipeline"
            element={<OpportunityWorkspace opportunities={opportunities} setOpportunities={setOpportunities} organizations={organizations} contacts={contacts} accountLinks={accountLinks} acquisitionAttributions={acquisitionAttributions} />}
          />
          <Route path="/inbox" element={null} />
          <Route
            path="/communications"
            element={null}
          />
          <Route path="/tasks" element={null}/>
          <Route path="/admin-foundation" element={can('audit.read.all')?<AdminFoundation role={effectiveRole} />:<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole}/>} />
          <Route path="/team-access" element={can('users.manage.bounded')?<TeamAccess />:<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole}/>} />
          <Route path="/admin/phone-system" element={actualRole==='SUPER_ADMIN'&&effectiveRole==='SUPER_ADMIN'&&can('phone.system.manage')?<PhoneSystemAdmin />:<div className="runtime-banner issue" role="alert">Phone System is available only to Super Admin.</div>} />
          <Route path="/my-phone-setup" element={can('phone.self.read')?<MyPhoneSetup />:<RoleCommandCenter role={workspaceRole} canonicalRole={effectiveRole}/>} />
          <Route path="/settings" element={actualRole==='SUPER_ADMIN'&&effectiveRole==='SUPER_ADMIN'&&can('system.configure')?<SettingsPage dataMode={dataMode} />:<div className="runtime-banner issue" role="alert">Settings is available only to Super Admin.</div>} />
          <Route path="/workforce-privacy" element={<WorkforcePrivacyPage />} />
          <Route path="*" element={<div className="page"><div className="page-heading"><div><span className="eyebrow">NAVIGATION</span><h2>Page not found</h2><p>The requested view does not exist in this workspace.</p></div></div><Link className="primary-btn" to="/">Back to command center</Link></div>} />
        </Routes>}</CrmResourceAccessProvider>
        {unavailableNotice&&<div className="unavailable-notice" role="status"><div><b>{unavailableNotice.status.replaceAll('_',' ')}</b><span>{unavailableNotice.message}</span></div><button aria-label="Close explanation" onClick={()=>setUnavailableNotice(null)}><X size={16}/></button></div>}
        <TeamChat
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          onUnreadChange={setChatUnread}
        />
        {recordOpen && (
          <div className="record-modal-backdrop">
            <form
              className="record-modal"
              onSubmit={async (event) => {
                event.preventDefault();
                if (recordState === "creating") return;
                const name = recordName.trim();
                if (!name) {
                  setRecordState("error");
                  setRecordError("Organization name is required.");
                  return;
                }
                setRecordState("creating");
                setRecordError("");
                const recordId = crypto.randomUUID();
                try {
                  const saved = await createOrganization(recordId, name);
                  const created: Organization = {
                    id: saved.id,
                    name: saved.name,
                    accountStatus: saved.account_status,
                    ownerUserId: saved.owner_user_id || undefined,
                    ownerAppAccountId: saved.owner_app_account_id || undefined,
                    agencyAccountId: saved.agency_account_id || undefined,
                    updatedAt: saved.updated_at,
                  };
                  const refreshedOrganizations = await getOrganizations();
                  setOrganizations(
                    refreshedOrganizations.some((item) => item.id === created.id)
                      ? refreshedOrganizations
                      : [created, ...refreshedOrganizations],
                  );
                  setRecordState("success");
                  setRecordName("");
                } catch (error) {
                  setRecordState("error");
                  setRecordError(
                    error instanceof Error
                      ? error.message.replaceAll("_", " ")
                      : "Record creation failed.",
                  );
                }
              }}
            >
              <header>
                <div>
                  <span className="eyebrow">NEW COMPANY</span>
                  <h2>Add company</h2>
                  <p>Create a company as a prospect in this CRM workspace.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setRecordOpen(false)}
                  aria-label="Close"
                >
                  <X />
                </button>
              </header>
              <label>
                Organization name
                <input
                  value={recordName}
                  onChange={(event) => setRecordName(event.target.value)}
                  maxLength={200}
                  required
                  autoFocus
                />
              </label>
              {recordState === "success" && (
                <div className="composer-success" role="status">
                  Company created successfully.
                </div>
              )}
              {recordState === "error" && (
                <div className="composer-error" role="alert">
                  {recordError}
                </div>
              )}
              <footer>
                <button type="button" onClick={() => setRecordOpen(false)}>
                  Close
                </button>
                <button
                  className="primary-btn"
                  disabled={recordState === "creating"}
                >
                  {recordState === "creating" ? "Creating…" : "Create company"}
                </button>
              </footer>
            </form>
          </div>
        )}
        {contactOpen && (
          <div className="record-modal-backdrop">
            <form
              className="record-modal contact-create-modal"
              noValidate
              onSubmit={async (event) => {
                event.preventDefault();
                if (contactState === "creating") return;
                if (
                  !contactForm.firstName.trim() ||
                  !contactForm.lastName.trim() ||
                  (!contactForm.email.trim() && !contactForm.phone.trim())
                ) {
                  setContactState("error");
                  setContactError(
                    "First name, last name, and an email or phone are required.",
                  );
                  return;
                }
                if (
                  contactForm.emailAllowed &&
                  (!contactForm.email.trim() ||
                    !contactForm.emailConsentSource.trim())
                ) {
                  setContactState("error");
                  setContactError(
                    "Email consent requires an email and consent source.",
                  );
                  return;
                }
                if (
                  contactForm.smsAllowed &&
                  (!contactForm.phone.trim() ||
                    !contactForm.smsConsentSource.trim())
                ) {
                  setContactState("error");
                  setContactError(
                    "SMS consent requires a phone and consent source.",
                  );
                  return;
                }
                setContactState("creating");
                setContactError("");
                try {
                  const saved = await createContact({
                    recordId: crypto.randomUUID(),
                    ...contactForm,
                    organizationId: contactForm.organizationId || null,
                  });
                  const created = {
                    ...contactFromRaw(saved),
                    company:
                      organizations.find(
                        (item) => item.id === contactForm.organizationId,
                      )?.name || "Unassociated",
                  };
                  setContacts((current) =>
                    current.some((item) => item.id === created.id)
                      ? current
                      : [created, ...current],
                  );
                  setSelected(created);
                  setContactState("success");
                  setContactForm(emptyContact());
                } catch (error) {
                  setContactState("error");
                  setContactError(
                    error instanceof Error
                      ? error.message.replaceAll("_", " ")
                      : "Contact creation failed.",
                  );
                }
              }}
            >
              <header>
                <div>
                  <span className="eyebrow">NEW CONTACT</span>
                  <h2>Add contact</h2>
                  <p>
                    Create a person and record communication permission
                    explicitly.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setContactOpen(false)}
                  aria-label="Close"
                >
                  <X />
                </button>
              </header>
              <div className="contact-create-grid">
                <label>
                  First name
                  <input
                    value={contactForm.firstName}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        firstName: e.target.value,
                      })
                    }
                    maxLength={100}
                    required
                    autoFocus
                  />
                </label>
                <label>
                  Last name
                  <input
                    value={contactForm.lastName}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        lastName: e.target.value,
                      })
                    }
                    maxLength={100}
                    required
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, email: e.target.value })
                    }
                    maxLength={320}
                  />
                </label>
                <label>
                  Phone
                  <input
                    value={contactForm.phone}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, phone: e.target.value })
                    }
                    maxLength={40}
                  />
                </label>
                <label>
                  Associated company <span>Optional</span>
                  <select
                    value={contactForm.organizationId}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        organizationId: e.target.value,
                      })
                    }
                  >
                    <option value="">No company yet</option>
                    {organizations.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Job title <span>Optional</span>
                  <input
                    value={contactForm.jobTitle}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        jobTitle: e.target.value,
                      })
                    }
                    maxLength={200}
                  />
                </label>
                <label>
                  Source <span>Optional</span>
                  <input
                    value={contactForm.source}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, source: e.target.value })
                    }
                    maxLength={200}
                  />
                </label>
                <label className="wide">
                  Notes <span>Optional</span>
                  <textarea
                    value={contactForm.notes}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, notes: e.target.value })
                    }
                    maxLength={5000}
                  />
                </label>
              </div>
              <fieldset>
                <legend>Communication consent</legend>
                <div className="consent-grid">
                  <section>
                    <label className="consent-toggle">
                      <input
                        type="checkbox"
                        checked={contactForm.emailAllowed}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            emailAllowed: e.target.checked,
                          })
                        }
                      />{" "}
                      Email allowed
                    </label>
                    <label>
                      Consent source
                      <input
                        disabled={!contactForm.emailAllowed}
                        value={contactForm.emailConsentSource}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            emailConsentSource: e.target.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      Note / reference <span>Optional</span>
                      <input
                        disabled={!contactForm.emailAllowed}
                        value={contactForm.emailConsentReference}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            emailConsentReference: e.target.value,
                          })
                        }
                      />
                    </label>
                  </section>
                  <section>
                    <label className="consent-toggle">
                      <input
                        type="checkbox"
                        checked={contactForm.smsAllowed}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            smsAllowed: e.target.checked,
                          })
                        }
                      />{" "}
                      SMS allowed
                    </label>
                    <label>
                      Consent source
                      <input
                        disabled={!contactForm.smsAllowed}
                        value={contactForm.smsConsentSource}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            smsConsentSource: e.target.value,
                          })
                        }
                      />
                    </label>
                    <label className="consent-toggle">
                      <input
                        type="checkbox"
                        checked={contactForm.doNotCall}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            doNotCall: e.target.checked,
                          })
                        }
                      />{" "}
                      Do not call
                    </label>
                  </section>
                </div>
                <p className="consent-law">
                  An address or phone number never grants permission. Select
                  allowed only when evidence is available.
                </p>
              </fieldset>
              {contactState === "success" && (
                <div className="composer-success" role="status">
                  Contact and consent evidence saved successfully.
                </div>
              )}
              {contactState === "error" && (
                <div className="composer-error" role="alert">
                  {contactError}
                </div>
              )}
              <footer>
                <button type="button" onClick={() => setContactOpen(false)}>
                  Close
                </button>
                <button
                  className="primary-btn"
                  disabled={contactState === "creating"}
                >
                  {contactState === "creating" ? "Creating…" : "Create contact"}
                </button>
              </footer>
            </form>
          </div>
        )}
      </main>
    </div>
    </>
  );
}

function WorkforcePrivacyPage() {
  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">EMPLOYEE &amp; CONTRACTOR POLICY</span>
          <h1>Internal communications privacy</h1>
          <p>
            This policy is available to employees and contractors at any time.
          </p>
        </div>
      </div>
      <section className="panel">
        <h2>CRM-mediated internal messages</h2>
        <p>
          Internal messages that pass through the AvertSignal CRM may be
          retained for 14 days solely to support conflict resolution. They are
          automatically removed after that period.
        </p>
        <p>
          Access to retained message content is restricted to the SUPER_ADMIN
          role. Each archive review is recorded in the CRM audit history.
        </p>
        <p>
          The archive covers only messages received or sent through the CRM
          integration. It does not claim access to Zoho Cliq activity that the
          CRM did not receive.
        </p>
      </section>
    </div>
  );
}


type CallsProps = { isVisible: boolean; contacts: Contact[]; organizations: Organization[]; opportunities: Opportunity[]; accountLinks: AccountLink[]; acquisitionAttributions: AcquisitionAttribution[]; activities: ActivityType[]; dispositions: CallDisposition[]; historyUnavailable: boolean; onOpportunitiesChanged?: () => void; requestedContactId?: string; requestedOrganizationId?:string; requestedPhoneRouteId?:string; selectedPhoneRoute?:{id:string;phone:string;contactId:string|null;purpose:string}|null; retainedContact?:Contact; companyOnlyRequestKey?:string; onCompanyOnlyReady?:(key:string)=>void; onWorkingContactChange?:(contactId:string)=>void; requestKey?: string; taskContext?: Task; returnTo?: string };
function CallsPanelContent({render}:{render:()=>ReactNode}) {return render()}

function CallsPage(props: CallsProps) {
  const context=companyCallContext(props.contacts,props.organizations,props.requestedOrganizationId,props.requestedContactId);
  const scope=JSON.stringify([props.requestedOrganizationId,props.requestKey]);
  const currentScope=useRef(scope);currentScope.current=scope;
  const [routeSelection,setRouteSelection]=useState<{scope:string;route:NonNullable<CallsProps['selectedPhoneRoute']>}|null>(null);
  const selectedPhoneRoute=routeSelection?.scope===scope?routeSelection.route:null;
  const requestedContactId=selectedPhoneRoute?.contactId||props.requestedContactId||context.contact?.id;
  const [initialContact,setInitialContact]=useState<Contact|null>(()=>context.contact||null);
  const [workingContactId,setWorkingContactId]=useState(context.contact?.id??'');
  useEffect(()=>{if(!initialContact&&context.contact)setInitialContact(context.contact)},[initialContact,context.contact]);
  const callContextReady=!initialContact||Boolean(context.contacts.some(contact=>contact.id===workingContactId)&&(!selectedPhoneRoute?.contactId||selectedPhoneRoute.contactId===workingContactId));
  const company=context.company;
  const companyContext=props.requestedOrganizationId&&<section className="panel" aria-label="Selected company calling context">
    {company?<><h2>{company.name}</h2><p>Company loaded for calling. Choose a saved line and review its eligibility before placing a call.</p>
    <CompanyPhoneRoutes companyId={company.id} contactId={requestedContactId} selectedRouteId={selectedPhoneRoute?.id??props.requestedPhoneRouteId} mode="call" callContextReady={callContextReady} onSelect={route=>{if(route.contactId&&!context.contacts.some(contact=>contact.id===route.contactId))return;setRouteSelection(previous=>previous?.scope===scope&&previous.route.id===route.id&&previous.route.phone===route.phone&&previous.route.contactId===route.contactId&&previous.route.purpose===route.purpose?previous:{scope,route})}}/>
    {!callContextReady&&<p role="status">The prior contact and its drafts are still open. Complete the contact switch before calling this selected number.</p>}
    <CompanyIntelligenceProfile companyId={company.id}/>
    <details open><summary>Associated contacts ({context.contacts.length})</summary>{context.contacts.length?context.contacts.map(contact=><p key={contact.id}><Link to={'/work/calls?'+new URLSearchParams({organizationId:company.id,contactId:contact.id,returnTo:safeWorkReturn(props.returnTo??null)})}>{contact.name}</Link> · {contact.role||'Role not recorded'} · {contact.phone||'Phone not recorded'}</p>):<p>No named contact is recorded. Keep company calling and research associated with this company; do not invent a contact.</p>}</details>
    <details><summary>Company notes and history</summary><RecordNotes recordType="organization" recordId={company.id}/></details></>:<p role="status">The requested company is unavailable in the current authorized response. No other company has been selected.</p>}
  </section>;
  if(!initialContact)return <>{companyContext}<div className="empty-state">{props.requestedContactId?'The requested contact is not available in the current authorized response. No other customer has been selected.':props.requestedOrganizationId?'This company has no available named contact. Its saved lines and company notes remain above.':'Add or assign a CRM contact before starting a call workflow.'}</div></>;
  const companyOnlyRequestKey=company&&context.contacts.length===0&&!props.requestedContactId?scope:undefined;
  return <>{companyContext}<div hidden={Boolean(props.requestedOrganizationId&&!company)}><CallsWorkspace {...props} contacts={context.contacts} initialContact={initialContact} requestedContactId={requestedContactId} selectedPhoneRoute={selectedPhoneRoute} retainedContact={props.contacts.find(contact=>contact.id===workingContactId)} companyOnlyRequestKey={companyOnlyRequestKey} onCompanyOnlyReady={finishedKey=>{if(currentScope.current!==finishedKey)return;setInitialContact(null);setWorkingContactId('')}} onWorkingContactChange={setWorkingContactId}/></div></>;
}

function CallsWorkspace({ contacts, organizations, opportunities, accountLinks, acquisitionAttributions, activities, dispositions, historyUnavailable, initialContact, onOpportunitiesChanged, requestedContactId, requestedOrganizationId, requestedPhoneRouteId, selectedPhoneRoute, retainedContact, companyOnlyRequestKey, onCompanyOnlyReady, onWorkingContactChange, requestKey, taskContext, returnTo, isVisible }: CallsProps & {initialContact:Contact}) {
  const identity=useCrmIdentity();
  const communications=useCommunications();
  const voice=useCrmVoice();
  const can=(capability:CrmCapability)=>identity.capabilities.includes(capability);
  type CommTab =
    | "email"
    | "text"
    | "notes"
    | "visibility"
    | "record"
    | "activity";
  const queueItem = (contact:Contact) => ({
    contact,
    name: contact.name,
    role: contact.role || "CRM contact",
    company: contact.company,
    priority: contact.score >= 90 ? "Hot" : contact.score >= 70 ? "Warm" : "Normal",
    score: contact.score,
    override: false,
    reason: `${contact.stage} · canonical CRM contact`,
    phone: contact.phone,
    email: contact.email,
    consentEmail: contact.consentEmail,
    consentSms: contact.consentSms,
    source: contact.source || "CRM contact record",
  });
  const queue = contacts.slice(0,25).map(queueItem);
  const topQueue = queue.slice(0, 3);
  const [workingContact,setWorkingContact]=useState(initialContact);
  const resolvedContact=resolveWorkingContact(workingContact,retainedContact?[...contacts,retainedContact]:contacts);
  const currentRecord=resolvedContact.contact;
  const currentAvailable=resolvedContact.available;
  useEffect(()=>{onWorkingContactChange?.(currentRecord.id)},[currentRecord.id,onWorkingContactChange]);
  useEffect(()=>{if(currentAvailable)setWorkingContact(currentRecord)},[currentAvailable,currentRecord]);
  const queueIndex=topQueue.findIndex(item=>item.contact.id===workingContact.id);
  const [queueOpen, setQueueOpen] = useState(false);
  useEffect(() => {
    if (!queueOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setQueueOpen(false);
      document.querySelector<HTMLButtonElement>('.queue-compact-trigger')?.focus();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [queueOpen]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<'guide'|'notes'|'next'>('guide');
  useEffect(() => {
    const views=document.querySelector<HTMLElement>('.calls-mobile-views');
    const offset=()=>views?.style.setProperty('--calls-header-height',`${document.querySelector('.topbar')?.getBoundingClientRect().height??72}px`);
    offset();
    if (window.innerWidth <= 760 || (window.innerWidth <= 980 && window.innerHeight <= 500)) views?.scrollIntoView({block:'start'});
    window.addEventListener('resize',offset);
    return ()=>window.removeEventListener('resize',offset);
  }, [mobilePanel]);
  useEffect(() => {
    if (!scheduleOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const drawer = document.querySelector<HTMLElement>('.calls-page .meeting-drawer');
    drawer?.querySelector<HTMLButtonElement>('[aria-label="Close scheduler and keep draft"]')?.focus();
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setScheduleOpen(false); return }
      if (event.key !== 'Tab') return;
      const controls = [...(drawer?.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),textarea:not(:disabled),select:not(:disabled),a[href]') ?? [])].filter(control => control.getClientRects().length > 0);
      const first=controls[0],last=controls[controls.length-1];
      if (event.shiftKey && document.activeElement===first) {event.preventDefault();last?.focus()}
      else if (!event.shiftKey && document.activeElement===last) {event.preventDefault();first?.focus()}
    };
    window.addEventListener('keydown',key);
    return () => {window.removeEventListener('keydown',key);if(previous?.getClientRects().length)previous.focus()};
  },[scheduleOpen]);
  const [meetingCreated, setMeetingCreated] = useState(false);
  const defaultMeetingDate = () =>
    new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
  const [meetingDate, setMeetingDate] = useState(defaultMeetingDate);
  const [meetingTime, setMeetingTime] = useState("14:00");
  const [meetingDuration, setMeetingDuration] = useState("30");
  const [meetingTimeZone, setMeetingTimeZone] = useState("America/Chicago");
  const [researchDue, setResearchDue] = useState("");
  const [meetingAttendees, setMeetingAttendees] = useState("");
  const [reminderDeliveryMode, setReminderDeliveryMode] =
    useState<ReminderDeliveryMode>("email-sms");
  const [includeFourHourReminder, setIncludeFourHourReminder] = useState(true);
  const [includeTenMinuteReminder, setIncludeTenMinuteReminder] =
    useState(true);
  const [syncToRepCalendar, setSyncToRepCalendar] = useState(true);
  const [blockRepTime, setBlockRepTime] = useState(true);
  const [meetingAgenda, setMeetingAgenda] = useState(
    "Review verified visibility issues, confirm decision criteria, and agree on the next step.",
  );
  const [nextStepType, setNextStepType] = useState<"phone" | "cliq">("phone");
  const [scheduledNextStep, setScheduledNextStep] = useState<null | {
    type: "phone" | "cliq";
    date: string;
    time: string;
    duration: string;
    attendees: string;
    agenda: string;
    reminders: ReminderPlanItem[];
    deliveryMode: ReminderDeliveryMode;
    syncStatus: "local-draft";
  }>(null);
  const [outcomeState,setOutcomeState]=useState<"idle"|"saving"|"success"|"error">("idle");
  const [outcomeError,setOutcomeError]=useState("");
  const [outcomeStatus,setOutcomeStatus]=useState("");
  const outcomeInFlight=useRef(false);
  const outcomeDispositionId=useRef<string>(crypto.randomUUID());
  const outcomeCheckpoint=useRef({contactId:'',id:'',scheduleId:'',scheduleSaved:false,researchSaved:false,dispositionSaved:false,scheduleReceipt:null as null|{eventId:string;startsAt:string;timeZone:string}});
  const callControlScrollRef=useRef<HTMLDivElement>(null);
  const [attendeesConfirmed, setAttendeesConfirmed] = useState(false);
  const [commitmentConfirmed, setCommitmentConfirmed] = useState(false);
  const [rescheduleConfirmed, setRescheduleConfirmed] = useState(false);
  const [followUpPermission, setFollowUpPermission] = useState<
    "not-asked" | "granted" | "declined"
  >("not-asked");
  const current = queueItem(currentRecord);
  const activityEntries=callsEvidence(currentRecord.id,activities,dispositions,voice.recentCalls,communications.snapshot?.activities||[]);
  const currentOrg = organizations.find(o => o.id === currentRecord.organizationId) || null;
  const currentAttribution = acquisitionAttributions.find(item => item.contactId === currentRecord.id || item.organizationId === currentOrg?.id);
  const currentDemoAudience = resolveDemoAudience({
    ownerAppAccountId: currentOrg?.ownerAppAccountId,
    agencyAccountId: currentOrg?.agencyAccountId,
    productLanes: accountLinks.filter(item => item.organizationId === currentOrg?.id && item.linkStatus === 'active').map(item => item.productLane),
    leadType: currentAttribution?.leadType,
  });
  const [commTab, setCommTab] = useState<CommTab>("notes");
  const [disposition, setDisposition] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [emailSubject, setEmailSubject] = useState("AvertSignal follow-up");
  const [emailBody, setEmailBody] = useState("");
  const [callDraftStatus, setCallDraftStatus] = useState("");
  const [callDraftError, setCallDraftError] = useState("");
  const guidanceAccess = can('sales.write') || can('prospect.work.handle');
  const conversation = useLiveConversation(currentRecord.id, identity.displayName, currentAvailable && guidanceAccess, currentRecord);
  const restOutcomeChosen=!conversation.state.archived&&(conversation.state.completionIntent?.disposition??disposition)==='Rest for 2 weeks';
  const liveRestSelection=useCompanyRestSelection(currentRecord.organizationId,currentRecord.id,currentAvailable&&restOutcomeChosen,'call:'+conversation.state.sessionId);
  const dispatchConversation=conversation.dispatch;
  const getConversationState=conversation.getState;
  const capturedGuidanceRevision=guidanceRevision(conversation.state);
  const [guidanceQuoteContact,setGuidanceQuoteContact]=useState('');
  const [observedGuidanceQuote,setObservedGuidanceQuote]=useState<CommercialQuoteObservation|null>(null);
  const guidanceContactId=currentRecord.id;
  const recordedQuoteContext=savedCommercialSelection(conversation.state);
  const observeGuidanceQuote=useCallback((observation:CommercialQuoteObservation)=>{
    setObservedGuidanceQuote(previous=>JSON.stringify(previous)===JSON.stringify(observation)?previous:observation);
  },[]);
  const selectGuidancePlan = useCallback((plan: Plan, lower = false) => {
    dispatchConversation({type:'configure',guidanceRevision:capturedGuidanceRevision,values:{recommendedPlan:plan,price:null,...(lower?{lowerPlan:plan}:{})}});
  },[dispatchConversation,capturedGuidanceRevision]);
  const syncGuidancePrice = useCallback((plan: Plan, price: number | null, context?:{quantity:number;discountBps:number;program:string;policyVersion:string|null}) => {
    try {
      const action=commercialConfirmationAction(getConversationState(),guidanceContactId,plan,price,context);
      if(!action)return;
      setGuidanceQuoteContact(price===null?'':guidanceContactId);
      dispatchConversation({...action,guidanceRevision:capturedGuidanceRevision});
    } catch {return}
  },[dispatchConversation,guidanceContactId,getConversationState,capturedGuidanceRevision]);
  const [commercialCue, setCommercialCue] = useState(false);
  useEffect(() => {
    callControlScrollRef.current?.scrollTo({ top: 0 });
  }, [currentRecord.id]);
  useEffect(() => {
    if (!commercialCue) return;
    const t = window.setTimeout(() => setCommercialCue(false), 6000);
    return () => window.clearTimeout(t);
  }, [commercialCue]);
  const callSessionId=conversation.state.sessionId;
  const callSessionLoaded=conversation.loaded;
  const callSessionState=conversation.status;
  const responseHistory=normalizeResponseEvidence([...conversation.legacyResponses,...conversationEvidence(conversation.state)],REACTIVE_PHASE_LABELS);
  const agreementMap=normalizeAgreementEvidence(conversation.legacyAgreements,REACTIVE_PHASE_LABELS);
  const activeModeLabel = "Cold outreach";
  const supplementalContactHistory = useMemo<SupplementalHistoryEntry[]>(() => [], []);
  const reminderPlan = useMemo(
    () =>
      buildReminderPlan({
        date: meetingDate,
        time: meetingTime,
        timeZone: meetingTimeZone,
        callType: nextStepType,
        deliveryMode: reminderDeliveryMode,
        includeFourHour: includeFourHourReminder,
        includeTenMinute: includeTenMinuteReminder,
        recipientName: current.name,
      }),
    [
      meetingDate,
      meetingTime,
      meetingTimeZone,
      nextStepType,
      reminderDeliveryMode,
      includeFourHourReminder,
      includeTenMinuteReminder,
      current.name,
    ],
  );
  const reminderCommitmentLanguage = useMemo(
    () =>
      buildReminderCommitmentLanguage(
        {
          date: meetingDate,
          time: meetingTime,
          timeZone: meetingTimeZone,
          callType: nextStepType,
          deliveryMode: reminderDeliveryMode,
          includeFourHour: includeFourHourReminder,
          includeTenMinute: includeTenMinuteReminder,
          recipientName: current.name,
        },
        reminderPlan,
      ),
    [
      meetingDate,
      meetingTime,
      meetingTimeZone,
      nextStepType,
      reminderDeliveryMode,
      includeFourHourReminder,
      includeTenMinuteReminder,
      current.name,
      reminderPlan,
    ],
  );

  function prepareCallDraft(
    channel: "email" | "sms",
    body: string,
    consent: boolean,
  ) {
    try {
      const result = prepareCommunication({ channel, body, consent });
      setCallDraftStatus(`${result.status}: ${result.summary}`);
      setCallDraftError("");
    } catch (value) {
      setCallDraftStatus("");
      setCallDraftError(
        value instanceof Error ? value.message : "unable_to_prepare",
      );
    }
  }

  useEffect(() => {
    setCallDraftStatus("");
    setCallDraftError("");
    setEmailBody("");
  }, [currentRecord.id]);

  const switchDraftFingerprint=JSON.stringify([currentRecord.id,disposition,nextAction,emailBody,emailSubject,meetingAgenda,meetingDate,meetingTime,meetingDuration,meetingTimeZone,meetingAttendees,attendeesConfirmed,commitmentConfirmed,rescheduleConfirmed,followUpPermission,reminderDeliveryMode,includeFourHourReminder,includeTenMinuteReminder,syncToRepCalendar,blockRepTime,nextStepType,scheduleOpen,scheduledNextStep]);
  const latestSwitchDraft=useRef(switchDraftFingerprint);latestSwitchDraft.current=switchDraftFingerprint;
  const switchVoiceState=useRef(voice.state);switchVoiceState.current=voice.state;
  const contactSwitchRequest=useRef({key:'',version:0});
  const contactSwitchKey=JSON.stringify([requestedOrganizationId,requestedContactId,requestKey]);
  if(contactSwitchRequest.current.key!==contactSwitchKey){contactSwitchRequest.current={key:contactSwitchKey,version:contactSwitchRequest.current.version+1}}
  async function prepareContactSwitch() {
    const capturedDraft=latestSwitchDraft.current;
    if(outcomeInFlight.current||['connecting','dialing','ringing','connected'].includes(voice.state)){setOutcomeError("Finish the active call or outcome save before selecting another customer.");return false}
    if((disposition||nextAction||emailBody||emailSubject!=="AvertSignal follow-up"||meetingAgenda!=="Review verified visibility issues, confirm decision criteria, and agree on the next step."||meetingDate!==defaultMeetingDate()||meetingTime!=="14:00"||meetingDuration!=="30"||meetingTimeZone!=="America/Chicago"||meetingAttendees&&meetingAttendees!==current.email||attendeesConfirmed||commitmentConfirmed||rescheduleConfirmed||followUpPermission!=="not-asked"||reminderDeliveryMode!=="email-sms"||!includeFourHourReminder||!includeTenMinuteReminder||!syncToRepCalendar||!blockRepTime||nextStepType!=="phone"||scheduleOpen||scheduledNextStep)&&!window.confirm("Switch customer and discard this customer's unsaved call drafts?"))return false;
    try{await conversation.flush()}catch{setOutcomeError("Save this customer's conversation before switching. Use Retry in Call Guidance.");return false}
    if(outcomeInFlight.current||['connecting','dialing','ringing','connected'].includes(switchVoiceState.current)){setOutcomeError("Finish the active call or outcome save before selecting another customer.");return false}
    if(latestSwitchDraft.current!==capturedDraft){setOutcomeError('Call drafts changed while switching. Review them and try the contact switch again.');return false}
    return true;
  }
  async function selectQueue(contactId: string) {
    const contact = contacts.find(entry=>entry.id===contactId);
    const item = contact ? queueItem(contact) : undefined;
    if(!item||contactId===currentRecord.id)return;
    const switchVersion=++contactSwitchRequest.current.version;
    if(!await prepareContactSwitch()||contactSwitchRequest.current.version!==switchVersion)return;
    outcomeCheckpoint.current={contactId:'',id:'',scheduleId:'',scheduleSaved:false,researchSaved:false,dispositionSaved:false,scheduleReceipt:null};
    setWorkingContact(item.contact);
    setEmailSubject("AvertSignal follow-up");setEmailBody("");setCallDraftStatus("");setCallDraftError("");
    setMeetingAgenda("Review verified visibility issues, confirm decision criteria, and agree on the next step.");
    setMeetingDate(defaultMeetingDate());setMeetingTime("14:00");setMeetingDuration("30");setMeetingTimeZone("America/Chicago");setResearchDue("");
    setDisposition("");
    setOutcomeState("idle");
    setOutcomeError("");
    setOutcomeStatus("");
    outcomeDispositionId.current=crypto.randomUUID();
    setNextAction("");
    setScheduleOpen(false);
    setMeetingCreated(false);
    setScheduledNextStep(null);
    setNextStepType("phone");
    setMeetingAttendees(item.email);
    setAttendeesConfirmed(false);
    setCommitmentConfirmed(false);
    setRescheduleConfirmed(false);
    setFollowUpPermission("not-asked");
    setReminderDeliveryMode("email-sms");
    setIncludeFourHourReminder(true);
    setIncludeTenMinuteReminder(true);
    setSyncToRepCalendar(true);
    setBlockRepTime(true);
    setCommercialCue(false);
  }

  const attemptedContext=useRef('');
  const companyOnlyContext=useRef(companyOnlyRequestKey);companyOnlyContext.current=companyOnlyRequestKey;
  const companyOnlyInFlight=useRef(false);
  useEffect(()=>()=>{companyOnlyContext.current=undefined},[]);
  async function switchToCompanyOnly(){
    const key=companyOnlyRequestKey;
    if(!key||companyOnlyInFlight.current)return;
    companyOnlyInFlight.current=true;
    try{if(await prepareContactSwitch()&&companyOnlyContext.current===key)onCompanyOnlyReady?.(key)}
    finally{companyOnlyInFlight.current=false}
  }
  useEffect(()=>{
    if(companyOnlyRequestKey)void switchToCompanyOnly();
  // Attempt once per explicit company navigation; a blocked switch has a retry action.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[companyOnlyRequestKey]);
  useEffect(()=>{
    if(!requestedContactId)return;
    const key=requestKey+':'+requestedContactId;
    if(attemptedContext.current===key)return;
    if(!contacts.some(contact=>contact.id===requestedContactId))return;
    attemptedContext.current=key;
    selectQueue(requestedContactId);
  // Each explicit navigation uses the existing draft and active-call guard once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[requestedContactId,requestKey,contacts]);

  useEffect(()=>{
    if(!callSessionLoaded)return;
    const saved=conversation.state.completionIntent?.disposition??dispositions.find(item=>item.id===callSessionId&&item.contactId===currentRecord.id)?.disposition;
    if(saved&&!conversation.state.archived)setDisposition(saved);
  },[callSessionLoaded,callSessionId,currentRecord.id,conversation.state.completionIntent,conversation.state.archived,dispositions]);

  function openNextStepScheduler(type: "phone" | "cliq") {
    if(conversation.getState().completionIntent||conversation.getState().scheduledContinuation?.eventId)return;
    setNextStepType(type);
    setMeetingCreated(false);
    if (new Date(`${meetingDate}T${meetingTime}`).getTime() <= Date.now()) {
      setMeetingDate(defaultMeetingDate());
    }
    setMeetingAttendees(meetingAttendees || current.email);
    setScheduleOpen(true);
  }

  function openGuidanceScheduler() {
    setMobilePanel('next');
    const state=conversation.getState();
    const resume=state.schedulingRequest?.resumeNodeId??state.currentMainNodeId;
    setMeetingAgenda(`Continue from ${resume}. ${state.confirmedNeed??state.schedulingRequest?.context??"Confirm the requested next step"}. ${state.authorityState==="shared approval"?"Include the decision makers.":""}`);
    setDisposition(state.authorityState==="shared approval"?"Connected — Needs Decision Maker":"Connected — Callback Requested");
    openNextStepScheduler("phone");
    const knownSlot=state.v9?.facts.callbackAt&&state.v9.facts.callbackZone?{startsAt:state.v9.facts.callbackAt,timeZone:state.v9.facts.callbackZone}:state.appointmentSlots[0];
    if(knownSlot){const local=slotToLocal(knownSlot,knownSlot.timeZone);if(local){setMeetingDate(local.slice(0,10));setMeetingTime(local.slice(11,16));setMeetingTimeZone(knownSlot.timeZone)}}
    callControlScrollRef.current?.scrollTo({top:0});
  }

  function createScheduledNextStep() {
    const attendees = (meetingAttendees || current.email).trim();
    const created = {
      type: nextStepType,
      date: meetingDate,
      time: meetingTime,
      duration: meetingDuration,
      attendees,
      agenda: meetingAgenda.trim(),
      reminders: reminderPlan,
      deliveryMode: reminderDeliveryMode,
      syncStatus: "local-draft" as const,
    };
    setScheduledNextStep(created);
    setMeetingCreated(true);
    setNextAction(
      `Draft ${nextStepType === "cliq" ? "Cliq video decision call" : "phone follow-up"} for ${meetingDate} at ${meetingTime} for ${meetingDuration} minutes${attendees ? ` with ${attendees}` : ""}. Goal: ${meetingAgenda.trim()}. Proposed reminders: ${reminderDeliveryMode === "email-sms" ? "email + SMS" : "email only"}. No event or delivery has been created.`,
    );
  }

  async function completeCallOutcome() {
    if(outcomeInFlight.current||!currentAvailable||!callSessionLoaded)return;
    const initial=conversation.getState();
    const chosen=initial.completionIntent?.disposition??disposition;
    if(!chosen){setOutcomeState("error");setOutcomeError("Choose a call outcome before saving.");return}
    if(scheduleRequired&&!scheduledNextStep&&!initial.completionIntent&&!initial.scheduledContinuation?.eventId){setOutcomeState("error");setOutcomeError("Schedule the required next step before saving this outcome.");return}
    outcomeInFlight.current=true;
    const dispositionId=initial.sessionId;
    outcomeDispositionId.current=dispositionId;
    if(outcomeCheckpoint.current.id!==dispositionId||outcomeCheckpoint.current.contactId!==currentRecord.id)outcomeCheckpoint.current={contactId:currentRecord.id,id:dispositionId,scheduleId:dispositionId,scheduleSaved:false,researchSaved:false,dispositionSaved:false,scheduleReceipt:null};
    const checkpoint=outcomeCheckpoint.current;
    setOutcomeState("saving");setOutcomeError("");setOutcomeStatus("Saving this call outcome and record notes.");
    try{
      await conversation.flush();
      let intent=conversation.getState().completionIntent;
      if(chosen==='Rest for 2 weeks'){
        if(!intent){
          if(!liveRestSelection.ready)throw Error('Review and confirm the company timezone before saving Rest.');
          const completed=conversation.getState();
          intent={disposition:chosen,timeZone:liveRestSelection.timezone,schedule:null,receipt:null,research:null,outcome:{dispositionId,contactId:currentRecord.id,organizationId:currentRecord.organizationId||null,disposition:'Rest for 2 weeks',notes:summarizeConversation({...completed,finalDisposition:chosen}),nextAction:deriveNextAction('Rest for 2 weeks'),scheduleEventId:null}};
          conversation.dispatch({type:'prepare-completion',intent});await conversation.flush();
          intent=conversation.getState().completionIntent;
        }
        if(!intent?.outcome)throw Error('completion_intent_missing');
        if(!checkpoint.dispositionSaved&&liveRestSelection.ready&&intent.timeZone!==liveRestSelection.timezone){
          conversation.dispatch({type:'prepare-completion',intent:{...intent,timeZone:liveRestSelection.timezone}});await conversation.flush();
          intent=conversation.getState().completionIntent;
          if(!intent?.outcome)throw Error('completion_intent_missing');
        }
        if(!checkpoint.dispositionSaved)await liveRestSelection.save({dispositionId:intent.outcome.dispositionId,notes:intent.outcome.notes,nextAction:intent.outcome.nextAction});
        checkpoint.dispositionSaved=true;
        await conversation.archive(chosen);
        liveRestSelection.complete();
        setOutcomeState('success');setDisposition('');setOutcomeStatus(`Two-week Rest and prior notes saved for ${current.name}. The follow-up will return for manual review.`);
        return;
      }
      if(!intent){
        let schedule:null|Parameters<typeof manageSchedule>[0]=null;
        const receipt=initial.scheduledContinuation?.eventId?{eventId:initial.scheduledContinuation.eventId,startsAt:initial.scheduledContinuation.startsAt,timeZone:initial.scheduledContinuation.timeZone}:null;
        if(scheduleRequired&&!receipt){
          if(!scheduledNextStep)throw new Error("schedule_required");
          const slot=slotFromLocal(`${scheduledNextStep.date}T${scheduledNextStep.time}`,meetingTimeZone);
          const startsAt=new Date(slot?.startsAt??'');
          if(Number.isNaN(startsAt.getTime())||startsAt<=new Date())throw new Error("future_schedule_required");
          schedule={action:"CREATE",eventId:dispositionId,contactId:currentRecord.id,organizationId:currentRecord.organizationId||null,assignedUserId:await getCurrentUserId(),title:`${scheduledNextStep.type==="cliq"?"Video follow-up":"Callback"} — ${current.name}`,eventType:scheduledNextStep.type==="cliq"?"MEETING":"FOLLOW_UP",startsAt:startsAt.toISOString(),endsAt:new Date(startsAt.getTime()+Number(scheduledNextStep.duration)*60000).toISOString(),notes:scheduledNextStep.agenda,reminderMinutes:scheduledNextStep.reminders.some(item=>item.status!=="suppressed")?10:null};
        }
        const researchSlot=initial.callOutcome==="research/email follow-up"?slotFromLocal(researchDue,meetingTimeZone):null;
        if(initial.callOutcome==="research/email follow-up"&&(!researchDue||!researchSlot||!Number.isFinite(Date.parse(researchSlot.startsAt))||Date.parse(researchSlot.startsAt)<=Date.now()))throw new Error("future_research_date_required");
        const research=initial.callOutcome==="research/email follow-up"?{action:"CREATE" as const,taskId:dispositionId,title:"Email research follow-up - "+current.name,description:"Research "+(initial.knownSystemOrVendor??"the prospect's system")+" and prepare an email with what it covers. No callback.\n"+summarizeConversation(initial),priority:"NORMAL" as const,ownerUserId:currentRecord.ownerUserId||null,dueAt:researchSlot!.startsAt,organizationId:currentRecord.organizationId||null,contactId:currentRecord.id,opportunityId:null,scheduleEventId:null}:null;
        intent={disposition:chosen,timeZone:receipt?.timeZone??meetingTimeZone,schedule,receipt,research,outcome:null};
        conversation.dispatch({type:"prepare-completion",intent});await conversation.flush();
        intent=conversation.getState().completionIntent;
        if(!intent)throw new Error("completion_intent_missing");
      }
      const scheduleEventId=intent.receipt?.eventId??intent.schedule?.eventId??null;
      if(scheduleEventId){
        const existing=(await getSchedules()).find(item=>item.id===scheduleEventId);
        if(existing&&(existing.contactId!==currentRecord.id||existing.status!=="SCHEDULED"||intent.schedule&&(Date.parse(existing.startsAt)!==Date.parse(intent.schedule.startsAt)||Date.parse(existing.endsAt)!==Date.parse(intent.schedule.endsAt))))throw new Error("saved_schedule_changed");
        if(!existing&&intent.receipt)throw new Error("saved_schedule_unavailable");
        const saved=existing??await manageSchedule(intent.schedule!);
        if(saved.id!==scheduleEventId||saved.contactId!==currentRecord.id||saved.status!=="SCHEDULED")throw new Error("schedule_confirmation_missing");
        checkpoint.scheduleReceipt={eventId:saved.id,startsAt:saved.startsAt,timeZone:intent.timeZone};checkpoint.scheduleSaved=true;checkpoint.scheduleId=saved.id;
        if(!intent.receipt){intent={...intent,receipt:checkpoint.scheduleReceipt};conversation.dispatch({type:"prepare-completion",intent});await conversation.flush();}
        if(conversation.getState().schedulingRequest&&!conversation.getState().scheduledContinuation){conversation.dispatch({type:"schedule-confirmed",...checkpoint.scheduleReceipt});await conversation.flush();}
      }
      if(intent.research&&!checkpoint.researchSaved){
        const existing=(await getTasks()).find(item=>item.id===intent!.research!.taskId);
        if(!intent.research.dueAt)throw new Error("future_research_date_required");
        if(existing&&(existing.contactId!==currentRecord.id||(existing.organizationId||null)!==intent.research.organizationId||existing.title!==intent.research.title||(existing.description||'')!==intent.research.description||existing.priority!==intent.research.priority||Date.parse(existing.dueAt||'')!==Date.parse(intent.research.dueAt)||(existing.ownerUserId||null)!==intent.research.ownerUserId||existing.status!=='OPEN'))throw new Error("saved_research_changed");
        if(!existing)await manageTask(intent.research);
        checkpoint.researchSaved=true;
      }
      const completedState=conversation.getState();
      if(checkpoint.scheduleSaved){setMeetingCreated(false);setNextAction(`CRM appointment saved. Attend the saved appointment${completedState.scheduledContinuation?` and resume ${completedState.scheduledContinuation.resumeNodeId}`:''}. Provider delivery is tracked separately.`);}
      if(!intent.outcome){
        // Recover a pre-intent partial completion from canonical history when available.
        const existing=(await getDispositions()).find(item=>item.id===dispositionId&&item.contactId===currentRecord.id);
        const outcome=existing?{dispositionId,contactId:existing.contactId,organizationId:existing.organizationId||null,disposition:existing.disposition,notes:existing.notes,nextAction:existing.nextAction,scheduleEventId:existing.scheduleEventId||null}:{dispositionId,contactId:currentRecord.id,organizationId:currentRecord.organizationId||null,disposition:chosen as CallDisposition['disposition'],notes:summarizeConversation({...completedState,finalDisposition:chosen}),nextAction:scheduleEventId?`Attend the saved CRM appointment${completedState.scheduledContinuation?` and resume ${completedState.scheduledContinuation.resumeNodeId}`:''}.`:completedState.callOutcome==="research/email follow-up"?"Research the named system and prepare an email follow-up; no callback.":completedState.callOutcome==="paid"?"Proceed to self-onboarding; payment completion reported by rep.":deriveNextAction(chosen as CallDisposition['disposition']),scheduleEventId};
        intent={...intent,outcome};conversation.dispatch({type:"prepare-completion",intent});await conversation.flush();
        if(!conversation.getState().completionIntent?.outcome)throw new Error("completion_intent_missing");
      }
      if(!checkpoint.dispositionSaved)await recordCallDisposition(intent.outcome!);
      checkpoint.dispositionSaved=true;
      await conversation.archive(chosen);
      setOutcomeState("success");setDisposition("");setOutcomeStatus(`Call outcome and notes saved for ${current.name}. Select Next when ready.`);
    }catch(error){
      setOutcomeState("error");setOutcomeStatus("");
      const detail=typeof error==='object'&&error!==null&&'message'in error&&typeof error.message==='string'?error.message:'';
      setOutcomeError(chosen==='Rest for 2 weeks'&&!checkpoint.dispositionSaved?detail||'Rest outcome is unverified. Retry the saved request.':detail==="future_research_date_required"?"Choose a future date for the research task before saving. No callback or email will start automatically.":detail==="future_schedule_required"||detail==="schedule_required"?"Choose a valid future time before saving this callback.":detail==="saved_research_changed"?"The saved research task has changed. Review that task before retrying; no duplicate or reassignment was made.":detail.startsWith("saved_schedule_")?"The saved appointment could not be verified or has changed. Review it in the CRM scheduler before retrying; no replacement was created.":checkpoint.dispositionSaved?"Call outcome and notes are saved. Closing guidance failed; retry will not create another outcome.":checkpoint.scheduleSaved?"The CRM appointment is saved. Call outcome and notes are still pending; retry will use the same appointment.":"We couldn't save this call outcome. Your completion request is retained, so you can try again.");
    }finally{outcomeInFlight.current=false;}
  }

  const dispositionActions=Object.fromEntries(canonicalDispositions.map(value=>[value,dispositionPolicy[value].nextAction]));
  const completionPending=Boolean(conversation.state.completionIntent&&!conversation.state.archived);
  const guidanceDisabledReason=!currentAvailable?'This contact is unavailable in the current authorized response. Refresh the contact before continuing.':!guidanceAccess?'Your role cannot save live sales guidance. Use Roleplay Training for synthetic practice.':conversation.status==='error'?'Guidance is paused because the call session could not be loaded or saved. Use Retry save / load below.':!callSessionLoaded?'Loading the saved call session. Guidance will unlock when it finishes.':outcomeState==='saving'?'Saving the call outcome. Wait for confirmation before continuing.':completionPending?'A call outcome is pending completion. Review Next step / outcome and retry Save outcome to finish.':'';
  const savedReceipt=conversation.state.completionIntent?.receipt??conversation.state.scheduledContinuation;
  const crmScheduleSaved=Boolean(savedReceipt?.eventId||outcomeCheckpoint.current.contactId===currentRecord.id&&outcomeCheckpoint.current.id===callSessionId&&outcomeCheckpoint.current.scheduleSaved);
  const scheduleRequired=Boolean(disposition&&dispositionPolicy[disposition as CallDisposition['disposition']]?.schedule!=='NONE'&&!['paid','research/email follow-up','gatekeeper routing','routing unresolved'].includes(conversation.state.callOutcome??''));
  const scheduleHint=crmScheduleSaved?'Attend the saved CRM appointment; keep the continuation context.':disposition?deriveNextAction(disposition as CallDisposition['disposition']):'Choose the truthful call outcome first.';

  const resolutionLabels: Record<
    ResolutionState|"unknown",
    { title: string; description: string }
  > = {
    unknown: {title:"Unknown resolution",description:"The saved evidence does not establish a supported outcome."},
    resolved: {
      title: "Resolved",
      description: "Record the exact fact, agreement, or next step.",
    },
    conditional: {
      title: "Conditionally agreed",
      description:
        "Require the condition, owner, trigger, measurement, and deadline.",
    },
    parked: {
      title: "Parked",
      description: "Record what is missing, who owns it, and when it returns.",
    },
    unresolved: {
      title: "Unresolved disagreement",
      description:
        "Record both positions and the current boundary without ending the call automatically.",
    },
  };

  return (
    <div className="page calls-page">
      {returnTo&&<Link className="work-return-link" to={returnTo}>Return to work queue</Link>}
      {companyOnlyRequestKey&&<div className="runtime-banner issue" role="status"><span>{current.name}'s workspace is retained until its call and drafts are resolved.</span><button type="button" onClick={()=>void switchToCompanyOnly()}>Continue to company calling</button></div>}
      {requestedContactId&&requestedContactId!==currentRecord.id&&<div className="runtime-banner issue" role="status">Requested customer has not been selected. Your existing customer and drafts are retained.</div>}
      {taskContext?.contactId===currentRecord.id&&<section className="work-task-context" aria-label="Task conversation context"><strong>{taskContext.title}</strong><p>Reason: {taskContext.description||taskContext.title}</p><span>{current.company} · Task {taskContext.id}</span>{taskContext.opportunityId&&<span> · Related opportunity {taskContext.opportunityId}</span>}</section>}
      {!currentAvailable&&<div className="runtime-banner issue" role="status">{current.name} is absent from the current bounded response. Drafts and customer identity are retained; calling and saving are unavailable until the record is verified again.</div>}
      {callSessionState==="error"&&<div className="runtime-banner issue" role="alert">Call progress could not be saved. Use Retry in Call Guidance before continuing.</div>}
      <div className="call-toolbar">
        <div className="queue-menu-shell">
          <button
            className={`queue-compact-trigger ${queueOpen ? "open" : ""}`}
            aria-expanded={queueOpen}
            aria-controls="calls-queue-overlay"
            onClick={() => setQueueOpen((v) => !v)}
          >
            <PhoneCall size={15} />
            <span>Queue</span>
            <strong>
              {queueIndex<0?"Selected contact":queueIndex + 1}/{topQueue.length}
            </strong>
            <ChevronRight size={13} />
          </button>
          {queueOpen && (
            <div className="queue-overlay" id="calls-queue-overlay" role="region" aria-label="Call queue">
              <div className="queue-overlay-head">
                <div>
                  <span>PRIORITY QUEUE</span>
                  <strong>Top three calls</strong>
                </div>
                <button aria-label="Close call queue" onClick={() => setQueueOpen(false)}>
                  <X size={15} />
                </button>
              </div>
              {topQueue.map(q => (
                <button
                  key={q.contact.id}
                  className={`queue-overlay-item ${q.contact.id === currentRecord.id ? "active" : ""}`}
                  onClick={() => {
                    selectQueue(q.contact.id);
                    setQueueOpen(false);
                  }}
                >
                  <span
                    className={`queue-priority ${q.priority.toLowerCase()}`}
                  >
                    {q.priority}
                  </span>
                  <div>
                    <strong>{q.name}</strong>
                    <small>
                      {q.company} · {q.reason}
                    </small>
                  </div>
                  <b>{q.score}</b>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="toolbar-contact-context">
          <strong>{current.name}</strong>
          <span>
            {current.company} · {current.role}
          </span>
          <small>{current.reason}</small>
        </div>

        <div className="toolbar-call-state">
          <span className={`queue-priority ${current.priority.toLowerCase()}`}>
            {current.priority}
          </span>
          <span className="toolbar-score">Score {current.score}</span>
          <DemoLaunchAction compact audience={currentDemoAudience} organizationId={currentOrg?.id} contactId={currentRecord.id} opportunityId={currentAttribution?.opportunityId} />
          <button
            className="toolbar-next-call"
            disabled={!topQueue.length}
            onClick={() => selectQueue(topQueue[(queueIndex + 1) % topQueue.length].contact.id)}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <nav className="calls-mobile-views" aria-label="Call workspace views">
        <small>{current.name} · {current.company}{taskContext ? ' · Task conversation' : ''}</small>
        <button aria-pressed={mobilePanel==='guide'} onClick={()=>setMobilePanel('guide')}>Guide</button>
        <button aria-pressed={mobilePanel==='notes'} onClick={()=>{setCommTab('notes');setMobilePanel('notes')}}>Notes</button>
        <button aria-pressed={mobilePanel==='next'} onClick={()=>setMobilePanel('next')}>Next step / outcome</button>
      </nav>
      <section className="calls-cockpit detailed-cockpit" data-mobile-panel={mobilePanel}>
        <div className="panel cockpit-panel script-workbench reactive-call-center">
          <div className="cockpit-header">
            <div>
              <span>CALL GUIDANCE</span>
              <strong>{current.name}</strong>
            </div>
            <div className="cockpit-header-tools">
              <div className="phase-count">{activeModeLabel}</div>
            </div>
          </div>
          <div className="script-scroll-region">
            <CallsWorkspaceReactiveBridge
              key={currentRecord.id}
              undoAvailable={conversation.undoAvailable}
              state={conversation.state} onSchedule={openGuidanceScheduler}
              quoteReady={conversation.state.guidanceVersion===9?commercialGuidanceReady(conversation.state,currentRecord.id,observedGuidanceQuote):guidanceQuoteContact===currentRecord.id}
              disabled={!currentAvailable||!callSessionLoaded||!guidanceAccess||outcomeState==='saving'||completionPending||conversation.status==='error'}
              disabledReason={guidanceDisabledReason}
              status={conversation.status}
              error={conversation.error}
              onAction={conversation.dispatch}
              onRetry={conversation.retry}
              onPlan={selectGuidancePlan}
              onCheckout={()=>{const latest=conversation.getState();if(latest.guidanceVersion===9){if(!commercialGuidanceReady(latest,currentRecord.id,observedGuidanceQuote))return;const source=latest.v9?.facts.quoteSource;if(source?.startsWith('https:')){const url=new URL(source);if(['avertsignal.com','www.avertsignal.com'].includes(url.hostname)&&!url.username&&!url.password){window.open(url.href,'_blank','noopener,noreferrer');return}}}setMobilePanel('next');setCommercialCue(true);callControlScrollRef.current?.querySelector('.commercial-close')?.scrollIntoView({block:'nearest'})}}
            />
          </div>
        </div>

        <div className="panel cockpit-panel call-control-panel">
          <div
            ref={callControlScrollRef}
            className="call-control-scroll-region"
            aria-label="Call controls and Close / Payment workspace"
          >
          <div className="call-context-badge">{activeModeLabel}</div>
          <div className="avatar huge call-avatar">
            {current.name
              .split(" ")
              .map((n) => n[0])
              .join("")}
          </div>
          <h2>{current.name}</h2>
          <p>{current.role}</p>
          <small>{current.company}</small>
          <div className="contact-quick-facts">
            <span>{current.phone}</span>
            <span>Source: {current.source}</span>
            <span>Last recorded outcome: {dispositions.filter(item=>item.contactId===currentRecord.id).sort((a,b)=>Date.parse(b.occurredAt)-Date.parse(a.occurredAt))[0]?.disposition||'No outcome in the current history projection'}</span>
            <span>
              Email {current.consentEmail ? "allowed" : "blocked"} · SMS{" "}
              {current.consentSms ? "allowed" : "blocked"}
            </span>
          </div>
          {(selectedPhoneRoute||requestedPhoneRouteId)&&<p role="status">{selectedPhoneRoute?`Selected saved line: ${selectedPhoneRoute.phone}. Use Call selected number in the company lines panel.`:'Loading the selected saved line. Contact-primary dialing is paused.'}</p>}
          <VoiceCallControls contactId={currentRecord.id} unavailable={!currentAvailable||Boolean(selectedPhoneRoute||requestedPhoneRouteId)||Boolean(requestedOrganizationId&&requestedOrganizationId!==currentRecord.organizationId)} prospectTaskId={taskContext?.prospectSequenceId?taskContext.id:undefined} phone={currentRecord.phone}/>
          <div className="call-controls">
            <button onClick={() => {setCommTab("notes");setMobilePanel('notes')}}>
              <MessageSquareText size={18} />
              <span>Note</span>
            </button>
            <button onClick={() => {setCommTab("record");setMobilePanel('notes')}}>
              <ShieldCheck size={18} />
              <span>Consent</span>
            </button>
          </div>
          <div className="live-call-stats">
            <div>
              <span>Recorded responses</span>
              <strong>{responseHistory.length}</strong>
            </div>
            <div>
              <span>Open items</span>
              <strong>
                {agreementMap.filter((x) => x.state !== "resolved" && x.state !== "unknown").length}
              </strong>
            </div>
            <div>
              <span>Agreements</span>
              <strong>
                {agreementMap.filter((x) => x.state === "resolved").length}
              </strong>
            </div>
          </div>
          <form className="disposition-box" onSubmit={(event)=>{event.preventDefault();void completeCallOutcome()}}>
            <label>
              Disposition
              <select
                value={disposition}
                disabled={outcomeState==="saving"||completionPending||liveRestSelection.retained}
                onChange={(e) => {setDisposition(e.target.value);setOutcomeState("idle");setOutcomeError("");setOutcomeStatus("");outcomeDispositionId.current=crypto.randomUUID()}}
              >
                <option value="">Select outcome</option>
                {Object.keys(dispositionActions).map((d) => (
                  <option key={d}>{d}</option>
                ))}
              </select>
            </label>
            {restOutcomeChosen&&<CompanyRestTimezone selection={liveRestSelection}/>}
            {disposition && (
              <div className="automation-preview">
                <strong>Automatic action</strong>
                <span>{crmScheduleSaved ? "Save the call outcome and Notes; the CRM appointment is already saved." : dispositionActions[disposition]}</span>
                {disposition === "No Answer — Left Voicemail" && (
                  <div className="followup-options">
                    <label>
                      <input type="checkbox" /> Draft email
                    </label>
                    <label>
                      <input type="checkbox" /> Draft SMS
                    </label>
                  </div>
                )}
              </div>
            )}

            <div
              className={`next-step-planner ${scheduleRequired ? "required" : ""}`}
            >
              <div className="next-step-planner-head">
                <div>
                  <span>NEXT STEP</span>
                  <strong>
                    {crmScheduleSaved ? "CRM appointment saved" : scheduledNextStep
                      ? "Schedule draft and commitment captured"
                      : "Prepare a schedule draft before ending the call"}
                  </strong>
                  <small>{scheduleHint}</small>
                </div>
                {scheduleRequired && <b>{crmScheduleSaved ? "Saved" : "Required"}</b>}
              </div>
              {crmScheduleSaved&&!scheduledNextStep ? <div className="scheduled-next-step"><div><strong>Saved CRM appointment</strong><span>{savedReceipt?.startsAt} · {savedReceipt?.timeZone}</span><small>Complete the saved outcome; use the CRM scheduler for appointment changes.</small></div></div> : scheduledNextStep ? (
                <div className="scheduled-next-step">
                  <div className={`scheduled-type ${scheduledNextStep.type}`}>
                    {scheduledNextStep.type === "cliq" ? (
                      <Video size={15} />
                    ) : (
                      <PhoneCall size={15} />
                    )}
                  </div>
                  <div>
                    <strong>
                      {scheduledNextStep.type === "cliq"
                        ? "Cliq video decision call"
                        : "Phone follow-up"}
                    </strong>
                    <span>
                      {scheduledNextStep.date} · {scheduledNextStep.time} ·{" "}
                      {scheduledNextStep.duration} min
                    </span>
                    <small>{scheduledNextStep.attendees || current.name}</small>
                  </div>
                  <button
                    type="button"
                    disabled={crmScheduleSaved||completionPending}
                    onClick={() =>
                      openNextStepScheduler(scheduledNextStep.type)
                    }
                  >
                    {crmScheduleSaved ? "Saved" : "Edit"}
                  </button>
                </div>
              ) : (
                <div className="next-step-buttons">
                  <button type="button" disabled={completionPending} onClick={() => openNextStepScheduler("phone")}>
                    <PhoneCall size={14} /> Schedule call
                  </button>
                  <button type="button" onClick={() => openNextStepScheduler("cliq")}>
                    <Video size={14} /> Schedule video
                  </button>
                </div>
              )}
            </div>

            {conversation.state.callOutcome==='research/email follow-up'&&<fieldset className="crm-field"><legend>Research follow-up task</legend><label>Due date and time<input type="datetime-local" aria-label="Research follow-up due" value={researchDue} disabled={Boolean(conversation.state.completionIntent?.research?.dueAt)} onChange={event=>setResearchDue(event.target.value)}/></label><label>Task timezone<input aria-label="Research follow-up timezone" value={meetingTimeZone} disabled={completionPending} onChange={event=>setMeetingTimeZone(event.target.value)}/></label><small>{conversation.state.completionIntent?.research?.dueAt?`Saved task date: ${conversation.state.completionIntent.research.dueAt}`:'Choose when this manual task should return. This does not schedule a prospect appointment or send a message.'}</small></fieldset>}
            <div className="derived-next-action"><span>DERIVED NEXT ACTION</span><strong>{crmScheduleSaved?'Attend the saved CRM appointment':disposition?deriveNextAction(disposition as CallDisposition['disposition']):'Select a disposition'}</strong>{nextAction&&<small>{nextAction}</small>}</div>
            <button
              type="submit"
              className="complete-next-btn"
              disabled={!currentAvailable||!callSessionLoaded||!disposition||(scheduleRequired && !scheduledNextStep&&!crmScheduleSaved&&!completionPending)||outcomeState==="saving"||restOutcomeChosen&&!liveRestSelection.ready&&!outcomeCheckpoint.current.dispositionSaved}
            >
              {outcomeState==="saving"
                ? "Saving outcome…"
                : scheduleRequired && !scheduledNextStep
                ? "Schedule the required next step"
                : "Save outcome"}
            </button>
            {outcomeState==="success"&&<div className="runtime-banner success" role="status">{outcomeStatus}</div>}
            {outcomeState==="error"&&<div className="runtime-banner issue" role="alert">{outcomeError||"Call outcome could not be saved."}</div>}
          </form>
          <div className={commercialCue ? "commercial-close-cue" : ""}>
            {can('sales.write')&&can('commercial.discount.request')&&currentRecord&&conversation.state.v9?.facts.check!=='website'&&(
              <CommercialClosePanel
                contact={currentRecord}
                organizations={organizations}
                opportunities={opportunities}
                callSessionId={callSessionId||null}
                onOpportunitiesChanged={onOpportunitiesChanged}
                prescribedPlan={conversation.state.recommendedPlan}
                prescribedQuantity={conversation.state.v9?.facts.quantity}
                onQuantitySelected={quantity=>dispatchConversation({type:'configure',guidanceRevision:capturedGuidanceRevision,values:{price:null,v9Facts:{quantity}}})}
                onPlanSelected={selectGuidancePlan}
                prescribedProgram={recordedQuoteContext?.program}
                prescribedDiscountBps={recordedQuoteContext?.discountBps}
                onQuoteObserved={observeGuidanceQuote}
                onTermsChanged={syncGuidancePrice}
                onExternalBoundary={async(reason,status)=>{
                  const latest=conversation.getState();
                  if(latest.contactId!==guidanceContactId)throw new Error('guidance_contact_changed');
                  if(latest.guidanceVersion!==9)return;
                  if(status==='pending'&&reason==='Actual attributed checkout issue'&&!commercialIssueReady(latest,guidanceContactId,observedGuidanceQuote))throw new Error('guidance_offer_not_current');
                  if(status==='pending'&&latest.guidanceUndo?.externalPending)throw new Error('guidance_external_action_unresolved');
                  const revision=status==='pending'?capturedGuidanceRevision:guidanceRevision(latest);
                  if(!conversation.dispatch({type:'external-boundary',reason,status,guidanceRevision:revision}))throw new Error('guidance_boundary_not_accepted');
                  await conversation.flush();
                  if(status==='pending'&&reason==='Actual attributed checkout issue'){
                    const saved=conversation.getState();
                    if(saved.contactId!==guidanceContactId)throw new Error('guidance_contact_changed');
                    if(!commercialIssueReady(saved,guidanceContactId,observedGuidanceQuote)){
                      if(conversation.dispatch({type:'external-boundary',reason:'Checkout cancelled before issuance because accepted terms changed',status:'completed',guidanceRevision:guidanceRevision(saved)}))await conversation.flush();
                      throw new Error('guidance_offer_not_current');
                    }
                  }
                }}
              />
            )}
          </div>
          </div>
        </div>

        <div className="panel cockpit-panel contact-comms-panel">
          <div className="contact-comms-header">
            <div>
              <span>CONTACT WORKSPACE</span>
              <strong>{current.name}</strong>
            </div>
            <small>Work without leaving the call</small>
          </div>
          <div className="contact-tabs cockpit-tabs">
            {(
              [
                "email",
                "text",
                "notes",
                "visibility",
                "record",
                "activity",
              ] as CommTab[]
            ).map((t) => (
              <button
                key={t}
                className={commTab === t ? "active" : ""}
                onClick={() => setCommTab(t)}
              >
                {t}
                {t === "record" && agreementMap.length > 0 ? (
                  <span className="tab-count">{agreementMap.length}</span>
                ) : null}
                {t === "text" && communications.unreadSmsByContact[currentRecord.id] > 0 ? (
                  <span className="tab-unread-badge" aria-label={`${communications.unreadSmsByContact[currentRecord.id]} unread text messages`}>
                    {communications.unreadSmsByContact[currentRecord.id] > 99 ? "99+" : communications.unreadSmsByContact[currentRecord.id]}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
          <div className="comm-tab-shell">
            <CallsPanelBoundary panel="email" active={commTab === "email"} resetKey={identity.userId+':'+identity.workspaceId+':'+currentRecord.id}>
              {commTab === "email" && <CallsPanelContent render={() => (
              <div className="call-email-tab">
                <div className="runtime-banner">
                  Canonical email history is not available in this workspace yet.
                  No demonstration messages are shown in authenticated CRM state.
                </div>
                <div className="compact-composer branded-email-composer">
                  <input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Subject"
                  />
                  <textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder={`Email ${current.name} during the call...`}
                  />
                  <div className="email-signature-preview">
                    <div className="email-signature-logo">
                      <AvertSignalMark size={28} />
                    </div>
                    <div>
                      <strong>{identity.displayName} · AvertSignal</strong>
                      <span>
                        Business signal monitoring, accountability, and verified
                        resolution
                      </span>
                      <small>
                        Approved AvertSignal logo included in draft preview
                      </small>
                    </div>
                  </div>
                  <div className="bounded-composer-status">
                    <ShieldCheck size={13} />
                    <span>
                      {current.consentEmail
                        ? "Email consent recorded. Preparing does not send."
                        : "Email blocked: no recorded consent."}
                    </span>
                  </div>
                  {callDraftError && (
                    <div className="composer-error">
                      {callDraftError.replaceAll("_", " ")}
                    </div>
                  )}
                  {callDraftStatus && (
                    <div className="composer-success">{callDraftStatus}</div>
                  )}
                  <div className="composer-actions">
                    <button className="tool-button" onClick={()=>setCommTab("notes")} title="Attach a link or file in chronological record history">
                      <Paperclip size={14} /> Add link/file
                    </button>
                    <button
                      className="primary-btn"
                      onClick={() =>
                        prepareCallDraft(
                          "email",
                          `${emailSubject.trim()}\n${emailBody.trim()}`,
                          current.consentEmail,
                        )
                      }
                    >
                      <Send size={14} /> Prepare email draft
                    </button>
                  </div>
                </div>
              </div>
              )} />}
            </CallsPanelBoundary>
            <CallsPanelBoundary panel="text" active={commTab === "text"} resetKey={identity.userId+':'+identity.workspaceId+':'+currentRecord.id}>
              {commTab === "text" && <CallsPanelContent render={() => (
              <div className="call-text-tab">
                <CanonicalSmsConversation
                  contactId={currentRecord.id}
                  contactName={currentRecord.name}
                  consentSms={currentRecord.consentSms}
                  presentation="compact"
                  isVisible={isVisible && commTab === "text"}
                  unreadCount={communications.unreadSmsByContact[currentRecord.id] || 0}
                />
              </div>
              )} />}
            </CallsPanelBoundary>
            <CallsPanelBoundary panel="notes" active={commTab === "notes"} resetKey={identity.userId+':'+identity.workspaceId+':'+currentRecord.id}>
              {commTab === "notes" && <CallsPanelContent render={() => (
              <div className="call-notes-tab">
                <div className="current-call-context" aria-label="Current call context">
                  <strong>Call context</strong>
                  <span>{activeModeLabel} in progress</span>
                </div>
                {currentRecord?<RecordNotes recordType="contact" recordId={currentRecord.id} supplementalHistory={supplementalContactHistory}/>:<div className="runtime-banner issue" role="alert">The queue contact is not linked to a canonical CRM contact record.</div>}
              </div>
              )} />}
            </CallsPanelBoundary>
            <CallsPanelBoundary panel="visibility" active={commTab === "visibility"} resetKey={identity.userId+':'+identity.workspaceId+':'+currentRecord.id}>
              {commTab === "visibility" && <CallsPanelContent render={() => (
              <div className="comm-history call-visibility-tab">
                <div className="visibility-tab-summary">
                  <div>
                    <span>Visibility evidence</span>
                    <strong>No scan evidence recorded</strong>
                    <small>Evidence appears here only after a verified site scan for this account.</small>
                  </div>
                </div><div className="visibility-call-angle">
                  <span>SUGGESTED QUESTION</span>
                  <p>
                    “I was wondering if you could help me understand something.
                    When someone submits the contact form on your site, where
                    should that request normally go?”
                  </p>
                  <button className="secondary-btn">
                    Insert into call flow
                  </button>
                </div>
              </div>
              )} />}
            </CallsPanelBoundary>
            <CallsPanelBoundary panel="record" active={commTab === "record"} resetKey={identity.userId+':'+identity.workspaceId+':'+currentRecord.id}>
              {commTab === "record" && <CallsPanelContent render={() => (
              <div className="comm-history call-record-tab">
                <div className="record-summary-grid">
                  {(Object.keys(resolutionLabels) as (ResolutionState|"unknown")[]).map(
                    (state) => (
                      <div key={state} className={state}>
                        <span>{resolutionLabels[state].title}</span>
                        <strong>
                          {agreementMap.filter((x) => x.state === state).length}
                        </strong>
                      </div>
                    ),
                  )}
                </div>
                <div className="record-section">
                  <span className="section-label">
                    Agreement and open-item map
                  </span>
                  {agreementMap.length === 0 ? (
                    <p className="empty-record">No branches classified yet.</p>
                  ) : (
                    agreementMap
                      .slice()
                      .reverse()
                      .map((item) => (
                        <div
                          className={`record-item ${item.state}`}
                          key={item.id}
                        >
                          <div>
                            <span>{(resolutionLabels[item.state]??resolutionLabels.unknown).title}</span>
                            <small>{item.timestamp}</small>
                          </div>
                          <strong>{item.topic}</strong>
                          <p>{item.detail}</p>
                          {item.issues.map(issue=><small key={issue}>{issue}</small>)}
                          {item.owner && (
                            <em>
                              Owner: {item.owner} · Review: {item.deadline}
                            </em>
                          )}
                        </div>
                      ))
                  )}
                </div>
                <div className="record-section">
                  <span className="section-label">Response history</span>
                  {responseHistory
                    .slice()
                    .reverse()
                    .map((event) => (
                      <div className="history-event" key={event.id}>
                        <strong>{event.context}</strong>
                        <p>“{event.mainResponse}”</p>
                        {event.followUpResponse && (
                          <p className="depth-two">
                            ↳ “{event.followUpResponse}”
                          </p>
                        )}
                        {event.issues.map(issue=><small key={issue}>{issue}</small>)}
                        <span>
                          {(resolutionLabels[event.resolution]??resolutionLabels.unknown).title} ·{" "}
                          {event.timestamp}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
              )} />}
            </CallsPanelBoundary>
            <CallsPanelBoundary panel="activity" active={commTab === "activity"} resetKey={identity.userId+':'+identity.workspaceId+':'+currentRecord.id}>
              {commTab === "activity" && <CallsPanelContent render={() => (
              <div className="comm-history activity-scroll" aria-label="Selected contact activity">
                {activityEntries.map(item=><div className="timeline-item" key={item.id}><div className="timeline-icon">{activityIcon(item.kind as ActivityType['type'])}</div><div><strong>{item.title}</strong><span>{item.detail}</span><small><time dateTime={item.occurredAt}>{Number.isNaN(Date.parse(item.occurredAt))?'Timestamp unavailable':new Date(item.occurredAt).toLocaleString()}</time></small></div></div>)}
                {!activityEntries.length&&<div className="empty-state">{historyUnavailable||communications.failure?'Contact activity is unavailable.':'No recorded activity for this contact in the current history projection.'}</div>}
                {activityEntries.length>0&&(historyUnavailable||communications.failure)&&<div role="status">Some contact activity sources are unavailable.</div>}
              </div>
              )} />}
            </CallsPanelBoundary>
          </div>
        </div>
      </section>

      {scheduleOpen && (
        <div className="meeting-drawer" role="dialog" aria-label="CRM next-step scheduling" aria-modal="true">
          <div className="meeting-drawer-head">
            <div>
              <span>NEXT-STEP COMMITMENT</span>
              <h2>Prepare a bounded schedule draft</h2>
              <p>
                Capture the agreed time, attendees, and proposed reminders.
                Calendar sync, meeting-link creation, and provider delivery
                remain disabled until verified adapters are configured.
              </p>
            </div>
            <button aria-label="Close scheduler and keep draft" onClick={() => setScheduleOpen(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="meeting-drawer-scroll">
            <div className="meeting-type-switch">
              <button
                className={nextStepType === "phone" ? "active" : ""}
                onClick={() => setNextStepType("phone")}
              >
                <PhoneCall size={16} />
                <span>
                  <strong>Regular call</strong>
                  <small>Scheduled callback to {current.phone}</small>
                </span>
              </button>
              <button
                className={nextStepType === "cliq" ? "active" : ""}
                onClick={() => setNextStepType("cliq")}
              >
                <Video size={16} />
                <span>
                  <strong>Video call · Cliq</strong>
                  <small>Joint decision or working session</small>
                </span>
              </button>
            </div>


            <div className="meeting-participant-summary">
              {nextStepType === "cliq" ? (
                <UsersRound size={18} />
              ) : (
                <PhoneCall size={18} />
              )}
              <div>
                <strong>
                  {nextStepType === "cliq"
                    ? "Primary contact and decision group"
                    : "Scheduled phone contact"}
                </strong>
                <span>
                  {current.name} ·{" "}
                  {nextStepType === "cliq" ? current.email : current.phone}
                </span>
              </div>
            </div>
            <label>
              {nextStepType === "cliq"
                ? "Decision makers and stakeholders"
                : "Confirmation recipients"}
              <textarea
                value={meetingAttendees || current.email}
                onChange={(e) => setMeetingAttendees(e.target.value)}
                placeholder={
                  nextStepType === "cliq"
                    ? "Comma-separated emails for every approver, blocker, budget owner, and implementation owner."
                    : "Email or mobile contact used for confirmations and reminders."
                }
              />
            </label>
            <div className="meeting-grid">
              <label>
                Date
                <input
                  type="date"
                  value={meetingDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setMeetingDate(e.target.value)}
                />
              </label>
              <label>
                Time
                <input
                  type="time"
                  value={meetingTime}
                  onChange={(e) => setMeetingTime(e.target.value)}
                />
              </label>
              <label>
                Duration
                <select
                  value={meetingDuration}
                  onChange={(e) => setMeetingDuration(e.target.value)}
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                </select>
              </label>
              <label>
                Time zone
                <select
                  value={meetingTimeZone}
                  onChange={(e) => setMeetingTimeZone(e.target.value)}
                >
                  <option>America/Chicago</option>
                  <option>America/New_York</option>
                  <option>America/Denver</option>
                  <option>America/Los_Angeles</option>
                </select>
              </label>
            </div>
            <label>
              Decision-oriented agenda
              <textarea
                value={meetingAgenda}
                onChange={(e) => setMeetingAgenda(e.target.value)}
              />
            </label>

            <details className="meeting-optional" open={window.innerWidth > 760 && !(window.innerWidth <= 980 && window.innerHeight <= 500)}><summary>Optional reminders and calendar preferences</summary>
            <div className="scheduler-settings-grid">
              <div className="scheduler-setting-card">
                <div className="scheduler-setting-head">
                  <AtSign size={16} />
                  <div>
                    <strong>Proposed delivery channels</strong>
                    <span>
                      Record where confirmation details should be sent after a
                      provider adapter is verified.
                    </span>
                  </div>
                </div>
                <div className="segmented-control">
                  <button
                    className={
                      reminderDeliveryMode === "email-only" ? "active" : ""
                    }
                    onClick={() => setReminderDeliveryMode("email-only")}
                  >
                    <Mail size={14} /> Email only
                  </button>
                  <button
                    className={
                      reminderDeliveryMode === "email-sms" ? "active" : ""
                    }
                    onClick={() => setReminderDeliveryMode("email-sms")}
                  >
                    <Smartphone size={14} /> Email + text
                  </button>
                </div>
                <small className="consent-confirmation">
                  <ShieldCheck size={13} /> SMS consent verified for{" "}
                  {current.name}
                </small>
              </div>

              <div className="scheduler-setting-card">
                <div className="scheduler-setting-head">
                  <CalendarDays size={16} />
                  <div>
                    <strong>Proposed rep calendar behavior</strong>
                    <span>
                      These preferences are captured in the draft only; no
                      calendar write occurs.
                    </span>
                  </div>
                </div>
                <label className="switch-row">
                  <input
                    type="checkbox"
                    checked={syncToRepCalendar}
                    onChange={(e) => setSyncToRepCalendar(e.target.checked)}
                  />
                  <span>Request calendar sync after provider verification</span>
                </label>
                <label className="switch-row">
                  <input
                    type="checkbox"
                    checked={blockRepTime}
                    onChange={(e) => setBlockRepTime(e.target.checked)}
                  />
                  <span>Request busy-time hold</span>
                </label>
              </div>
            </div>

            <div className="reminder-builder">
              <div className="reminder-builder-head">
                <AlarmClock size={17} />
                <div>
                  <span>REMINDER PLAN</span>
                  <strong>Quiet-hour-safe reminders</strong>
                  <small>No SMS after 8:00 PM or before 7:50 AM.</small>
                </div>
              </div>
              <div className="reminder-options">
                <label>
                  <input
                    type="checkbox"
                    checked={includeFourHourReminder}
                    onChange={(e) =>
                      setIncludeFourHourReminder(e.target.checked)
                    }
                  />{" "}
                  Four hours before
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={includeTenMinuteReminder}
                    onChange={(e) =>
                      setIncludeTenMinuteReminder(e.target.checked)
                    }
                  />{" "}
                  Ten minutes before
                </label>
              </div>
              <div className="reminder-plan-list">
                {reminderPlan.map((item) => (
                  <div
                    className={`reminder-plan-item ${item.status}`}
                    key={item.id}
                  >
                    <div className={`reminder-channel ${item.channel}`}>
                      {item.channel === "email" ? (
                        <Mail size={14} />
                      ) : (
                        <Smartphone size={14} />
                      )}
                    </div>
                    <div>
                      <strong>
                        {item.kind === "confirmation"
                          ? "Confirmation"
                          : item.kind === "four-hour"
                            ? "Four-hour reminder"
                            : "Ten-minute reminder"}{" "}
                        · {item.channel.toUpperCase()}
                      </strong>
                      <span>{item.sendAt}</span>
                      <small>Draft policy: {item.explanation}</small>
                    </div>
                    <b>
                      {item.status === "scheduled" ? "proposed" : item.status}
                    </b>
                  </div>
                ))}
              </div>
            </div>

            <div className="dynamic-reminder-language">
              <div>
                <MessageSquareText size={16} />
                <span>DRAFT CONFIRMATION LANGUAGE · DO NOT SEND YET</span>
              </div>
              <p>“{reminderCommitmentLanguage}”</p>
              <button>
                <ClipboardCopy size={14} /> Copy language
              </button>
            </div>

            </details>
            <div className="commitment-capture">
              <strong>
                Capture the verbal commitment before preparing the draft
              </strong>
              <label>
                <input
                  type="checkbox"
                  checked={attendeesConfirmed}
                  onChange={(e) => setAttendeesConfirmed(e.target.checked)}
                />{" "}
                The contact confirmed the time and expected attendees.
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={commitmentConfirmed}
                  onChange={(e) => setCommitmentConfirmed(e.target.checked)}
                />{" "}
                The purpose and intended outcome were stated aloud.
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={rescheduleConfirmed}
                  onChange={(e) => setRescheduleConfirmed(e.target.checked)}
                />{" "}
                They agreed to notify the rep if the time must change.
              </label>
              <label>
                Permission for one confirmation follow-up
                <select
                  value={followUpPermission}
                  onChange={(e) =>
                    setFollowUpPermission(
                      e.target.value as typeof followUpPermission,
                    )
                  }
                >
                  <option value="not-asked">Not asked yet</option>
                  <option value="granted">Granted</option>
                  <option value="declined">Declined and recorded</option>
                </select>
              </label>
            </div>

            <div className="meeting-rules">
              <strong>The next call must produce:</strong>
              <span>• A shared understanding of the verified problem</span>
              <span>• Agreement on decision criteria and required proof</span>
              <span>• Named ownership for the next action</span>
              <span>
                • A decision, condition, scheduled continuation, or explicit
                reason to stop
              </span>
            </div>
            {meetingCreated && scheduledNextStep && (
              <div className={`next-step-created ${scheduledNextStep.type}`}>
                {scheduledNextStep.type === "cliq" ? (
                  <Video size={19} />
                ) : (
                  <PhoneCall size={19} />
                )}
                <div>
                  <strong>
                    {scheduledNextStep.type === "cliq"
                      ? "Cliq meeting draft prepared"
                      : "Phone follow-up draft prepared"}
                  </strong>
                  <span>
                    {scheduledNextStep.date} at {scheduledNextStep.time} ·{" "}
                    {scheduledNextStep.duration} minutes · local draft only
                  </span>
                  <code>No provider link or calendar event created</code>
                  <small>
                    {
                      scheduledNextStep.reminders.filter(
                        (r) => r.status !== "suppressed",
                      ).length
                    }{" "}
                    reminder steps proposed ·{" "}
                    {scheduledNextStep.deliveryMode === "email-sms"
                      ? "email + text"
                      : "email only"}{" "}
                    · writesPerformed:false
                  </small>
                </div>
                <button>
                  <ClipboardCopy size={14} /> Copy
                </button>
              </div>
            )}
          </div>
          <div className="meeting-drawer-actions">
            <button
              className="secondary-btn"
              onClick={() => setScheduleOpen(false)}
            >
              Close / keep draft
            </button>
            <button
              className="primary-btn"
              disabled={
                !meetingDate ||
                !meetingTime ||
                !meetingAgenda.trim() ||
                !attendeesConfirmed ||
                !commitmentConfirmed ||
                !rescheduleConfirmed ||
                followUpPermission === "not-asked"
              }
              onClick={createScheduledNextStep}
            >
              {nextStepType === "cliq" ? (
                <Video size={15} />
              ) : (
                <PhoneCall size={15} />
              )}{" "}
              {meetingCreated
                ? "Update schedule draft"
                : nextStepType === "cliq"
                  ? "Prepare Cliq draft"
                  : "Prepare phone-call draft"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}



type VisibilityIssueStatus =
  | "Detected"
  | "Needs Verification"
  | "Verified"
  | "Customer Notified"
  | "Provider Notified"
  | "Quick Fix Offered"
  | "Quick Fix Approved"
  | "In Progress"
  | "Awaiting Verification"
  | "Verified Fixed"
  | "Recurring"
  | "Closed"
  | "Archived";
type VisibilityIssue = {
  id: string;
  title: string;
  severity: "Critical" | "High" | "Medium" | "Low";
  confidence: number;
  found: string;
  verified: string;
  status: VisibilityIssueStatus;
  impact: string;
  resolution: string;
  evidence: string;
  source: string;
  assigned: string;
  category: string;
};
function VisibilityIntelligencePage() {
  const [selectedId, setSelectedId] = useState("vi1");
  const [filter, setFilter] = useState<
    "All" | "Critical" | "High" | "Medium" | "Low"
  >("All");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [providerTab, setProviderTab] = useState<"provider" | "history">(
    "provider",
  );
  const [scan, setScan] = useState<"complete" | "queued" | "scanning">(
    "complete",
  );
  const [reportStatus, setReportStatus] = useState<"idle" | "working" | "sent">(
    "idle",
  );
  const issues: VisibilityIssue[] = [
    {
      id: "vi1",
      title: "Contact form fails after submission",
      severity: "Critical",
      confidence: 96,
      found: "Jul 15, 2026",
      verified: "Today, 9:42 AM",
      status: "Verified",
      impact: "Potential loss of inbound leads and attribution data.",
      resolution:
        "Repair the submission handler, confirm delivery, then verify with a controlled test.",
      evidence:
        "POST request returns 500 after the success animation. No message reaches the configured mailbox.",
      source: "Direct browser inspection",
      assigned: "Canonical assignee unavailable",
      category: "Conversion",
    },
    {
      id: "vi2",
      title: "Two location pages missing LocalBusiness schema",
      severity: "High",
      confidence: 92,
      found: "Jul 15, 2026",
      verified: "Today, 9:39 AM",
      status: "Provider Notified",
      impact: "Reduced clarity for location-specific search and AI systems.",
      resolution: "Add validated location-specific LocalBusiness markup.",
      evidence: "/laredo and /mcallen contain Organization schema only.",
      source: "HTML inspection",
      assigned: "Website Provider",
      category: "Structured data",
    },
    {
      id: "vi3",
      title: "Booking link uses a redirect chain",
      severity: "Medium",
      confidence: 88,
      found: "Jul 15, 2026",
      verified: "Today, 9:37 AM",
      status: "Awaiting Verification",
      impact: "Mobile friction and slower booking completion.",
      resolution:
        "Point the visible booking URL directly to the final HTTPS destination.",
      evidence: "Homepage CTA passes through two 301 redirects before loading.",
      source: "Header inspection",
      assigned: "Maya Lee",
      category: "Conversion",
    },
    {
      id: "vi4",
      title: "Security headers incomplete",
      severity: "Low",
      confidence: 85,
      found: "Jul 15, 2026",
      verified: "Today, 9:34 AM",
      status: "Needs Verification",
      impact: "Minor trust and hardening concern.",
      resolution: "Review CSP, Permissions-Policy, and Referrer-Policy.",
      evidence: "CSP and Permissions-Policy were not present.",
      source: "Header inspection",
      assigned: "Unassigned",
      category: "Security",
    },
    {
      id: "vi5",
      title: "Broken social profile link",
      severity: "Low",
      confidence: 99,
      found: "Jul 13, 2026",
      verified: "Yesterday",
      status: "Verified Fixed",
      impact: "Removed a dead-end from the trust path.",
      resolution: "No further action unless recurring.",
      evidence: "Previous URL returned 404. Current link returns 200.",
      source: "Resolution rescan",
      assigned: "Internal Team",
      category: "Trust",
    },
  ];
  const selected = issues.find((x) => x.id === selectedId) || issues[0];
  const visible = issues.filter(
    (x) => filter === "All" || x.severity === filter,
  );
  function runScan() {
    setScan("queued");
    setTimeout(() => setScan("scanning"), 500);
    setTimeout(() => setScan("complete"), 1800);
  }
  function sendReport() {
    setReportStatus("working");
    setTimeout(() => setReportStatus("sent"), 900);
  }
  return (
    <div className="page visibility-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">VISIBILITY INTELLIGENCE</span>
          <h1>Evidence, ownership, and verified resolution</h1>
          <p>
            AvertSignal supplies the intelligence. The CRM turns it into
            outreach, accountability, and action.
          </p>
        </div>
        <div className="visibility-actions">
          <button className="secondary-btn branded-action" onClick={sendReport}>
            <FileText size={15} />
            {reportStatus === "idle"
              ? "Send report"
              : reportStatus === "working"
                ? "Generating…"
                : "Report sent"}
          </button>
          <button className="primary-btn" onClick={runScan}>
            <RefreshCw size={15} />
            {scan === "scanning"
              ? "Scanning…"
              : scan === "queued"
                ? "Queued"
                : "Run lightweight scan"}
          </button>
        </div>
      </div>
      <section className="visibility-summary">
        <div className="visibility-score-card">
          <div className="score-ring">
            <strong>71</strong>
            <span>Visibility</span>
          </div>
          <div>
            <span>OVERALL STATUS</span>
            <h2>Needs attention</h2>
            <p>One verified conversion failure creates immediate sales risk.</p>
          </div>
        </div>
        {[
          ["Risk score", "64", "Elevated"],
          ["Confidence", "91%", "Direct evidence"],
          ["Business impact", "$2.4K–$7.8K", "Estimated monthly"],
          ["Open issues", "4", "1 critical · 1 high"],
          ["Last scan", "Today", "9:42 AM"],
        ].map((x) => (
          <div className="visibility-stat" key={x[0]}>
            <span>{x[0]}</span>
            <strong>{x[1]}</strong>
            <small>{x[2]}</small>
          </div>
        ))}
      </section>
      <div className="scan-progress-card">
        <div>
          <ScanSearch size={18} />
          <span>
            <strong>Initial Visibility Snapshot</strong>
            <small>
              {scan === "complete"
                ? "Complete · 27 checks · 5 findings"
                : scan === "queued"
                  ? "Background job queued…"
                  : "Inspecting direct website signals…"}
            </small>
          </span>
        </div>
        <div className={`scan-progress ${scan}`}>
          <span />
        </div>
        <div className="scan-meta">
          <span>Browser inspection</span>
          <span>Cached headers</span>
          <span>Low-cost checks first</span>
          <span>Duplicate prevention</span>
        </div>
      </div>
      <section className="visibility-workspace">
        <div className="panel issue-list-panel">
          <div className="panel-header">
            <h3>Issue lifecycle</h3>
            <div className="severity-filter">
              {(["All", "Critical", "High", "Medium", "Low"] as const).map(
                (s) => (
                  <button
                    className={filter === s ? "active" : ""}
                    onClick={() => setFilter(s)}
                    key={s}
                  >
                    {s}
                  </button>
                ),
              )}
            </div>
          </div>
          <div className="issue-list-scroll">
            {visible.map((i) => (
              <button
                className={`visibility-issue-row ${selected.id === i.id ? "active" : ""}`}
                onClick={() => setSelectedId(i.id)}
                key={i.id}
              >
                <div className={`issue-severity ${i.severity.toLowerCase()}`}>
                  {i.severity === "Critical" ? (
                    <CircleAlert size={16} />
                  ) : i.status === "Verified Fixed" ? (
                    <CheckCircle size={16} />
                  ) : (
                    <AlertTriangle size={16} />
                  )}
                </div>
                <div>
                  <strong>{i.title}</strong>
                  <span>
                    {i.category} · {i.status}
                  </span>
                  <small>{i.impact}</small>
                </div>
                <div className="issue-confidence">
                  <strong>{i.confidence}%</strong>
                  <span>confidence</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="panel issue-detail-panel">
          <div className="issue-detail-head">
            <div>
              <span
                className={`severity-pill ${selected.severity.toLowerCase()}`}
              >
                {selected.severity}
              </span>
              <h2>{selected.title}</h2>
              <p>{selected.impact}</p>
            </div>
            <button className="issue-menu-btn">
              Actions <ChevronRight size={14} />
            </button>
          </div>
          <div className="issue-detail-scroll">
            <div className="issue-fact-grid">
              {[
                ["Status", selected.status],
                ["Confidence", selected.confidence + "%"],
                ["Date found", selected.found],
                ["Last verified", selected.verified],
                ["Source", selected.source],
                ["Assigned to", selected.assigned],
              ].map((x) => (
                <div key={x[0]}>
                  <span>{x[0]}</span>
                  <strong>{x[1]}</strong>
                </div>
              ))}
            </div>
            <div className="evidence-card">
              <div>
                <Eye size={17} />
                <strong>Evidence</strong>
              </div>
              <p>{selected.evidence}</p>
              <button>
                <ClipboardCopy size={14} /> Copy evidence
              </button>
            </div>
            <div className="resolution-card">
              <div>
                <Wrench size={17} />
                <strong>Suggested resolution</strong>
              </div>
              <p>{selected.resolution}</p>
              <div className="resolution-action-grid">
                <button onClick={sendReport}>
                  Send branded customer report
                </button>
                <button>Provider report</button>
                <button>Call talking points</button>
                <button>Generate SMS</button>
                <button>Create task</button>
                <button>Rescan issue</button>
                <button onClick={() => setQuoteOpen(true)}>
                  Quick fix quote
                </button>
              </div>
            </div>
            <div className="diy-card">
              <div>
                <strong>Fix it internally</strong>
                <span>
                  Generate scoped DIY instructions and a verification checklist.
                </span>
              </div>
              <button>Generate DIY instructions</button>
            </div>
          </div>
        </div>
        <div className="panel provider-panel">
          <div className="provider-tabs">
            <button
              className={providerTab === "provider" ? "active" : ""}
              onClick={() => setProviderTab("provider")}
            >
              Provider
            </button>
            <button
              className={providerTab === "history" ? "active" : ""}
              onClick={() => setProviderTab("history")}
            >
              History
            </button>
          </div>
          {providerTab === "provider" ? (
            <div className="provider-scroll">
              <div className="provider-profile">
                <div className="avatar large">QP</div>
                <div>
                  <strong>Quartermile Digital</strong>
                  <span>Website and marketing provider</span>
                </div>
              </div>
              <div className="provider-details">
                <span>
                  <Mail size={14} /> support@provider.example
                </span>
                <span>
                  <Phone size={14} /> (555) 019-8821
                </span>
                <span>
                  <Globe2 size={14} /> provider.example
                </span>
              </div>
              <div className="provider-action-list">
                {[
                  "Generate provider report",
                  "Email provider",
                  "Request completion",
                  "Request ETA",
                  "Mark provider responded",
                  "Mark unresponsive",
                  "Request verification",
                ].map((a) => (
                  <button key={a}>{a}</button>
                ))}
              </div>
              <div className="internal-only">
                <ShieldCheck size={16} />
                <span>Provider performance remains internal only.</span>
              </div>
            </div>
          ) : (
            <div className="provider-scroll provider-history">
              {[
                ["Issues sent", "8"],
                ["Median response", "1d 4h"],
                ["Median resolution", "4d 7h"],
                ["Verified success", "75%"],
              ].map((x) => (
                <div key={x[0]}>
                  <span>{x[0]}</span>
                  <strong>{x[1]}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      {quoteOpen && (
        <div className="quote-drawer">
          <div className="quote-drawer-head">
            <div>
              <span>QUICK FIX QUOTE</span>
              <h2>{selected.title}</h2>
            </div>
            <button onClick={() => setQuoteOpen(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="quote-drawer-scroll">
            <div className="quote-grid">
              <label>
                Estimated labor hours
                <input defaultValue="2.5" />
              </label>
              <label>
                Complexity
                <select defaultValue="Moderate">
                  <option>Simple</option>
                  <option>Moderate</option>
                  <option>Complex</option>
                </select>
              </label>
              <label>
                Internal cost
                <input defaultValue="$175" />
              </label>
              <label>
                Customer price
                <input defaultValue="$495" />
              </label>
              <label>
                Warranty
                <input defaultValue="14-day verification warranty" />
              </label>
              <label>
                Expected completion
                <input defaultValue="2–3 business days" />
              </label>
              <label className="wide">
                Required access
                <textarea defaultValue="CMS administrator access and form-delivery confirmation." />
              </label>
              <label className="wide">
                Customer notes
                <textarea defaultValue="One-time technical correction only. This is not a recurring SEO engagement." />
              </label>
            </div>
          </div>
          <div className="quote-drawer-actions">
            <button className="secondary-btn">Save draft</button>
            <button className="primary-btn">Review before sending</button>
          </div>
        </div>
      )}
    </div>
  );
}

function LiveCallTimer({connectedAt,durationSeconds=0}:{connectedAt:string|null;durationSeconds?:number}){
  const[,tick]=useState(0);
  useEffect(()=>{if(!connectedAt)return;const timer=window.setInterval(()=>tick(value=>value+1),1000);return()=>window.clearInterval(timer)},[connectedAt]);
  return <>{formatCallDuration(connectedAt?Math.max(0,Math.floor((Date.now()-Date.parse(connectedAt))/1000)):durationSeconds)}</>;
}

function OrganizationsPage({
  contacts,
  organizations: canonicalOrganizations,
  accountLinks,
  lifecycleEvents,
  acquisitionAttributions,
  canLaunchDemo,
  activities,
  query,
  live,
  includeSynthetic,
  onCreateCompany,
  onOrganizationsChange,
  onContactsChange,
}: {
  contacts: Contact[];
  organizations: Organization[];
  accountLinks: AccountLink[];
  lifecycleEvents: LifecycleEvent[];
  acquisitionAttributions: AcquisitionAttribution[];
  canLaunchDemo: boolean;
  activities: ActivityType[];
  query: string;
  live: boolean;
  includeSynthetic: boolean;
  onCreateCompany: () => void;
  onOrganizationsChange: React.Dispatch<React.SetStateAction<Organization[]>>;
  onContactsChange: (value: Contact[]) => void;
}) {
  const identity=useCrmIdentity();
  const [companyImportOpen,setCompanyImportOpen]=useState(false);
  const communications=useCommunications();
  const voice=useCrmVoice();
  const navigate = useNavigate();
  const {contactId:routeContactId}=useParams();
  const [searchParams]=useSearchParams();
  const organizations = useMemo(
    () =>
      buildOrganizations(
        contacts,
        canonicalOrganizations,
        accountLinks,
        lifecycleEvents,
      ),
    [contacts, canonicalOrganizations, accountLinks, lifecycleEvents],
  );
  const [reviewSegmentKey,setReviewSegmentKey]=useState('ALL');
  const [reviewSegments,setReviewSegments]=useState<CompanyReviewSegment[]>([]);
  useEffect(()=>{setReviewSegmentKey('ALL');setReviewSegments([])},[identity.userId,identity.workspaceId]);
  const visibleOrganizations = useMemo(
    () => searchOrganizations(organizations, query).filter(company=>reviewSegmentKey==='ALL'||reviewSegments.some(segment=>segment.key===reviewSegmentKey&&segment.companyIds.includes(company.id))),
    [organizations, query,reviewSegmentKey,reviewSegments],
  );
  const [selectedContactId, setSelectedContactId] = useState("");
  const [tab, setTab] = useState<
    "overview" | "email" | "text" | "notes" | "calls" | "activity"
  >("overview");
  useEffect(()=>{const requested=searchParams.get('tab');if(requested&&['overview','email','text','notes','calls','activity'].includes(requested))setTab(requested as typeof tab)},[routeContactId,searchParams]);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailDraftId,setEmailDraftId]=useState("");
  const [emailLinks,setEmailLinks]=useState<EmailDraftLink[]>([]);
  const [emailAttachments,setEmailAttachments]=useState<EmailDraftAttachment[]>([]);
  const [emailLinkUrl,setEmailLinkUrl]=useState("");
  const [emailLinkLabel,setEmailLinkLabel]=useState("");
  const [emailFile,setEmailFile]=useState<File|null>(null);
  const [emailSaveState,setEmailSaveState]=useState<"idle"|"loading"|"saving"|"success"|"error">("idle");
  const [emailCommunications,setEmailCommunications]=useState<MicrosoftCommunicationsSnapshot|null>(null);
  const [emailMailboxId,setEmailMailboxId]=useState("");
  const [emailIdempotencyKey,setEmailIdempotencyKey]=useState<string>(()=>crypto.randomUUID());
  const [emailSendState,setEmailSendState]=useState<"idle"|"sending"|"accepted"|"error">("idle");
  const [emailSendOutcome,setEmailSendOutcome]=useState<ReplyAttemptResult>();
  const [emailSendStatus,setEmailSendStatus]=useState("");
  const [emailHistoryDelayed,setEmailHistoryDelayed]=useState(false);
  const [emailIntentChanged,setEmailIntentChanged]=useState(false);
  const [emailLoadedKey,setEmailLoadedKey]=useState("");
  const emailAttempt=useRef<NewEmailState['attempt']>();
  const emailLocks=useRef(new Set<string>()),emailEpoch=useRef(0);
  const emailDraftDirty=useRef(false),previousEmailScope=useRef("");
  const emailScope=JSON.stringify([identity.userId,identity.workspaceId]);
  const emailDraftScope=useMemo(()=>({userId:identity.userId,workspaceId:identity.workspaceId}),[identity.userId,identity.workspaceId]);
  const emailGeneration=crmDraftGeneration(emailDraftScope);
  const [draftStatus, setDraftStatus] = useState("");
  const [communicationError, setCommunicationError] = useState("");
  const [editCompany, setEditCompany] = useState<Organization | null>(null);
  const editCompanyRequest=useRef(0);
  const [editContact, setEditContact] = useState<Contact | null>(null);
  const [editState, setEditState] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [editError, setEditError] = useState("");
  const [dispositions,setDispositions]=useState<CallDisposition[]>([]);
  const [contactDisposition,setContactDisposition]=useState<CallDisposition["disposition"]>("No Answer");
  const [dispositionNotes,setDispositionNotes]=useState("");
  const profileOutcomeAttempt=useRef({contactId:'',id:crypto.randomUUID(),inFlight:false});
  const [dispositionState,setDispositionState]=useState<"idle"|"saving"|"success"|"error">("idle");
  const [profileDispositionError,setProfileDispositionError]=useState('');
  const [profileCallState,setProfileCallState]=useState<"idle"|"starting"|"error">("idle");
  const [profileCallError,setProfileCallError]=useState("");
  const profileCallInFlight=useRef(false);
  useEffect(()=>{if(live)getDispositions().then(setDispositions).catch(()=>setDispositions([]))},[live]);
  const routedContact=routeContactId?contacts.find(item=>item.id===routeContactId):undefined;
  const requestedOrganizationId=searchParams.get('organization');
  const org =
    requestedOrganizationId?organizations.find(item=>item.id===requestedOrganizationId):
    (routedContact?organizations.find(item=>item.contacts.some(contact=>contact.id===routedContact.id)):undefined) ||
    visibleOrganizations[0];
  const person =
    (routeContactId?org?.contacts.find(item=>item.id===routeContactId):undefined) ||
    org?.contacts.find((item) => item.id === selectedContactId) ||
    org?.contacts[0];
  const [profileCallRoute,setProfileCallRoute]=useState<{scope:string;selection:CompanyCallSelection}|null>(null);
  const profileCallScope=JSON.stringify([identity.userId,identity.workspaceId,identity.role,org?.id,person?.id]);
  const currentProfileCallScope=useRef(profileCallScope);currentProfileCallScope.current=profileCallScope;
  const profileRestSelection=useCompanyRestSelection(person?.organizationId,person?.id,tab==='calls'&&contactDisposition==='Rest for 2 weeks','profile');
  useEffect(()=>{setDispositionNotes('');setDispositionState('idle');setProfileDispositionError('');setContactDisposition('No Answer');profileOutcomeAttempt.current={contactId:person?.id||'',id:crypto.randomUUID(),inFlight:false}},[profileCallScope,person?.id]);
  useEffect(()=>{if(profileRestSelection.pendingDispositionId)setContactDisposition('Rest for 2 weeks')},[profileRestSelection.pendingDispositionId]);
  useEffect(()=>setProfileCallRoute(previous=>previous?.scope===profileCallScope?previous:null),[profileCallScope]);
  const profileCallSelection=profileCallRoute?.scope===profileCallScope?profileCallRoute.selection:undefined;
  function selectProfileCallRoute(route:{id:string;contactId:string|null}){
    if(currentProfileCallScope.current!==profileCallScope||!org?.canonical||!route.id||route.contactId&&!org.contacts.some(contact=>contact.id===route.contactId))return;
    const selection={companyId:org.id,phoneRouteId:route.id,contactId:route.contactId??person?.id};
    setProfileCallRoute(previous=>previous?.scope===profileCallScope&&previous.selection.phoneRouteId===selection.phoneRouteId&&previous.selection.contactId===selection.contactId?previous:{scope:profileCallScope,selection});
  }
  const canonicalOrg = canonicalOrganizations.find(item => item.id === org?.id);
  const audienceAttribution = acquisitionAttributions.find(item => item.organizationId === org?.id || item.contactId === person?.id);
  const selectedDemoAudience = resolveDemoAudience({
    ownerAppAccountId: canonicalOrg?.ownerAppAccountId,
    agencyAccountId: canonicalOrg?.agencyAccountId,
    productLanes: org?.links.filter(item => item.linkStatus === 'active').map(item => item.productLane),
    leadType: audienceAttribution?.leadType,
  });
  const activeProfileCall=voice.activeCall&&voice.resolveContact(voice.activeCall)?.id===person?.id?voice.activeCall:null;
  const endedProfileCall=voice.lastEndedCall&&voice.resolveContact(voice.lastEndedCall)?.id===person?.id?voice.lastEndedCall:null;
  useEffect(()=>{if(endedProfileCall)setTab("calls")},[endedProfileCall]);
  useEffect(()=>{profileCallInFlight.current=false;setProfileCallState("idle");setProfileCallError("")},[person?.id]);
  const emailKey=JSON.stringify([emailScope,person?.id]);
  const emailForm=useRef({key:emailKey,subject:emailSubject,body:emailBody,sender:emailMailboxId,links:emailLinks,attachments:emailAttachments});
  emailForm.current={key:emailKey,subject:emailSubject,body:emailBody,sender:emailMailboxId,links:emailLinks,attachments:emailAttachments};
  useEffect(()=>{
    if(previousEmailScope.current&&previousEmailScope.current!==emailScope)purgeNewEmailStates(previousEmailScope.current);
    previousEmailScope.current=emailScope;
    if(!identity.userId||!identity.workspaceId)return;
    const purge=()=>{emailEpoch.current++;purgeNewEmailStates(emailScope);emailAttempt.current=undefined;setEmailLoadedKey("");setEmailSubject("");setEmailBody("");setEmailMailboxId("");setEmailLinks([]);setEmailAttachments([]);setEmailDraftId("");setEmailSendOutcome(undefined);setEmailSendStatus("");setEmailSendState("idle")};
    registerCrmDraft(`new-email:${emailScope}`,purge,'microsoft-communications',emailDraftScope);
  },[emailScope,emailDraftScope,emailGeneration,emailLoadedKey,identity.userId,identity.workspaceId]);
  useEffect(()=>()=>{emailEpoch.current++},[]);
  useEffect(()=>{
    emailEpoch.current++;emailDraftDirty.current=false;const stored=person?.id?readNewEmailState(emailScope,person.id):undefined;
    emailAttempt.current=stored?.attempt;setEmailIdempotencyKey(stored?.attempt?.id||crypto.randomUUID());setEmailSendOutcome(undefined);setEmailSendStatus(stored?.attempt?'Previous email attempt needs a status check before sending.':'');setEmailSendState("idle");setEmailHistoryDelayed(false);
    setEmailSubject(stored?.draft.subject||"");setEmailBody(stored?.draft.body||"");setEmailMailboxId(stored?.draft.senderMailboxId||"");setEmailLoadedKey(stored||!live?emailKey:"");
  },[emailScope,emailKey,person?.id,live]);
  useEffect(()=>{
    if(!live||!person?.id||emailLoadedKey!==emailKey||!identity.userId||!identity.workspaceId||!crmDraftWritable(emailDraftScope,emailGeneration))return;
    try{saveNewEmailState(emailScope,person.id,{draft:{subject:emailSubject,body:emailBody,senderMailboxId:emailMailboxId},...(emailAttempt.current?{attempt:emailAttempt.current}:{})})}catch{setEmailSendStatus('Local draft/attempt storage unavailable. Sending is blocked until storage works.')}
  },[live,person?.id,emailLoadedKey,emailKey,emailScope,emailSubject,emailBody,emailMailboxId,emailDraftScope,emailGeneration,identity.userId,identity.workspaceId]);
  useEffect(()=>{
    let active=true;const attempt=emailAttempt.current;setEmailIntentChanged(false);
    if(!attempt||!person)return;
    void newEmailFingerprint({actorId:identity.userId,workspaceId:identity.workspaceId,contactId:person.id,senderMailboxId:emailMailboxId,recipient:person.email,subject:emailSubject,body:emailBody}).then(fingerprint=>{if(active)setEmailIntentChanged(fingerprint!==attempt.fingerprint)}).catch(()=>{if(active)setEmailIntentChanged(true)});
    return()=>{active=false};
  },[emailSubject,emailBody,emailMailboxId,person,identity.userId,identity.workspaceId,emailSendOutcome,emailKey]);
  const startProfileCall=async(contactId:string)=>{
    if(profileCallInFlight.current||['connecting','dialing','ringing','connected'].includes(voice.state))return;
    profileCallInFlight.current=true;setProfileCallState("starting");setProfileCallError("");
    try{await voice.start(contactId);setProfileCallState("idle")}
    catch(error){setProfileCallState("error");setProfileCallError(`${error instanceof Error?error.message.replaceAll('_',' '):'voice call unavailable'}. Review contact call eligibility and retry.`)}
    finally{profileCallInFlight.current=false}
  };
  useEffect(()=>{let active=true;if(!live||!person?.id)return;const contactId=person.id,epoch=emailEpoch.current;setEmailSaveState("loading");getEmailDraft(contactId).then(draft=>{if(!active||epoch!==emailEpoch.current||!crmDraftWritable(emailDraftScope,emailGeneration))return;setEmailDraftId(draft?.id||"");const local=readNewEmailState(emailScope,contactId);if(!local&&!emailDraftDirty.current){setEmailSubject(draft?.subject||"");setEmailBody(draft?.body||"")}setEmailLinks(draft?.links||[]);setEmailAttachments(draft?.attachments||[]);setEmailSaveState("idle");setEmailLoadedKey(emailKey)}).catch(()=>{if(active&&epoch===emailEpoch.current&&crmDraftWritable(emailDraftScope,emailGeneration)){setEmailSaveState("error");setEmailLoadedKey(emailKey)}});return()=>{active=false}},[live,person?.id,emailScope,emailDraftScope,emailGeneration,emailKey]);
  useEffect(()=>{let active=true;if(!live)return;const epoch=emailEpoch.current;getMicrosoftCommunications(includeSynthetic).then(snapshot=>{if(!active||epoch!==emailEpoch.current||!crmDraftWritable(emailDraftScope,emailGeneration))return;setEmailCommunications(snapshot);const sendable=snapshot.mailboxes.filter(mailbox=>mailbox.send_enabled&&mailbox.verification_state==='VERIFIED'&&mailbox.permissions.canSend);setEmailMailboxId(current=>sendable.some(mailbox=>mailbox.id===current)?current:(sendable.find(mailbox=>mailbox.id===snapshot.defaultMailboxId&&mailbox.mailbox_type==='SHARED')||sendable.find(mailbox=>mailbox.mailbox_type==='SHARED'))?.id||"")}).catch(value=>{if(!active||epoch!==emailEpoch.current)return;if((value as {status?:number}).status===403){purgeCrmResourceDrafts('microsoft-communications');setEmailCommunications(null)}else setEmailHistoryDelayed(true)});return()=>{active=false}},[includeSynthetic,live,person?.id,emailScope,emailDraftScope,emailGeneration]);
  const persistEmailDraft=async(links=emailLinks)=>{if(!person)return null;setEmailSaveState("saving");setCommunicationError("");try{const saved=await saveEmailDraft({contactId:person.id,organizationId:person.organizationId,senderIdentity:"",recipient:person.email,subject:emailSubject,body:emailBody,links});setEmailDraftId(saved.id);setEmailLinks(saved.links);setEmailAttachments(current=>saved.attachments.length?saved.attachments:current);setEmailSaveState("success");return saved}catch(value){setEmailSaveState("error");setCommunicationError(value instanceof Error?value.message:"email_draft_save_failed");return null}};
  const persistEmailLink=async()=>{if(!/^https:\/\/\S+$/.test(emailLinkUrl))return;const links=[...emailLinks,{url:emailLinkUrl,label:emailLinkLabel.trim()}];if(await persistEmailDraft(links)){setEmailLinkUrl("");setEmailLinkLabel("")}};
  const persistEmailFile=async()=>{if(!person||!emailFile)return;const draft=emailDraftId?{id:emailDraftId}:await persistEmailDraft();if(!draft)return;setEmailSaveState("saving");try{const result=await addEmailDraftFile({contactId:person.id,draftId:draft.id,file:emailFile});setEmailAttachments(current=>[...current,{id:result.attachment.id,filename:result.attachment.filename,contentType:result.attachment.content_type,sizeBytes:result.attachment.size_bytes,createdAt:result.attachment.created_at}]);setEmailFile(null);setEmailSaveState("success")}catch(value){setEmailSaveState("error");setCommunicationError(value instanceof Error?value.message:"email_attachment_save_failed")}};
  const removeEmailLink=async(url:string)=>{await persistEmailDraft(emailLinks.filter(link=>link.url!==url))};
  const downloadEmailFile=async(attachmentId:string)=>{if(!person||!emailDraftId)return;setEmailSaveState("loading");try{const result=await getEmailDraftFile({contactId:person.id,draftId:emailDraftId,attachmentId});window.open(result.downloadUrl,"_blank","noopener,noreferrer");setEmailSaveState("idle")}catch(value){setEmailSaveState("error");setCommunicationError(value instanceof Error?value.message:"email_attachment_download_failed")}};
  const removeEmailFile=async(attachmentId:string)=>{if(!person||!emailDraftId)return;setEmailSaveState("saving");try{await removeEmailDraftFile({contactId:person.id,draftId:emailDraftId,attachmentId});setEmailAttachments(current=>current.filter(file=>file.id!==attachmentId));setEmailSaveState("success")}catch(value){setEmailSaveState("error");setCommunicationError(value instanceof Error?value.message:"email_attachment_remove_failed")}};
  const emailIdempotencyKeyIsValid=/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(emailIdempotencyKey);
  const applyEmailOutcome=(result:ReplyAttemptResult,attempt:NonNullable<NewEmailState['attempt']>,captured:typeof emailForm.current)=>{
    if(result.attemptId!==attempt.id)throw Error('email_outcome_unverified');
    setEmailSendOutcome(result);setEmailSendStatus(result.attemptConflict?'Another email attempt already exists. Check its status before starting changed email.':newEmailAttemptLabel(result));setEmailSendState(result.providerAccepted?'accepted':'idle');
    if(result.providerAccepted&&!result.attemptConflict&&emailForm.current.key===captured.key&&emailForm.current.subject===captured.subject&&emailForm.current.body===captured.body&&emailForm.current.sender===captured.sender&&emailForm.current.links===captured.links&&emailForm.current.attachments===captured.attachments){
      setEmailSubject('');setEmailBody('');setEmailDraftId('');setEmailLinks([]);setEmailAttachments([]);
      try{saveNewEmailState(emailScope,person!.id,{draft:{subject:'',body:'',senderMailboxId:captured.sender},attempt})}catch{setEmailSendStatus(`${newEmailAttemptLabel(result)} Local draft storage update delayed.`)}
    }
  };
  const refreshEmailHistory=async(epoch:number,key:string)=>{
    try{const snapshot=await getMicrosoftCommunications(includeSynthetic);if(epoch===emailEpoch.current&&emailForm.current.key===key&&crmDraftWritable(emailDraftScope,emailGeneration)){setEmailCommunications(snapshot);setEmailHistoryDelayed(false)}}catch(value){if(epoch!==emailEpoch.current||emailForm.current.key!==key||!crmDraftWritable(emailDraftScope,emailGeneration))return;if((value as {status?:number}).status===403){purgeCrmResourceDrafts('microsoft-communications');setEmailCommunications(null);setEmailHistoryDelayed(false);setEmailSendState('accepted');setEmailSendStatus('Microsoft accepted the message; mailbox access is now unavailable. Protected drafts cleared.')}else setEmailHistoryDelayed(true)}
  };
  const checkEmailStatus=async()=>{
    if(!person||!emailAttempt.current||emailLocks.current.has(emailKey)||!crmDraftWritable(emailDraftScope,emailGeneration))return;
    const attempt=emailAttempt.current,epoch=emailEpoch.current,captured={...emailForm.current};emailLocks.current.add(emailKey);setEmailSendState('sending');setEmailSendStatus('Checking email status…');
    const current=()=>epoch===emailEpoch.current&&emailForm.current.key===captured.key&&crmDraftWritable(emailDraftScope,emailGeneration);
    try{
      const result=parseReplyAttemptResult(await microsoftAction({operation:'check_send_status',idempotencyKey:attempt.id}));
      if(!current())return;
      const fingerprint=await newEmailFingerprint({actorId:identity.userId,workspaceId:identity.workspaceId,contactId:person.id,senderMailboxId:captured.sender,recipient:person.email,subject:captured.subject,body:captured.body});
      if(!current())return;
      applyEmailOutcome(result,attempt,fingerprint===attempt.fingerprint?captured:{...captured,subject:'\0'});
      if(result.providerAccepted)void refreshEmailHistory(epoch,captured.key);
    }catch(value){if(current()){
      const failure=value as {status?:number;responseBody?:{attemptFound?:boolean;safeToRetry?:boolean;providerAccepted?:boolean;acceptanceUnknown?:boolean}};
      if(failure.status===404&&failure.responseBody?.attemptFound===false&&failure.responseBody.safeToRetry===true&&failure.responseBody.providerAccepted===false&&failure.responseBody.acceptanceUnknown===false){setEmailSendOutcome({ok:false,attemptId:attempt.id,attemptState:'NOT_RESERVED',providerAccepted:false,acceptanceUnknown:false,persistencePending:false,safeToRetry:true,writesPerformed:false});setEmailSendStatus('No durable email reservation found. Retry this attempt.')}else{setEmailSendOutcome(undefined);setEmailSendStatus('Send outcome unverified. Check email status; do not resend.')}
    }}finally{emailLocks.current.delete(captured.key);if(current())setEmailSendState(previous=>previous==='sending'?'idle':previous)}
  };
  const sendContactEmail=async()=>{
    if(!person||!emailMailboxId||!person.email||!emailSubject.trim()||!emailBody.trim()||!emailIdempotencyKeyIsValid||emailLoadedKey!==emailKey||emailLocks.current.has(emailKey)||!crmDraftWritable(emailDraftScope,emailGeneration))return;
    const captured={...emailForm.current},epoch=emailEpoch.current,contactId=person.id;emailLocks.current.add(emailKey);setEmailSendState('sending');setEmailSendStatus('Preparing email attempt…');setCommunicationError('');
    const current=()=>epoch===emailEpoch.current&&emailForm.current.key===captured.key&&crmDraftWritable(emailDraftScope,emailGeneration);
    try{
      const eligibility=await microsoftAction({operation:'email_recipient_eligibility',mailboxId:captured.sender,toRecipients:[person.email],ccRecipients:[],bccRecipients:[]}) as {allowed?:boolean;blockedRecipients?:{address:string;reason:string}[]};
      if(!current())return;
      if(eligibility.allowed!==true){setEmailSendState('error');setEmailSendStatus(eligibility.blockedRecipients?.length?`Email blocked: ${eligibility.blockedRecipients.map(item=>`${item.address} (${item.reason.replaceAll('_',' ')})`).join('; ')}.`:'Recipient eligibility unavailable. No send submitted.');return;}
      const fingerprint=await newEmailFingerprint({actorId:identity.userId,workspaceId:identity.workspaceId,contactId,senderMailboxId:captured.sender,recipient:person.email,subject:captured.subject,body:captured.body});if(!current())return;
      let attempt=emailAttempt.current;
      if(attempt){
        if(!emailSendOutcome||(!emailSendOutcome.safeToRetry&&!emailSendOutcome.providerAccepted)){setEmailSendStatus('Previous send outcome unresolved. Check email status; do not resend.');return}
        if(attempt.fingerprint!==fingerprint){setEmailSendStatus('The sender, recipient or content changed. Start changed email only after the prior attempt is resolved.');return}
        if(emailSendOutcome.providerAccepted){setEmailSendStatus(newEmailAttemptLabel(emailSendOutcome));return}
      }else attempt={id:emailIdempotencyKey,fingerprint};
      saveNewEmailState(emailScope,contactId,{draft:{subject:captured.subject,body:captured.body,senderMailboxId:captured.sender},attempt});emailAttempt.current=attempt;setEmailSendOutcome(undefined);setEmailSendStatus('Email pending…');
      let result:ReplyAttemptResult;
      try{result=parseReplyAttemptResult(await microsoftAction({operation:'new_email',contactId,mailboxId:captured.sender,subject:captured.subject.trim(),body:captured.body.trim(),expectedRecipientEmails:[person.email.trim().toLowerCase()],idempotencyKey:attempt.id}))}
      catch(value){result=parseReplyAttemptResult((value as {responseBody?:unknown})?.responseBody)}
      if(!current())return;
      if(result.attemptId!==attempt.id){
        if(result.requestKind!=='NEW_MESSAGE'||result.contactId!==contactId||result.mailboxId!==captured.sender)throw Error('email_outcome_unverified');
        attempt={id:result.attemptId,fingerprint:result.attemptConflict?'0'.repeat(64):attempt.fingerprint};emailAttempt.current=attempt;setEmailIdempotencyKey(attempt.id);
        saveNewEmailState(emailScope,contactId,{draft:{subject:emailForm.current.subject,body:emailForm.current.body,senderMailboxId:emailForm.current.sender},attempt});
      }
      applyEmailOutcome(result,attempt,captured);if(result.providerAccepted)void refreshEmailHistory(epoch,captured.key);
    }catch{if(current()){setEmailSendOutcome(undefined);setEmailSendStatus(emailAttempt.current?'Send outcome unverified. Check email status; do not resend.':'Email preparation failed; no send was submitted.');setEmailSendState('error')}}
    finally{emailLocks.current.delete(captured.key);if(current())setEmailSendState(previous=>previous==='sending'?'idle':previous)}
  };
  const startChangedEmail=async()=>{
    if(!person||emailLocks.current.has(emailKey)||!emailAttempt.current||!emailSendOutcome||(!emailSendOutcome.safeToRetry&&!emailSendOutcome.providerAccepted)||!crmDraftWritable(emailDraftScope,emailGeneration))return;
    const previous=emailAttempt.current,epoch=emailEpoch.current,currentKey=emailKey;
    const fingerprint=await newEmailFingerprint({actorId:identity.userId,workspaceId:identity.workspaceId,contactId:person.id,senderMailboxId:emailMailboxId,recipient:person.email,subject:emailSubject,body:emailBody});
    if(epoch!==emailEpoch.current||emailForm.current.key!==currentKey||fingerprint===previous.fingerprint)return;
    saveNewEmailState(emailScope,person.id,{draft:{subject:emailSubject,body:emailBody,senderMailboxId:emailMailboxId}});emailAttempt.current=undefined;setEmailIdempotencyKey(crypto.randomUUID());setEmailSendOutcome(undefined);setEmailSendStatus('Changed email prepared as a new attempt. Review it before sending.');setEmailSendState('idle');
  };
  const prepareBoundedDraft = (
    channel: "email" | "sms",
    body: string,
    consent: boolean,
  ) => {
    try {
      const result = prepareCommunication({ channel, body, consent });
      setDraftStatus(`${result.status}: ${result.summary}`);
      setCommunicationError("");
    } catch (value) {
      setDraftStatus("");
      setCommunicationError(
        value instanceof Error ? value.message : "unable_to_prepare",
      );
    }
  };
  const sendMailboxes=(emailCommunications?.mailboxes||[]).filter(mailbox=>mailbox.send_enabled&&mailbox.verification_state==='VERIFIED'&&mailbox.permissions.canSend);
  const emailMailboxById=new Map((emailCommunications?.mailboxes||[]).map(mailbox=>[mailbox.id,mailbox]));
  const emailOperatorById=new Map((emailCommunications?.operators||[]).map(operator=>[operator.user_id,operator]));
  const contactEmailMessages=(emailCommunications?.messages||[]).filter(message=>message.contact_id===person?.id).sort((left,right)=>new Date(left.received_at||left.sent_at||0).getTime()-new Date(right.received_at||right.sent_at||0).getTime());
  const contactCommunicationTimeline=[...activities.filter(item=>item.contactId===person?.id).map(item=>({id:`activity-${item.id}`,kind:item.type,title:item.title,detail:item.detail,at:item.occurredAt})),...contactEmailMessages.map(message=>{const mailbox=emailMailboxById.get(message.mailbox_id);const operator=emailOperatorById.get(message.operator_user_id||"");return{id:`email-${message.id}`,kind:"email",title:`Email · ${mailbox?.display_name||"Shared inbox"}`,detail:`${message.direction==="OUTBOUND"?`${operator?.display_name||"AvertSignal operator"} sent`:"Contact replied"} · ${message.subject} · ${message.delivery_state}`,at:message.received_at||message.sent_at||""}}),...dispositions.filter(item=>item.contactId===person?.id).map(item=>({id:`disposition-${item.id}`,kind:"call",title:`Call · ${item.disposition}`,detail:item.notes||item.nextAction||"Outcome recorded",at:item.occurredAt})),...voice.recentCalls.filter(item=>item.contactId===person?.id).map(item=>({id:`voice-${item.id}`,kind:"call",title:`Voice call · ${item.status}`,detail:`${voice.actors[item.actorUserId]||"Unknown CRM user"} · ${formatCallDuration(item.durationSeconds)}`,at:item.completedAt||item.updatedAt}))].sort((left,right)=>new Date(right.at).getTime()-new Date(left.at).getTime());
  return (
    <div className="page organization-page">
      <div className="page-heading organization-page-heading">
        <div>
          <span className="eyebrow">ORGANIZATIONS FIRST</span>
          <h1>Accounts, people, roles, and every conversation</h1>
          <p>
            The organization is the primary record. Contacts, decision roles,
            communication history, opportunities, and calls sit underneath it.
          </p>
        </div>
        <div className="org-actions">
        {live&&identity.role==='SUPER_ADMIN'&&<button type="button" className="primary-btn" onClick={()=>setCompanyImportOpen(true)}>Import companies</button>}
        <button className="primary-btn" onClick={onCreateCompany}>
          <Building2 size={17} /> Add company
        </button>
        </div>
      </div>
      {live&&identity.role==='SUPER_ADMIN'&&companyImportOpen&&<CompanyImport onClose={()=>setCompanyImportOpen(false)} onImported={()=>invalidateCrmData()}/>}
      {live&&<CompanyReviewSegments value={reviewSegmentKey} onChange={setReviewSegmentKey} onSegments={setReviewSegments}/>}
      {live&&<CompanyReviewSegmentAdmin selectedKey={reviewSegmentKey}/>}
      {org && (
        <div className="panel account-support-summary">
          <span className="eyebrow">SAFE APP SUMMARY · PROJECTION ONLY</span>
          <div className="account-reference-grid">
            <div>
              <span>Plan</span>
              <strong>{org.appSummary.plan}</strong>
            </div>
            <div>
              <span>Locations</span>
              <strong>{org.appSummary.locations}</strong>
            </div>
            <div>
              <span>Onboarding</span>
              <strong>{org.appSummary.onboarding}</strong>
            </div>
            <div>
              <span>Connections</span>
              <strong>{org.appSummary.connections}</strong>
              <small>No supported aggregate projection is available.</small>
            </div>
            <div>
              <span>Support</span>
              <strong>{org.appSummary.support}</strong>
            </div>
          </div>
          <small>
            Owner and Agency apps remain authoritative. CRM does not query raw
            app tables or impersonate the customer.
          </small>
        </div>
      )}
      <div className="organization-layout">
        <div className="panel org-list">
          <PanelHeader
            title="Organizations"
            action={`${visibleOrganizations.length} of ${organizations.length}`}
          />
          {visibleOrganizations.map((o) => (
            <Link
              key={o.id}
              to={'/organizations?organization='+encodeURIComponent(o.id)}
              aria-current={org?.id===o.id?'page':undefined}
              className={`org-row ${org?.id === o.id ? "active" : ""}`}
            >
              <div className="org-logo">
                {o.name
                  .split(" ")
                  .map((x) => x[0])
                  .join("")
                  .slice(0, 2)}
              </div>
              <div>
                <strong>{o.name}</strong>
                {o.operationalClass&&o.operationalClass!=='G_REAL_OR_POTENTIALLY_REAL'&&<span className="synthetic-class-badge">{o.operationalClass==='A_GOVERNED_ACTIVE_ACCEPTANCE'?'Acceptance':o.operationalClass==='B_GOVERNED_DEMO'?'Demo':'Historical synthetic'}</span>}
                <span>
                  {o.contacts.length} contact
                  {o.contacts.length === 1 ? "" : "s"} ·{" "}
                  {o.stages.join(", ") || "Stage unknown"}
                </span>
                <small>
                  {o.links.length
                    ? `${o.links.length} verified app reference${o.links.length === 1 ? "" : "s"}`
                    : "App references unknown"}
                </small>
              </div>
              <ChevronRight size={16} />
            </Link>
          ))}
          {!visibleOrganizations.length && (
            <div className="org-empty">
              <strong>No matching CRM records</strong>
              <span>
                Try an organization, CRM ID, app account, location, subscription
                reference, contact, email, phone, owner, role, or stage.
              </span>
            </div>
          )}
        </div>
        {org && person ? (
          <>
            <div className="panel org-record">
              <div className="org-header">
                <div>
                  <span className="eyebrow">ORGANIZATION</span>
                  <h2>{org.name}</h2>
                  {org.operationalClass&&org.operationalClass!=='G_REAL_OR_POTENTIALLY_REAL'&&<span className="synthetic-class-badge" title={org.classificationReason}>{org.operationalClass==='A_GOVERNED_ACTIVE_ACCEPTANCE'?'Governed acceptance':org.operationalClass==='B_GOVERNED_DEMO'?'Governed demo':'Historical synthetic'}</span>}
                  <p>
                    {org.contacts.length} contact
                    {org.contacts.length === 1 ? "" : "s"} · Owner: {org.owner}
                  </p>
                </div>
                <div className="org-actions">
                  {canLaunchDemo && <DemoLaunchAction compact audience={selectedDemoAudience} organizationId={org.id} contactId={person.id} opportunityId={audienceAttribution?.opportunityId} />}
                  <button type="button" onClick={() => { editCompanyRequest.current+=1; setEditCompany(canonicalOrganizations.find(item => item.id === org.id) || null); setEditState("idle"); setEditError(""); }}>
                    <Edit3 size={16} /> Edit company
                  </button>
                  <NavLink to="/communications"><Mail size={16} /> Email</NavLink>
                </div>
              </div>
              <div className="account-reference-grid">
                <div>
                  <span>CRM organization</span>
                  <strong>{org.id}</strong>
                </div>
                {org.links.map((link) => (
                  <div key={link.id}>
                    <span>
                      {link.productLane} app reference · {link.sourceSystem}
                    </span>
                    <strong>Account {link.appAccountId}</strong>
                    <small>
                      Location {link.appLocationId || "Unknown"} · Subscription{" "}
                      {link.subscriptionReference || "Unknown"}
                    </small>
                  </div>
                ))}
                {!org.links.length && (
                  <div>
                    <span>App references</span>
                    <strong>Unknown</strong>
                    <small>No cross-system identifier has been verified.</small>
                  </div>
                )}
              </div>
              <CompanyRestNotice contactId={person.id}/>
              {org.canonical&&<CompanyIntelligenceProfile companyId={org.id}/>}
              <CompanyDialSummary companyId={org.id} contacts={org.contacts} onDial={contactId=>void startProfileCall(contactId)}/>
              <CompanyPhoneRoutes companyId={org.id} contactId={org.contacts.some(contact=>contact.id===person?.id)?person?.id:undefined} mode="manage" selectedRouteId={profileCallSelection?.phoneRouteId} onSelect={selectProfileCallRoute}/>
              <div className="org-contact-grid">
                {org.contacts.map((c) => (
                  <button
                    key={c.id}
                    className={`person-card ${person.id === c.id ? "active" : ""}`}
                    onClick={() => {setSelectedContactId(c.id);navigate(`/organizations/contact/${c.id}`)}}
                  >
                    <div className="avatar">
                      {c.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </div>
                    <div>
                      <strong>{c.name}</strong>
                      {communications.unreadByContact[c.id]>0&&<b className="contact-unread-badge" aria-label={`${communications.unreadByContact[c.id]} unread communications`}>{communications.unreadByContact[c.id]>99?'99+':communications.unreadByContact[c.id]}</b>}
                      <span>{c.role || "Role not recorded"}</span>
                      <small>
                        {c.isDecisionMaker === true
                          ? "Decision maker"
                          : c.isDecisionMaker === false
                            ? "Decision role not confirmed"
                            : "Decision role unknown"}
                      </small>
                    </div>
                  </button>
                ))}
              </div>
              <section aria-label="All company contacts and notes"><h3>Contacts and notes</h3>{org.contacts.map(contact=><details key={contact.id}><summary>{contact.name} — {contact.role||"Role unknown"}</summary><p>Email: {contact.email||"Unknown"} · Phone: {contact.phone||"Unknown"}</p><p>Decision role: {contact.buyingRole&&contact.buyingRole!=="UNKNOWN"?contact.buyingRole.replaceAll("_"," ").toLowerCase():contact.isDecisionMaker===true?"Decision maker":"Unknown"} · Do not call: {contact.doNotCall?"Yes":"No"}</p><Link to={"/organizations/contact/"+contact.id}>Edit contact and communication history</Link><RecordNotes recordType="contact" recordId={contact.id}/></details>)}</section>
              <h3>Company notes and internal reports</h3><RecordNotes recordType="organization" recordId={org.id} companyCallSelection={profileCallSelection}/>
            </div>
            <div className="panel contact-command">
              <div className="contact-command-header">
                <div className="avatar large">
                  {person.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
                <div>
                  <h2>{person.name}</h2>
                  <p>
                    {person.role || "Role not recorded"} · {org.name}
                  </p>
                </div>
                <div className="contact-actions">
                  <button className="icon-btn" aria-label="Edit contact" onClick={() => { setEditContact(person); setEditState("idle"); setEditError(""); }}>
                    <Edit3 size={17} />
                  </button>
                  <button className="icon-btn" type="button" aria-label={profileCallState==="starting"?`Starting call to ${person.name}`:`Call ${person.name}`} aria-busy={profileCallState==="starting"} disabled={profileCallState==="starting"||['connecting','dialing','ringing','connected'].includes(voice.state)} onClick={()=>void startProfileCall(person.id)}>
                    <Phone size={17} />
                  </button>
                  <button className="icon-btn" aria-label={`Open ${person.name} email`} onClick={()=>navigate(`/organizations/contact/${person.id}?tab=email`)}>
                    <Mail size={17} />
                  </button>
                  <button className="icon-btn" aria-label={`Open ${person.name} messages`} onClick={()=>navigate(`/organizations/contact/${person.id}?tab=text`)}>
                    <MessageSquareText size={17} />
                  </button>
                </div>
              </div>
              {profileCallState==="starting"&&<div className="composer-success" role="status">Starting the canonical contact call…</div>}
              {profileCallState==="error"&&<div className="composer-error" role="alert">Call could not start: {profileCallError}</div>}
              {activeProfileCall&&<div className="profile-call-state connected" role="status"><div><span>CONNECTED</span><strong>{person.name} · {org.name}</strong><small>Active Voice controls remain available in the persistent call bar while you work this record.</small></div><b><LiveCallTimer connectedAt={activeProfileCall.connectedAt} durationSeconds={activeProfileCall.durationSeconds}/></b></div>}
              {!activeProfileCall&&endedProfileCall&&<div className="profile-call-state ended" role="status"><div><span>CALL ENDED</span><strong>{formatCallDuration(endedProfileCall.durationSeconds)} · Record the outcome</strong><small>You remain on the canonical contact profile. Save the disposition, notes, and next action below.</small></div><button type="button" onClick={()=>setTab("calls")}>Record outcome</button></div>}
              <div className="contact-tabs">
                {(
                  ["overview", "email", "text", "notes", "calls", "activity"] as const
                ).map((t) => (
                  <button
                    key={t}
                    className={tab === t ? "active" : ""}
                    onClick={() => setTab(t)}
                  >
                    {t}
                    {t==="text"&&communications.unreadSmsByContact[person.id]>0&&<span className="tab-unread-badge" aria-label={`${communications.unreadSmsByContact[person.id]} unread text messages`}>{communications.unreadSmsByContact[person.id]>99?'99+':communications.unreadSmsByContact[person.id]}</span>}
                  </button>
                ))}
              </div>
              {tab === "overview" && (
                <div className="contact-overview">
                  <div>
                    <span>Email</span>
                    <strong>{person.email}</strong>
                  </div>
                  <div>
                    <span>Phone</span>
                    <strong>{person.phone}</strong>
                  </div>
                  <div>
                    <span>Role</span>
                    <strong>{person.role || "Not recorded"}</strong>
                  </div>
                  <div>
                    <span>Buying position</span>
                    <strong>
                      {person.isDecisionMaker === true
                        ? "Decision maker"
                        : person.isDecisionMaker === false
                          ? "Not confirmed as decision maker"
                          : "Unknown"}
                    </strong>
                  </div>
                </div>
              )}
              {tab === "email" && (
                <div className="comm-pane">
                  <div className="contact-email-context"><div><span>CONTACT</span><strong>{person.name}</strong><small>{person.email}</small></div><div><span>ORGANIZATION</span><strong>{org.name}</strong><small>Contact-anchored communication</small></div><div><span>OPPORTUNITY</span><strong>{audienceAttribution?.opportunityId?"Authoritative opportunity linked":"No deterministic opportunity"}</strong><small>Context does not change thread identity</small></div></div>
                  <div className="email-compose branded-email-composer">
                    <div className="email-addressing"><label>From<select aria-label="From mailbox" value={emailMailboxId} onChange={event=>setEmailMailboxId(event.target.value)}><option value="">Choose an authorized sender</option>{sendMailboxes.map(mailbox=><option key={mailbox.id} value={mailbox.id}>{mailbox.display_name} · {mailbox.email_address}</option>)}</select></label><label>To<input aria-label="Recipient" type="email" value={person.email} readOnly/></label></div>
                    <input
                      value={emailSubject}
                      onChange={(event) => {emailDraftDirty.current=true;setEmailSubject(event.target.value)}}
                      placeholder="Subject"
                    />
                    <textarea
                      value={emailBody}
                      onChange={(event) => {emailDraftDirty.current=true;setEmailBody(event.target.value)}}
                      placeholder={`Write an email to ${person.name}...`}
                    />
                    <div className="email-draft-assets">
                      {emailLinks.map(link=><span className="email-draft-asset" key={link.url}><a href={link.url} target="_blank" rel="noreferrer"><Link2 size={13}/>{link.label||link.url}</a><button type="button" aria-label={`Remove link ${link.label||link.url}`} onClick={()=>void removeEmailLink(link.url)}>Remove</button></span>)}
                      {emailAttachments.map(file=><span className="email-draft-asset" key={file.id}><Paperclip size={13}/>{file.filename}<button type="button" aria-label={`Download ${file.filename}`} onClick={()=>void downloadEmailFile(file.id)}>Download</button><button type="button" aria-label={`Remove ${file.filename}`} onClick={()=>void removeEmailFile(file.id)}>Remove</button></span>)}
                      <div className="email-link-row"><input type="url" value={emailLinkUrl} onChange={event=>setEmailLinkUrl(event.target.value)} placeholder="https:// link"/><input value={emailLinkLabel} onChange={event=>setEmailLinkLabel(event.target.value)} placeholder="Optional label"/><button disabled={!/^https:\/\/\S+$/.test(emailLinkUrl)||emailSaveState==="saving"} onClick={()=>void persistEmailLink()}><Link2 size={13}/>Add link</button></div>
                      <div className="email-file-row"><input type="file" accept=".pdf,.txt,.csv,.png,.jpg,.jpeg,.docx,.xlsx" onChange={event=>setEmailFile(event.target.files?.[0]||null)}/><button disabled={!emailFile||emailSaveState==="saving"} onClick={()=>void persistEmailFile()}><Paperclip size={13}/>Add file</button></div>
                    </div>
                    <div className="email-signature-preview">
                      <div className="email-signature-logo">
                        <AvertSignalMark size={28} />
                      </div>
                      <div>
                        <strong>{identity.displayName} · AvertSignal</strong>
                        <span>
                          Business signal monitoring, accountability, and
                          verified resolution
                        </span>
                        <small>
                          Approved AvertSignal logo included in draft preview
                        </small>
                      </div>
                    </div>
                    <div className="bounded-composer-status">
                      <ShieldCheck size={13} />
                      <span>
                        Ordinary business email does not require a recorded opt-in. Explicit opt-outs and delivery-safety suppressions are checked before sending. From: {emailMailboxById.get(emailMailboxId)?.email_address||"Choose an authorized mailbox"}.
                      </span>
                    </div>
                    {includeSynthetic&&<label className="acceptance-request-key">Acceptance request key<input aria-label="Acceptance request key" value={emailIdempotencyKey} disabled={Boolean(emailAttempt.current)||emailSendState==='sending'} onChange={event=>setEmailIdempotencyKey(event.target.value.trim())}/><small>Shown only while governed demo and acceptance records are enabled. The same key is retained after a failed or ambiguous attempt.</small></label>}
                    {communicationError && (
                      <div className="composer-error">
                        {communicationError.replaceAll("_", " ")}
                      </div>
                    )}
                    {draftStatus && (
                      <div className="composer-success">{draftStatus}</div>
                    )}
                    {emailSaveState==="error"&&<div className="composer-error" role="alert">Draft was not saved to the server. Your current text remains open for retry.</div>}
                    {emailSaveState==="success"&&<div className="composer-success" role="status">Draft saved to CRM.</div>}
                    {emailSendStatus&&<div className={emailSendOutcome?.providerAccepted?'composer-success':'composer-error'} role="status">{emailSendStatus}</div>}
                    {emailHistoryDelayed&&<div className="composer-error" role="status">Email history updates delayed. The send outcome above is unchanged.</div>}
                    {emailAttempt.current&&<button className="secondary-btn" type="button" disabled={emailSendState==='sending'} onClick={()=>void checkEmailStatus()}>Check email status</button>}
                    {emailAttempt.current&&(emailSendOutcome?.safeToRetry||emailSendOutcome?.providerAccepted)&&<button className="secondary-btn" type="button" disabled={emailSendState==='sending'} onClick={()=>void startChangedEmail()}>Start changed email</button>}
                    <button className="secondary-btn" disabled={emailSaveState==="saving"||emailSaveState==="loading"} onClick={()=>void persistEmailDraft()}>{emailSaveState==="saving"?"Saving…":"Save draft"}</button>
                    <button
                      className="primary-btn"
                      onClick={() =>
                        prepareBoundedDraft(
                          "email",
                          `${emailSubject.trim()}\n${emailBody.trim()}`,
                          true,
                        )
                      }
                    >
                      <Send size={15} /> Prepare email draft
                    </button>
                    <button className="primary-btn" disabled={!person.email||!emailMailboxId||!emailSubject.trim()||!emailBody.trim()||!emailIdempotencyKeyIsValid||emailSendState==="sending"||emailLoadedKey!==emailKey||emailIntentChanged||Boolean(emailAttempt.current&&(!emailSendOutcome||(!emailSendOutcome.safeToRetry&&!emailSendOutcome.providerAccepted)))} onClick={()=>void sendContactEmail()}><Send size={15}/>{emailSendState==="sending"?"Sending…":"Send from shared inbox"}</button>
                  </div>
                  <div className="contact-email-history"><h3>Email history</h3>{contactEmailMessages.map(message=>{const mailbox=emailMailboxById.get(message.mailbox_id);const operator=emailOperatorById.get(message.operator_user_id||"");return <div className="timeline-item" key={message.id}><div className="timeline-icon"><Mail size={14}/></div><div><strong>Email · {mailbox?.display_name||"Shared inbox"}</strong><span>{message.direction==="OUTBOUND"?`${operator?.display_name||"AvertSignal operator"} sent`:"Contact replied"} · {message.subject}</span>{message.rendered_body_html?<BlipEmailPreview html={message.rendered_body_html} title={`Sent Blip email to ${message.recipient_emails.join(", ")}`}/>:<small>{message.body_preview||"No preview"}</small>}<small>{new Date(message.received_at||message.sent_at||"").toLocaleString()} · {message.delivery_state}</small></div></div>})}{!contactEmailMessages.length&&<p>No synchronized email for this Contact in the current data view.</p>}</div>
                </div>
              )}
              {tab === "text" && (
                <CanonicalSmsConversation
                  contactId={person.id}
                  contactName={person.name}
                  consentSms={person.consentSms}
                  presentation="full"
                  isVisible={tab === "text"}
                  unreadCount={communications.unreadSmsByContact[person.id] || 0}
                />
              )}
              {tab === "notes" && (
                <div className="contact-notes-pane">
                  <RecordNotes recordType="contact" recordId={person.id}/>
                </div>
              )}
              {tab === "calls" && (
                <div className="comm-pane disposition-pane">
                  <h3>Record call outcome</h3>
                  <p>Twilio is not required. CRM records the employee, customer context, timestamp, notes, and next action.</p>
                  <label>Disposition<select value={contactDisposition} disabled={dispositionState==='saving'||profileRestSelection.retained} onChange={event=>{if(dispositionState==='success')profileOutcomeAttempt.current={contactId:person.id,id:crypto.randomUUID(),inFlight:false};setContactDisposition(event.target.value as CallDisposition["disposition"]);setDispositionState('idle');setProfileDispositionError('')}}>{canonicalDispositions.map(value=><option key={value}>{value}</option>)}</select></label>
                  <label>Notes/reference<textarea value={dispositionNotes} disabled={dispositionState==='saving'||profileRestSelection.retained} onChange={event=>{if(dispositionState==="success")profileOutcomeAttempt.current={contactId:person.id,id:crypto.randomUUID(),inFlight:false};setDispositionState("idle");setDispositionNotes(event.target.value)}} placeholder="What happened or the voicemail reference."/></label>
                  {contactDisposition==='Rest for 2 weeks'&&<CompanyRestTimezone selection={profileRestSelection}/>}
                  <div className="derived-next-action"><span>DERIVED NEXT ACTION</span><strong>{deriveNextAction(contactDisposition)}</strong></div>
                  {dispositionState==="error"&&<div className="composer-error" role="alert">{profileDispositionError||'Call outcome could not be saved.'}</div>}
                  {dispositionState==="success"&&<div className="composer-success" role="status">Call outcome saved.</div>}
                  <button className="primary-btn" disabled={dispositionState==="saving"||dispositionState==="success"||contactDisposition==='Rest for 2 weeks'&&!profileRestSelection.ready} onClick={async()=>{
                    if(profileOutcomeAttempt.current.inFlight)return;
                    if(profileOutcomeAttempt.current.contactId!==person.id)profileOutcomeAttempt.current={contactId:person.id,id:crypto.randomUUID(),inFlight:false};
                    const attempt=profileOutcomeAttempt.current,scopeAtStart=profileCallScope,resting=contactDisposition==='Rest for 2 weeks';attempt.inFlight=true;setDispositionState('saving');setProfileDispositionError('');
                    try{
                      const saved=resting?(await profileRestSelection.save({dispositionId:attempt.id,notes:dispositionNotes,nextAction:deriveNextAction(contactDisposition)})).disposition:await recordCallDisposition({dispositionId:attempt.id,contactId:person.id,organizationId:person.organizationId||null,disposition:contactDisposition,notes:dispositionNotes,nextAction:deriveNextAction(contactDisposition),scheduleEventId:null});
                      if(currentProfileCallScope.current!==scopeAtStart)return;
                      if(resting)profileRestSelection.complete();
                      setDispositions(current=>[saved,...current.filter(item=>item.id!==saved.id)]);setDispositionNotes('');setDispositionState('success');
                    }catch(value){if(currentProfileCallScope.current===scopeAtStart){setDispositionState('error');setProfileDispositionError(value instanceof Error?value.message:'Call outcome could not be saved.')}}finally{attempt.inFlight=false}
                  }}>{dispositionState==='saving'?'Saving…':'Save call outcome'}</button>
                  <div className="team-actions">
                    <button onClick={()=>navigate(`/calendar?contact=${person.id}&organization=${person.organizationId||""}&type=CUSTOMER_CALLBACK&title=${encodeURIComponent(`Callback · ${person.name}`)}`)}>Schedule callback</button>
                    <button onClick={()=>navigate(`/tasks?contact=${person.id}&organization=${person.organizationId||""}&title=${encodeURIComponent(`Follow up · ${person.name}`)}`)}>Create follow-up</button>
                  </div>
                  <div className="timeline contact-activity">{dispositions.filter(item=>item.contactId===person.id).map(item=><div className="timeline-item" key={item.id}><div className="timeline-icon"><PhoneCall size={14}/></div><div><strong>{item.disposition}</strong><span>{item.notes||item.nextAction||"Outcome recorded"}</span><small>{new Date(item.occurredAt).toLocaleString()} · actor recorded</small></div></div>)}</div>
                  <div className="timeline contact-activity voice-history" aria-label="Provider-confirmed Voice call history">{voice.recentCalls.filter(item=>item.contactId===person.id).map(item=><div className="timeline-item" key={item.id}><div className="timeline-icon"><PhoneCall size={14}/></div><div><strong>{item.direction.toLowerCase()} Voice call · {item.status}</strong><span>{formatCallDuration(item.durationSeconds)} · {voice.actors[item.actorUserId]||"Unknown CRM user"}</span><small>{new Date(item.completedAt||item.updatedAt).toLocaleString()}</small></div></div>)}</div>
                </div>
              )}
              {tab === "activity" && (
                <div className="timeline contact-activity">
                  {contactCommunicationTimeline.map(item=><div className="timeline-item" key={item.id}><div className="timeline-icon">{item.kind==="email"?<Mail size={14}/>:item.kind==="call"?<PhoneCall size={14}/>:activityIcon(item.kind as ActivityType["type"])}</div><div><strong>{item.title}</strong><span>{item.detail}</span><small>{new Date(item.at).toLocaleString()}</small></div></div>)}
                  {!contactCommunicationTimeline.length && (
                    <div className="timeline-item">
                      <div>
                        <strong>No recorded activity</strong>
                        <span>
                          No provider or customer history is inferred.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : org ? (
          <div className="panel org-record org-empty-company">
            <div className="org-header">
              <div>
                <span className="eyebrow">ORGANIZATION</span>
                <h2>{org.name}</h2>
                <p>0 contacts · Owner: {org.owner}</p>
              </div>
              <div className="org-actions">
                {canLaunchDemo && <DemoLaunchAction compact audience={selectedDemoAudience} organizationId={org.id} opportunityId={audienceAttribution?.opportunityId} />}
                <button type="button" onClick={() => { editCompanyRequest.current+=1; setEditCompany(canonicalOrganizations.find(item => item.id === org.id) || null); setEditState("idle"); setEditError(""); }}>
                  <Edit3 size={16} /> Edit company
                </button>
              </div>
            </div>
            <div className="account-reference-grid">
              <div><span>CRM organization</span><strong>{org.id}</strong></div>
              <div><span>Contacts</span><strong>None recorded</strong><small>Add a verified person when one is known.</small></div>
            </div>
            <RecordNotes recordType="organization" recordId={org.id} companyCallSelection={profileCallSelection}/>
            {org.canonical&&<CompanyIntelligenceProfile companyId={org.id}/>}
            {org.canonical&&<CompanyPhoneRoutes companyId={org.id} mode="manage" selectedRouteId={profileCallSelection?.phoneRouteId} onSelect={selectProfileCallRoute}/>}
            <div className="timeline contact-activity" aria-label="Organization activity history">
              {activities.filter(item=>item.organizationId===org.id).map(item=><div className="timeline-item" key={item.id}><div className="timeline-icon">{activityIcon(item.type)}</div><div><strong>{item.title}</strong><span>{item.detail}</span><small>{new Date(item.occurredAt).toLocaleString()}</small></div></div>)}
              {!activities.some(item=>item.organizationId===org.id)&&<div className="timeline-item"><div><strong>No recorded activity</strong><span>This organization has no call, email, task, meeting, or provider history yet. Nothing is inferred.</span></div></div>}
            </div>
          </div>
        ) : (
          <div className="panel org-no-selection">
            <Building2 size={28} />
            <h2>No organization selected</h2>
            <p>
              No matching organization/contact record is available. No customer
              state is inferred.
            </p>
          </div>
        )}
      </div>
      {editCompany && <div className="record-modal-backdrop"><form className="record-modal" onSubmit={async event => {event.preventDefault();const request=++editCompanyRequest.current;setEditState("saving");setEditError("");try{const saved=await updateOrganization({organizationId:editCompany.id,name:editCompany.name,accountStatus:editCompany.accountStatus,ownerUserId:editCompany.ownerUserId||null});onOrganizationsChange(current=>current.map(item=>item.id===saved.id?saved:item));if(request===editCompanyRequest.current){setEditCompany(saved);setEditState("success")}}catch(error){if(request===editCompanyRequest.current){setEditState("error");setEditError(error instanceof Error?error.message.replaceAll("_"," "):"Company could not be saved.")}}}}><header><div><span className="eyebrow">COMPANY RECORD</span><h2>Edit company</h2></div><button type="button" aria-label="Close" onClick={()=>{editCompanyRequest.current+=1;setEditCompany(null)}}><X/></button></header><label>Company name<input disabled={editState==="saving"} value={editCompany.name} onChange={event=>setEditCompany({...editCompany,name:event.target.value})} required/></label><label>Status<input value={editCompany.accountStatus.replaceAll("_"," ")} readOnly/><small className="field-hint">Customer state is set by verified Owner billing only.</small></label>{editState==="success"&&<div className="composer-success" role="status">Company saved: {editCompany.name}.</div>}{editState==="error"&&<div className="composer-error" role="alert">{editError}</div>}<footer><button type="button" onClick={()=>{editCompanyRequest.current+=1;setEditCompany(null)}}>Close</button><button className="primary-btn" disabled={editState==="saving"}>{editState==="saving"?"Saving…":"Save company"}</button></footer></form></div>}
      {editContact && <div className="record-modal-backdrop"><form className="record-modal" onSubmit={async event => {event.preventDefault();setEditState("saving");setEditError("");try{const organizationId=editContact.organizationId||null,saved=await updateContact({contactId:editContact.id,firstName:editContact.firstName||editContact.name.split(" ")[0]||"",lastName:editContact.lastName||editContact.name.split(" ").slice(1).join(" ")||"Contact",email:editContact.email,phone:editContact.phone,organizationId,jobTitle:editContact.role||"",source:editContact.source||"",doNotCall:Boolean(editContact.doNotCall),ownerUserId:editContact.ownerUserId||null});const displayed={...saved,organizationId:organizationId||undefined,company:editContact.company};onContactsChange(contacts.map(item=>item.id===displayed.id?displayed:item));setSelectedContactId(displayed.id);setEditContact(displayed);setEditState("success")}catch(error){setEditState("error");setEditError(error instanceof Error?error.message.replaceAll("_"," "):"Contact could not be saved.")}}}><header><div><span className="eyebrow">CONTACT RECORD</span><h2>Edit contact</h2></div><button type="button" aria-label="Close" onClick={()=>setEditContact(null)}><X/></button></header><div className="contact-create-grid"><label>First name<input value={editContact.firstName||""} onChange={event=>setEditContact({...editContact,firstName:event.target.value,name:`${event.target.value} ${editContact.lastName||""}`.trim()})} required/></label><label>Last name<input value={editContact.lastName||""} onChange={event=>setEditContact({...editContact,lastName:event.target.value,name:`${editContact.firstName||""} ${event.target.value}`.trim()})} required/></label><label>Email<input type="email" value={editContact.email} onChange={event=>setEditContact({...editContact,email:event.target.value})}/></label><label>Phone<input value={editContact.phone} onChange={event=>setEditContact({...editContact,phone:event.target.value})}/></label><label>Company<select value={editContact.organizationId||""} onChange={event=>setEditContact({...editContact,organizationId:event.target.value||undefined,company:canonicalOrganizations.find(item=>item.id===event.target.value)?.name||"Unassociated"})}><option value="">No company</option>{canonicalOrganizations.map(item=><option key={item.id} value={item.id}>{item.name}</option>)}</select></label><label>Job title<input value={editContact.role||""} onChange={event=>setEditContact({...editContact,role:event.target.value})}/></label><ContactBuyingRole key={editContact.id} contact={editContact}/><label>Source<input value={editContact.source||""} onChange={event=>setEditContact({...editContact,source:event.target.value})}/></label><label className="consent-toggle"><input type="checkbox" checked={Boolean(editContact.doNotCall)} onChange={event=>setEditContact({...editContact,doNotCall:event.target.checked})}/>Do not call</label></div>{editState==="success"&&<div className="composer-success" role="status">Contact saved successfully.</div>}{editState==="error"&&<div className="composer-error" role="alert">{editError}</div>}<footer><button type="button" onClick={()=>setEditContact(null)}>Close</button><button className="primary-btn" disabled={editState==="saving"}>{editState==="saving"?"Saving…":"Save contact"}</button></footer></form></div>}
    </div>
  );
}

function SettingsPage({ dataMode }: { dataMode: DataSourceMode }) {
  const [providers, setProviders] = useState<ProviderReadiness | null>(null);
  const [providerError, setProviderError] = useState(false);
  useEffect(() => {
    let active = true;
    if (dataMode !== "live") {
      setProviders(null);
      setProviderError(false);
      return () => {
        active = false;
      };
    }
    getProviderReadiness()
      .then((value) => {
        if (active) {
          setProviders(value);
          setProviderError(false);
        }
      })
      .catch(() => {
        if (active) {
          setProviders(null);
          setProviderError(true);
        }
      });
    return () => {
      active = false;
    };
  }, [dataMode]);
  const providerIntegrations = CRM_PROVIDER_ADAPTERS.map((provider) => {
    const presentation = crmAdapterUi(provider.provider);
    return {
      name: provider.display_name,
      purpose: `${provider.capabilities.join(" · ")} · ${provider.blocker}`,
      status: presentation.status,
      label: presentation.label,
    };
  });
  const integrations: Array<IntegrationState & { label?: string }> = [
    {
      name: "Supabase",
      purpose: "Database, auth, bounded near-real-time sync (polling)",
      status:
        config.supabaseUrl && config.supabaseAnonKey
          ? "connected"
          : "needs-config",
    },
    {
      name: "Netlify Functions",
      purpose: "Secure backend actions",
      status: dataMode === "live" ? "connected" : "needs-config",
    },
    ...providerIntegrations,
    {
      name: "Email Tracking",
      purpose: "Delivery acceptance and replies are recorded. Open/click tracking setup required.",
      status: "needs-config",
    },
    {
      name: "Consent Engine",
      purpose: "Email, SMS, and call permissions are enforced from explicit consent evidence.",
      status: dataMode === "live" ? "connected" : "needs-config",
    },
  ];
  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">SYSTEM SETTINGS</span>
          <h1>Integrations and controls</h1>
          <p>Visible status, explicit ownership, and no mystery switches.</p>
          {providerError && (
            <small className="degraded-copy">
              Provider readiness is temporarily unavailable. No provider action
              was attempted.
            </small>
          )}
        </div>
      </div>
      <div className="settings-grid">
        {integrations.map((i) => (
          <div className="panel integration-card" key={i.name}>
            <div className="integration-icon">
              <Building2 size={20} />
            </div>
            <div>
              <strong>{i.name}</strong>
              <span>{i.purpose}</span>
            </div>
            <div className={`integration-status ${i.status}`}>
              {i.label ?? i.status.replace("-", " ")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminPage({ contacts }: { contacts: Contact[] }) {
  const [role, setRole] = useState<"admin" | "account-executive">("admin");
  const calls = [
    {
      rep: "Jordan Smith",
      contact: "Sarah Thompson",
      company: "BrightPath Dental",
      duration: "08:42",
      status: "Discovery",
    },
    {
      rep: "Maya Lee",
      contact: "Carlos Vega",
      company: "Vega Legal",
      duration: "03:18",
      status: "Gatekeeper",
    },
  ];
  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">ADMIN CONTROL CENTER</span>
          <h1>Visibility without chaos</h1>
          <p>
            Admins see the whole operation. Account executives see only assigned
            pipeline, contacts, tasks, and calls.
          </p>
        </div>
        <div className="role-switch">
          <button
            className={role === "admin" ? "active" : ""}
            onClick={() => setRole("admin")}
          >
            Admin view
          </button>
          <button
            className={role === "account-executive" ? "active" : ""}
            onClick={() => setRole("account-executive")}
          >
            AE view
          </button>
        </div>
      </div>
      <section className="admin-metrics">
        <Metric
          label="Visible contacts"
          value={
            role === "admin"
              ? String(contacts.length * 61)
              : String(contacts.length)
          }
          delta={role === "admin" ? "All teams" : "Assigned only"}
          icon={ContactRound}
        />
        <Metric
          label="Active calls"
          value={role === "admin" ? "2" : "1"}
          delta="Live now"
          icon={Phone}
        />
        <Metric
          label="Pipeline access"
          value={role === "admin" ? "$1.8M" : "$320K"}
          delta={role === "admin" ? "Company-wide" : "Personal"}
          icon={CircleDollarSign}
        />
        <Metric
          label="Permission level"
          value={role === "admin" ? "Full" : "Scoped"}
          delta="Role enforced"
          icon={ShieldCheck}
        />
      </section>
      <div className="admin-grid">
        <div className="panel permission-matrix">
          <PanelHeader title="Role permissions" action="RBAC" />
          <div className="permission-table">
            <div className="permission-head">
              <span>Capability</span>
              <span>Admin</span>
              <span>Account executive</span>
            </div>
            {[
              ["View all contacts", "Yes", "Assigned only"],
              ["View all pipelines", "Yes", "Own pipeline"],
              ["Reassign leads", "Yes", "No"],
              ["Manage scripts", "Yes", "No"],
              ["Listen to live calls", "Yes", "No"],
              ["Whisper coaching", "Yes", "No"],
              ["Barge into calls", "Restricted", "No"],
              ["Export data", "Permissioned", "No"],
              ["Manage integrations", "Yes", "No"],
            ].map((r) => (
              <div className="permission-line" key={r[0]}>
                <span>{r[0]}</span>
                <span>{r[1]}</span>
                <span>{r[2]}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="panel live-monitor">
          <PanelHeader title="Live call supervision" action="Audited" />
          {calls.map((c) => (
            <div className="monitor-row" key={c.rep}>
              <div className="avatar">
                {c.rep
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </div>
              <div>
                <strong>{c.rep}</strong>
                <span>
                  {c.contact} · {c.company}
                </span>
                <small>
                  {c.status} · {c.duration}
                </small>
              </div>
              <div className="monitor-actions">
                <button title="Listen">
                  <Headphones size={16} />
                </button>
                <button title="Whisper">
                  <Mic2 size={16} />
                </button>
                <button title="Barge">
                  <Radio size={16} />
                </button>
              </div>
            </div>
          ))}
          <div className="compliance-note">
            <ShieldCheck size={18} />
            <span>
              Every listen, whisper, and barge action creates an audit event.
              Recording remains separate and permission-controlled.
            </span>
          </div>
        </div>
        <div className="panel admin-tools">
          <PanelHeader title="Admin tools" action="Full access" />
          <div className="tool-grid">
            <button>
              <UserCog size={18} />
              <span>
                <strong>Team access</strong>
                <small>Roles, territories, ownership</small>
              </span>
            </button>
            <button>
              <Workflow size={18} />
              <span>
                <strong>Call scripts</strong>
                <small>Paths, prompts, outcomes</small>
              </span>
            </button>
            <button>
              <Shield size={18} />
              <span>
                <strong>Consent controls</strong>
                <small>Channel rules and evidence</small>
              </span>
            </button>
            <button>
              <BarChart3 size={18} />
              <span>
                <strong>Performance</strong>
                <small>Calls, pipeline, conversion</small>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GenericPage({
  title,
  subtitle,
  icon: Icon,
}: {
  title: string;
  subtitle: string;
  icon: any;
}) {
  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">AVERTSIGNAL CRM</span>
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
      </div>
      <div className="panel placeholder-panel">
        <Icon size={34} />
        <h2>{title} framework installed</h2>
        <p>
          This route is ready for the next workflow-specific build without
          changing the backend contract.
        </p>
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  delta,
  icon: Icon,
}: {
  label: string;
  value: string;
  delta: string;
  icon: any;
}) {
  return (
    <div className="metric-card">
      <div className="metric-icon">
        <Icon size={19} />
      </div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{delta}</small>
    </div>
  );
}
function PanelHeader({ title, action }: { title: string; action: string }) {
  return (
    <div className="panel-header">
      <h3>{title}</h3>
      <button>
        {action}
        <ChevronRight size={15} />
      </button>
    </div>
  );
}
function activityIcon(type: ActivityType["type"]) {
  const map = {
    email: <Mail size={15} />,
    call: <Phone size={15} />,
    sms: <MessageSquareText size={15} />,
    form: <ListTodo size={15} />,
    note: <Activity size={15} />,
    meeting: <CalendarClock size={15} />,
    tracking: <Gauge size={15} />,
  };
  return map[type];
}
export default App;
